/**
 * Site Analysis mapping tool — kit-of-parts diagrams over composite site-plan backgrounds.
 *
 * Full-screen modal in the QC/sketch/takeoff family, launched from a milestone's Documents
 * ⋯ menu via the `open-site-analysis-modal` CustomEvent ({ msId, projectId, projectName }).
 *
 * Round 27: exactly ONE analysis per PROJECT (Supabase `site_analyses`, migration 106 —
 * project work product, any team member can edit). Created lazily the first time it's opened
 * for a project and never deleted or switched between — `msId` is passed through only so a
 * freshly-created row can note which milestone it was first opened from (purely informational;
 * it is never the lookup key). There is no rename/open-another/delete UI; the ✕ button and
 * autosave are the only ways this document changes state from outside its own canvas.
 *
 * Design contract (docs/SITE_ANALYSIS.md): users choose WHAT to place, never how it looks —
 * every element is a typed item styled exclusively through SITE_STYLES, so every team
 * member's map comes out graphically identical. Dark #07272D ink on a WHITE field (the
 * whitewash slider knocks the aerial back so ink pops). No freehand tool — that's /sketch.
 *
 * Round 25: every save also renders a PDF (client-side pdf-lib, embedding the export PNG on an
 * 11×17-proportioned page) and stores it alongside the JSON — this is what makes the "final
 * state PDF" available for `api/site-analyses.js`'s `freeze` action to hand to
 * `milestone_documents` when the milestone posts (`services/siteAnalysis.js#freezeSiteAnalysis`,
 * called from `PostMilestoneModal.jsx`, unconditionally + best-effort on every post). There is
 * no manual "Export to Documents" button anymore — posting is what publishes it; "Preview PDF"
 * in the top bar renders the same PDF on demand into a new tab, matching the
 * `handleDownloadRenderingPdf` pattern in `MilestoneSections.jsx` (open the tab synchronously
 * with a loading message, point it at the blob once ready).
 */
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { getOrCreateSiteAnalysis, saveSiteAnalysis } from '../services/siteAnalysis.js'
import { getParcels, updateParcelField } from '../services/parcels.js'
import { confirmDialog, promptDialog } from './Dialog.jsx'
import { usePdfPagePicker } from './PdfPagePickerModal.jsx'
import { loadPdfjs } from '../lib/pdfjs.js'

const INK = '#07272D'
const PANEL_W = 264
const PANEL_MIN = 220
const PANEL_MAX = 480
// Satellite fetch pixel width/height — Mapbox's actual max for the (non-retina) Static Images
// API. Deliberately NOT requesting "@2x" — see api/vicinity-map.js for why.
const SAT_PX = 1280

/* The single source of truth for how everything looks. Tuning happens HERE, never per-item —
   that's the standardization contract: users choose WHAT to place, never how it looks. */
const C = { red: '#b3402f', purple: '#7d5ba6', blue: '#4a7fa5', green: '#5a8a3c', amber: '#c8891a', teal: '#2e8b8b', gray: '#8a949a', orange: '#c07830' }
export const SITE_STYLES = {
  pl:            { stroke: INK, width: 2.4, vertexR: 3.2, pending: { stroke: C.green, dash: [10, 6] }, label: 'Property line' },
  setback:       { stroke: C.red, width: 1.3, dash: [8, 5], label: 'Setback' },
  // Boundary matches `setback`'s red dashed line exactly (drawn using setback's stroke/width/
  // dash directly, not these — kept here only so the legend swatch shows the right color/dash).
  envelope:      { fill: 'rgba(90,138,60,0.10)', stroke: C.red, width: 1.3, dash: [8, 5], label: 'Buildable envelope' },
  easement:      { fill: 'rgba(125,91,166,0.16)', stroke: C.purple, width: 1.3, dash: [7, 4, 2, 4], label: 'Easement' },
  structure:     { fill: 'rgba(138,148,154,0.28)', stroke: INK, width: 1.3, label: 'Existing structure' },
  water:         { fill: 'rgba(74,127,165,0.20)', stroke: C.blue, width: 1.3, label: 'Water' },
  vegmass:       { fill: 'rgba(90,138,60,0.10)', stroke: C.green, width: 1.2, label: 'Vegetation' },
  boulders:      { fill: 'rgba(138,148,154,0.16)', stroke: C.gray, width: 1.2, dash: [2, 3], label: 'Rock / boulders' },
  importantzone: { stroke: C.red, width: 1.5, dash: [6, 5], dot: 'rgba(179,64,47,0.4)', label: 'Important zone' },
  /* Climate */
  sun:           { ring: INK, june: C.amber, equinox: INK, dec: '#c05f2a', label: 'Sun path' },
  windrose:      { stroke: INK, fill: 'rgba(7,39,45,0.20)', width: 1, label: 'Wind rose' },
  windarrow:     { stroke: C.blue, width: 1.5, storm: { fill: 'rgba(138,148,154,0.38)', stroke: C.gray }, breeze: { dash: [8, 5] }, label: 'Wind' },
  /* Influences */
  viewarrow:     { stroke: INK, width: 2.2, ray: { width: 0.9, dash: [2, 4] }, label: 'View' },
  viewcone:      { stroke: INK, width: 0.9, dash: [2, 4], fill: 'rgba(7,39,45,0.045)', label: 'View cone' },
  contextarc:    { stroke: C.teal, width: 1.4, dash: [9, 5], label: 'Context' },
  approach:      { stroke: INK, width: 1.6, dash: [14, 7], label: 'Approach' },
  nuisance:      { stroke: C.red, width: 1.6, label: 'Nuisance' },
  slope:         { stroke: INK, width: 1, dash: [6, 4], label: 'Slope' },
  /* Site features */
  tree:          { stroke: C.green, fill: 'rgba(90,138,60,0.14)', width: 1.1, label: 'Significant tree' },
  utilpoint:     { r: 5, label: 'Utility point' },   // per-type color/shape comes from UTIL_POINT_TYPES
  utilline:      { stroke: C.teal, width: 1.2, dash: [2, 5], label: 'Utility line' },
  hazardedge:    { stroke: C.red, width: 1.6, label: 'Hazard edge' },
  keytag:        { stroke: INK, r: 9, label: 'Key tag' },
  corner:        { stroke: INK, fill: C.gray, label: 'Corner elevation' },
  label:         { color: INK, size: 12, ls: 1.5, label: 'Note' },
  /* Utility, not a kit-of-parts design element — a quick way to check/verify distances against
     the calibrated scale. Deliberately plain (no fill, no accent color) so it doesn't read as a
     "real" site feature next to the rest of the vocabulary. */
  dimension:     { stroke: INK, width: 1, tick: 5, label: 'Dimension' },
}
/* Utility point sub-types — the "what am I missing" expansion beyond well/septic. Each has a
   locked color + marker shape (part of the standardization contract same as SITE_STYLES).
   Loosely follows the APWA uniform utility color code (red=electric, yellow=gas, blue=water,
   green=sewer, orange=communications) so the palette also reads correctly to a GC. 'other' is
   the legacy numbered catch-all (was ptype 'site' pre-Round-24 — see utilTypeDef()). */
const UTIL_POINT_TYPES = [
  { key: 'water_meter',    label: 'Water Meter',       color: C.blue,   shape: 'circle' },
  { key: 'water_poc',      label: 'Water POC',         color: C.blue,   shape: 'diamond' },
  { key: 'gas_meter',      label: 'Gas Meter',         color: C.amber,  shape: 'circle' },
  { key: 'gas_poc',        label: 'Gas POC',           color: C.amber,  shape: 'diamond' },
  { key: 'elec_meter',     label: 'Electrical Meter',  color: C.red,    shape: 'circle' },
  { key: 'elec_panel',     label: 'Electrical Panel',  color: C.red,    shape: 'square' },
  { key: 'hydrant',        label: 'Fire Hydrant',      color: C.red,    shape: 'hydrant' },
  { key: 'sewer_cleanout', label: 'Sewer Cleanout',    color: C.green,  shape: 'circle' },
  { key: 'telecom',        label: 'Telecom / Cable',   color: C.orange, shape: 'diamond' },
  { key: 'well',           label: 'Well',              color: C.teal,   shape: 'circle' },
  { key: 'septic',         label: 'Septic',            color: '#8a3324', shape: 'circle' },
  { key: 'other',          label: 'Other',             color: C.gray,   shape: 'circle', numbered: true },
]
function utilTypeDef(ptype) {
  return UTIL_POINT_TYPES.find(u => u.key === (ptype === 'site' ? 'other' : ptype)) || UTIL_POINT_TYPES[UTIL_POINT_TYPES.length - 1]
}
/* Corner elevation markers — tied directly to the project's Parcel record so the site diagram
   and Parcel Research never duplicate the same measurement (services/parcels.js#updateParcelField
   writes the SAME Airtable fields ParcelResearchTool.jsx edits). Note these are the real field
   names (High/Low side, i.e. topographic), not Front/Rear (street-facing) — see docs. */
const CORNER_ROLES = ['high1', 'high2', 'low1', 'low2']
const CORNER_LABEL = { high1: 'High Corner 1', high2: 'High Corner 2', low1: 'Low Corner 1', low2: 'Low Corner 2' }
const CORNER_FIELD = {
  high1: 'Lot High Corner 1 Elevation', high2: 'Lot High Corner 2 Elevation',
  low1: 'Lot Low Corner 1 Elevation', low2: 'Lot Low Corner 2 Elevation',
}
const KIT_ORDER = ['pl', 'setback', 'envelope', 'easement', 'structure', 'water', 'vegmass', 'boulders',
  'importantzone', 'hazardedge', 'tree', 'utilpoint', 'utilline', 'corner', 'sun', 'windrose', 'windarrow',
  'viewarrow', 'viewcone', 'contextarc', 'approach', 'nuisance', 'slope', 'keytag', 'dimension']
/* Geometry families — how each kind is PLACED and edited. */
const POLY_KINDS = ['pl', 'easement', 'structure', 'water', 'vegmass', 'boulders', 'importantzone']  // closed, click-to-place
const OPEN_LINE_KINDS = ['setback', 'approach', 'utilline', 'hazardedge']                             // open polylines
const ARROW_KINDS = ['windarrow', 'viewarrow', 'slope', 'dimension']                                  // drag from → to
const POINT_KINDS = ['sun', 'windrose', 'tree', 'utilpoint', 'nuisance', 'viewcone', 'keytag', 'corner']  // click-drop symbols
const DRAFT_KINDS = [...POLY_KINDS, ...OPEN_LINE_KINDS, 'contextarc']
const isOpenKind = k => OPEN_LINE_KINDS.includes(k) || k === 'contextarc' || ARROW_KINDS.includes(k)

function decodeImg(src) {
  return new Promise((resolve, reject) => { const img = new Image(); img.onload = () => resolve(img); img.onerror = reject; img.src = src })
}
function u8ToDataUrl(u8) {
  let s = ''
  for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000))
  return 'data:application/pdf;base64,' + btoa(s)
}
function dataUrlToU8(du) {
  const b = atob(du.slice(du.indexOf(',') + 1))
  const u8 = new Uint8Array(b.length)
  for (let i = 0; i < b.length; i++) u8[i] = b.charCodeAt(i)
  return u8
}

let _idSeq = 1
const newId = () => Date.now() + '_' + (_idSeq++)

/* ── Geometry ── */
function pointInPoly(x, y, pts) {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    if ((pts[i].y > y) !== (pts[j].y > y) && x < (pts[j].x - pts[i].x) * (y - pts[i].y) / (pts[j].y - pts[i].y) + pts[i].x) inside = !inside
  }
  return inside
}
function distToSeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy
  const t = l2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l2)) : 0
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}
/* Buildable envelope = intersection of the parcel with each edge's inward setback half-plane.
   This is the legally-correct definition of a setback envelope (not a "stroke offset outline"),
   and it's numerically robust for ANY polygon shape and ANY combination of per-edge setbacks —
   including wildly different values on adjacent edges. The prior approach (mitered offset lines
   intersected pairwise with their neighbors) self-intersects whenever offsets vary sharply
   between adjacent edges, producing exactly the pinched/crossed "bow-tie" artifacts seen on
   irregular lots. Sequential half-plane clipping (Sutherland–Hodgman) can't produce an invalid
   polygon — each clip step either shrinks the shape or leaves it unchanged. Returns null if the
   setbacks consume the entire parcel (nothing buildable left). */
function clipHalfPlane(poly, ax, ay, nx, ny) {
  const out = []
  const n = poly.length
  for (let i = 0; i < n; i++) {
    const cur = poly[i], prev = poly[(i - 1 + n) % n]
    const curSide = (cur.x - ax) * nx + (cur.y - ay) * ny
    const prevSide = (prev.x - ax) * nx + (prev.y - ay) * ny
    const curIn = curSide >= -1e-6, prevIn = prevSide >= -1e-6
    if (curIn !== prevIn) {
      const t = prevSide / (prevSide - curSide)
      out.push({ x: prev.x + (cur.x - prev.x) * t, y: prev.y + (cur.y - prev.y) * t })
    }
    if (curIn) out.push(cur)
  }
  return out
}
function clipEnvelope(pts, dists) {
  const n = pts.length
  if (n < 3) return null
  let area = 0
  for (let i = 0; i < n; i++) { const j = (i + 1) % n; area += pts[i].x * pts[j].y - pts[j].x * pts[i].y }
  const ccw = area > 0
  let poly = pts.map(p => ({ x: p.x, y: p.y }))
  for (let i = 0; i < n; i++) {
    const d = dists[i] || 0
    if (!(d > 0)) continue
    const a = pts[i], b = pts[(i + 1) % n]
    const ex = b.x - a.x, ey = b.y - a.y, len = Math.hypot(ex, ey) || 1
    // inward normal (screen space is y-DOWN, so the winding sign inverts vs. math convention)
    const nx = ccw ? -ey / len : ey / len
    const ny = ccw ? ex / len : -ex / len
    poly = clipHalfPlane(poly, a.x + nx * d, a.y + ny * d, nx, ny)
    if (poly.length < 3) return null
  }
  return poly
}
/* Mitered per-edge offset (a true "parallel offset" outline): each vertex is the intersection
   of its two adjacent edges' offset lines, so a RUN of consecutive edges sharing the same
   setback (e.g. several short segments approximating a gently-curved frontage) offsets as a
   faceted line that follows the original shape — clipEnvelope's global half-plane clip instead
   lets whichever edge's plane is most restrictive dominate across the whole run, flattening out
   the individual segments (the "front doesn't offset" bug). A miter join between two very
   differently-offset edges can shoot arbitrarily far out, so any join beyond `miterLimit` times
   the larger adjacent setback falls back to a bevel (two points, one per adjacent offset line) —
   the standard stroke-rendering technique for exactly this failure mode. Returns a polygon that
   may have more vertices than the input (one extra per bevel). */
function miterOffset(pts, dists, miterLimit = 4) {
  const n = pts.length
  if (n < 3) return null
  let area = 0
  for (let i = 0; i < n; i++) { const j = (i + 1) % n; area += pts[i].x * pts[j].y - pts[j].x * pts[i].y }
  const ccw = area > 0
  const lines = pts.map((a, i) => {
    const b = pts[(i + 1) % n]
    const ex = b.x - a.x, ey = b.y - a.y, len = Math.hypot(ex, ey) || 1
    const nx = ccw ? -ey / len : ey / len, ny = ccw ? ex / len : -ex / len
    const d = dists[i] || 0
    return { x: a.x + nx * d, y: a.y + ny * d, dx: ex, dy: ey, nx, ny, d }
  })
  const out = []
  for (let i = 0; i < n; i++) {
    const L1 = lines[(i - 1 + n) % n], L2 = lines[i], orig = pts[i]
    const bevelA = { x: orig.x + L1.nx * L1.d, y: orig.y + L1.ny * L1.d }
    const bevelB = { x: orig.x + L2.nx * L2.d, y: orig.y + L2.ny * L2.d }
    const det = L1.dx * L2.dy - L1.dy * L2.dx
    if (Math.abs(det) < 1e-9) { out.push(bevelA); continue }
    const t = ((L2.x - L1.x) * L2.dy - (L2.y - L1.y) * L2.dx) / det
    const mx = L1.x + t * L1.dx, my = L1.y + t * L1.dy
    const miterDist = Math.hypot(mx - orig.x, my - orig.y)
    const maxAllowed = miterLimit * Math.max(L1.d, L2.d, 1e-6)
    if (miterDist <= maxAllowed) out.push({ x: mx, y: my })
    else { out.push(bevelA); out.push(bevelB) }
  }
  return out
}
function polygonSelfIntersects(poly) {
  const n = poly.length
  function segCross(p1, p2, p3, p4) {
    const d = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y)
    if (Math.abs(d) < 1e-9) return false
    const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / d
    const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / d
    return ua > 1e-6 && ua < 1 - 1e-6 && ub > 1e-6 && ub < 1 - 1e-6
  }
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1)) continue
      if (segCross(poly[i], poly[(i + 1) % n], poly[j], poly[(j + 1) % n])) return true
    }
  }
  return false
}
/* The envelope shown to the user: prefer the mitered offset (it faithfully follows every edge,
   including runs of same-setback segments), falling back to the always-valid half-plane clip
   only if the miter+bevel result still comes out self-intersecting (rare — large, sharply
   mismatched adjacent setbacks on a tight polygon). */
function computeEnvelope(pts, dists) {
  const mitered = miterOffset(pts, dists)
  if (mitered && mitered.length >= 3 && !polygonSelfIntersects(mitered)) return mitered
  return clipEnvelope(pts, dists)
}
/* Sunrise azimuth (radians east of true north) for a latitude + solar declination:
   cos(Az) = sin(dec)/cos(lat). Sunset mirrors west (2π − Az). Clamped for polar edge cases. */
function sunriseAzimuth(latDeg, decDeg) {
  const lat = latDeg * Math.PI / 180, dec = decDeg * Math.PI / 180
  return Math.acos(Math.min(1, Math.max(-1, Math.sin(dec) / Math.cos(lat))))
}
/* ── True solar position ────────────────────────────────────────────────────
   Azimuth (radians east of TRUE north) + altitude for a latitude, solar declination, and SOLAR
   hour (12 = solar noon). Standard alt/az formulas from the hour angle H = 15°·(t−12):
     sin(alt) = sin(lat)sin(dec) + cos(lat)cos(dec)cos(H)
     cos(az)  = (sin(dec) − sin(alt)sin(lat)) / (cos(alt)cos(lat)),  mirrored west after noon.
   Verified in Node against theory: solar-noon altitude = 90 − lat + dec exactly; Jun 21 9AM at
   lat 33.54 → az 92.4° alt 49.5°; Dec 21 9AM → az 136.8° alt 18.7°; pre-sunrise → alt < 0.
   THIS is the source of the solar vectors — never points on the symbolic circle. */
function solarPosition(latDeg, decDeg, solarHour) {
  const lat = latDeg * Math.PI / 180, dec = decDeg * Math.PI / 180
  const H = (solarHour - 12) * 15 * Math.PI / 180
  const sinAlt = Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(H)
  const altitude = Math.asin(Math.min(1, Math.max(-1, sinAlt)))
  const cosAz = (Math.sin(dec) - sinAlt * Math.sin(lat)) / ((Math.cos(altitude) * Math.cos(lat)) || 1e-12)
  let azimuth = Math.acos(Math.min(1, Math.max(-1, cosAz)))
  if (H > 0) azimuth = Math.PI * 2 - azimuth
  return { azimuth, altitude }
}
/* Clock hour → solar hour. Longitude shifts local clock time away from solar time by the
   distance to the time-zone meridian (4 min/degree). With no longitude, clock time is treated as
   solar time directly (correct to within ~±30 min — fine for a diagram). utcOffset (hours, e.g.
   −8) overrides the inferred zone meridian when a site straddles a zone boundary. */
function solarHourFor(clockHour, lngDeg, utcOffset) {
  if (lngDeg == null || !isFinite(lngDeg)) return clockHour
  const tzMeridian = (utcOffset != null && isFinite(utcOffset) ? utcOffset : Math.round(lngDeg / 15)) * 15
  return clockHour + (lngDeg - tzMeridian) / 15
}
/* The season/time matrix behind the solar vectors. `key` doubles as the settings toggle id. */
const SUN_SEASONS = [
  { key: 'summerSolstice', dec: 23.44, lbl: 'JUN 21' },
  { key: 'equinox', dec: 0, lbl: 'MAR/SEP 21' },
  { key: 'winterSolstice', dec: -23.44, lbl: 'DEC 21' },
]
const SUN_VECTOR_HOURS = [{ h: 9, lbl: '9 AM' }, { h: 12, lbl: '12 PM' }, { h: 15, lbl: '3 PM' }]
/* Per-sun-item settings with defaults (persisted on the item as `it.solar`; absent = defaults).
   mode: 'paths' (circle only) | 'vectors' (paths + key vectors) | 'debug' (+ technical labels).
   vectorSet 'presentation' = Jun/Dec 9AM+3PM (+noon via showNoon); 'full' = all 9 season/time
   combinations. (A 'wash' mode existed briefly — removed at user request; old saves with it
   normalize to 'vectors'.) */
function sunSettings(it) {
  const s = it.solar || {}
  return {
    mode: s.mode === 'wash' ? 'vectors' : (s.mode || 'vectors'),
    radiusMode: s.radiusMode || (it.solar ? 'auto' : 'manual'),   // pre-existing suns keep their manual r
    radiusMultiplier: s.radiusMultiplier || 1.35,
    vectorSet: s.vectorSet || 'presentation',
    showNoon: !!s.showNoon,
    utcOffset: s.utcOffset != null ? s.utcOffset : null,
    // '<seasonKey>@<hour>' of the moment under study (parallel rays across the site), or null.
    // One moment at a time by design — all moments at once was unreadable.
    focus: s.focus || null,
  }
}
/* Which season/hour pairs are active for a given settings object. */
function sunVectorTimes(cfg) {
  const out = []
  for (const season of SUN_SEASONS) {
    for (const t of SUN_VECTOR_HOURS) {
      if (cfg.vectorSet !== 'full') {
        if (season.key === 'equinox') continue                       // presentation: solstices only
        if (t.h === 12 && !cfg.showNoon) continue                    // noon is opt-in
      }
      out.push({ season, time: t })
    }
  }
  return out
}
/* Circumcenter of 3 points (null if collinear) — the contextarc passes through all three. */
function circumcenter(A, B, C) {
  const d = 2 * (A.x * (B.y - C.y) + B.x * (C.y - A.y) + C.x * (A.y - B.y))
  if (Math.abs(d) < 1e-9) return null
  const a2 = A.x * A.x + A.y * A.y, b2 = B.x * B.x + B.y * B.y, c2 = C.x * C.x + C.y * C.y
  return {
    x: (a2 * (B.y - C.y) + b2 * (C.y - A.y) + c2 * (A.y - B.y)) / d,
    y: (a2 * (C.x - B.x) + b2 * (A.x - C.x) + c2 * (B.x - A.x)) / d,
  }
}
/* ── Bezier edges (Illustrator-style) ──────────────────────────────────────
   A vertex may carry RELATIVE control handles: `ho` shapes the edge leaving it, `hi` the
   edge arriving at it. An edge is a cubic bezier when either end contributes a handle.
   Everything downstream (hit tests, setback offsets, bbox, fills) works on the FLATTENED
   polyline from flattenItemPts(), so the straight-edge math is untouched. Handles being
   relative means item drags translate them for free. */
const CURVE_STEPS = 16
const isCurvableKind = k => POLY_KINDS.includes(k) || OPEN_LINE_KINDS.includes(k)
function edgeCurve(it, i) {
  const pts = it.pts, n = pts.length
  const a = pts[i], b = pts[(i + 1) % n]
  if (!a.ho && !b.hi) return null
  return {
    c1: { x: a.x + (a.ho ? a.ho.x : 0), y: a.y + (a.ho ? a.ho.y : 0) },
    c2: { x: b.x + (b.hi ? b.hi.x : 0), y: b.y + (b.hi ? b.hi.y : 0) },
  }
}
function cubicAt(a, c1, c2, b, t) {
  const u = 1 - t
  return {
    x: u * u * u * a.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * b.x,
    y: u * u * u * a.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * b.y,
  }
}
function hasCurves(it) { return !!(it.pts && it.pts.some(p => p.hi || p.ho)) }
/* Flattened outline: [{ x, y, edge, t }] — each sample tagged with the ORIGINAL edge index
   (so per-edge setbacks survive flattening) and its bezier parameter. */
function flattenItemPts(it, closed) {
  const pts = it.pts, n = pts.length
  const edgeCount = closed ? n : n - 1
  const out = []
  for (let i = 0; i < n; i++) {
    out.push({ x: pts[i].x, y: pts[i].y, edge: Math.min(i, edgeCount - 1), t: 0 })
    if (i >= edgeCount) continue
    const cv = edgeCurve(it, i)
    if (cv) {
      const b = pts[(i + 1) % n]
      for (let s = 1; s < CURVE_STEPS; s++) {
        const t = s / CURVE_STEPS
        const p = cubicAt(pts[i], cv.c1, cv.c2, b, t)
        out.push({ x: p.x, y: p.y, edge: i, t })
      }
    }
  }
  return out
}
/* Canvas path following the item's real geometry (lines + beziers). */
function itemPath(g, it, close) {
  const pts = it.pts, n = pts.length
  g.beginPath()
  g.moveTo(pts[0].x, pts[0].y)
  const edgeCount = close ? n : n - 1
  for (let i = 0; i < edgeCount; i++) {
    const b = pts[(i + 1) % n]
    const cv = edgeCurve(it, i)
    if (cv) g.bezierCurveTo(cv.c1.x, cv.c1.y, cv.c2.x, cv.c2.y, b.x, b.y)
    else g.lineTo(b.x, b.y)
  }
  if (close) g.closePath()
}
/* De Casteljau split of edge i at parameter t — inserts the new anchor with handles that
   preserve the curve exactly (the Illustrator add-node behavior). */
function splitEdge(it, i, t, closed) {
  const pts = it.pts, n = pts.length
  const a = pts[i], b = pts[(i + 1) % n]
  const cv = edgeCurve(it, i)
  let M
  if (cv) {
    const lerp = (p, q, tt) => ({ x: p.x + (q.x - p.x) * tt, y: p.y + (q.y - p.y) * tt })
    const q0 = lerp(a, cv.c1, t), q1 = lerp(cv.c1, cv.c2, t), q2 = lerp(cv.c2, b, t)
    const r0 = lerp(q0, q1, t), r1 = lerp(q1, q2, t)
    const m = lerp(r0, r1, t)
    M = { x: m.x, y: m.y, hi: { x: r0.x - m.x, y: r0.y - m.y }, ho: { x: r1.x - m.x, y: r1.y - m.y } }
    a.ho = { x: q0.x - a.x, y: q0.y - a.y }
    b.hi = { x: q2.x - b.x, y: q2.y - b.y }
  } else {
    M = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }
  }
  pts.splice(i + 1, 0, M)
  // Keep per-edge setbacks/roles aligned: the split halves inherit the original edge's values.
  if (it.setbacks) it.setbacks.splice(i + 1, 0, it.setbacks[i] || 0)
  if (it.edgeRoles) it.edgeRoles.splice(i + 1, 0, it.edgeRoles[i] || null)
  void closed
  return i + 1
}
/* Setback distance (feet) for edge `i` of a `pl` — role-based (`edgeRoles[i]` → `roleSetbacks`)
   when present, falling back to the legacy per-edge `setbacks[i]` array for older saves that
   predate the role system. */
function edgeSetbackFt(it, i) {
  if (it.edgeRoles) {
    const role = it.edgeRoles[i]
    return (role && it.roleSetbacks && it.roleSetbacks[role]) || 0
  }
  return (it.setbacks && it.setbacks[i]) || 0
}
function hasAnySetback(it) {
  if (it.edgeRoles && it.roleSetbacks) return Object.values(it.roleSetbacks).some(v => v > 0)
  return !!(it.setbacks && it.setbacks.some(d => d > 0))
}

/* World-radius a point symbol occupies — hit-testing + bbox padding. */
/* The site's real footprint — property-line bbox (falling back to any drawn geometry), shared by
   the sun circle's auto radius AND the parallel solar-influence rays (which must cross the SITE,
   not converge on the diagram — sunlight arrives as parallel rays from effectively infinite
   distance; the building is not the center of the solar system). Null when nothing is drawn. */
function siteFootprint(doc) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, any = false
  const pls = doc.items.filter(i2 => i2.kind === 'pl' && i2.pts && i2.pts.length > 2)
  const source = pls.length ? pls : doc.items.filter(i2 => i2.pts && i2.pts.length > 1)
  for (const src of source) {
    for (const p of src.pts) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); any = true }
  }
  if (!any) return null
  const w = maxX - minX, h = maxY - minY
  return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, w, h, maxDim: Math.max(w, h) }
}
/* The sun circle's radius is GRAPHIC, not analytical — vector directions never derive from it.
   Auto mode sizes it off the site footprint (spec: 1.35 × max(bbox W, H)), clamped so it always
   clears the PL if roughly centered on it but can't dominate the 11×17 sheet. Manual keeps `r`. */
function sunAutoRadius(it, doc) {
  const cfg = sunSettings(it)
  if (cfg.radiusMode === 'manual') return it.r || 90
  const fp = siteFootprint(doc)
  if (!fp) return it.r || 90
  const raw = cfg.radiusMultiplier * fp.maxDim
  const sheetCap = doc.canvas ? 0.5 * Math.max(doc.canvas.w, doc.canvas.h) : Infinity
  return Math.max(60, Math.max(0.75 * fp.maxDim, Math.min(raw, sheetCap)))
}
function pointExtent(it, doc) {
  if (it.kind === 'sun') return (doc ? sunAutoRadius(it, doc) : (it.r || 90)) + 30
  if (it.kind === 'windrose') return (it.r || 55) + 14
  if (it.kind === 'tree') return doc && doc.scalePixPerFoot && it.diaFt ? Math.max(12, (it.diaFt * doc.scalePixPerFoot) / 2) : 18
  if (it.kind === 'viewcone') return 26
  if (it.kind === 'nuisance') return 34
  return 12
}

/* Draggable divider for the right panel — same convention as QcMarkupModal's ResizeHandle
   (dragging left widens the panel since it's anchored to the right edge). */
function PanelResizeHandle({ getStart, onChange, onCommit }) {
  function down(e) {
    e.preventDefault()
    const startX = e.clientX
    const startW = getStart()
    let last = startW
    function move(ev) {
      const w = Math.max(PANEL_MIN, Math.min(PANEL_MAX, startW - (ev.clientX - startX)))
      last = w
      onChange(w)
    }
    function up() {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      onCommit && onCommit(last)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }
  return (
    <div
      onPointerDown={down}
      title="Drag to resize"
      style={{ width: 6, flexShrink: 0, cursor: 'col-resize', background: 'var(--aj-bg-muted)', borderLeft: '0.5px solid var(--aj-border)', borderRight: '0.5px solid var(--aj-border)' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-accent)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--aj-bg-muted)' }}
    />
  )
}

/* ── Host — mounted once in OutlineView ─────────────────────────────────────── */
export default function SiteAnalysisHost() {
  const [launch, setLaunch] = useState(null)
  useEffect(() => {
    const h = e => setLaunch(e.detail || null)
    window.addEventListener('open-site-analysis-modal', h)
    return () => window.removeEventListener('open-site-analysis-modal', h)
  }, [])
  if (!launch) return null
  return <SiteAnalysisModal key={launch.msId} {...launch} onClose={() => setLaunch(null)} />
}

/* ── The tool ───────────────────────────────────────────────────────────────── */
function SiteAnalysisModal({ msId, projectId, projectName, onClose }) {
  const wrapRef = useRef(null)
  const compRef = useRef(null)
  const uiRef = useRef(null)
  const evRef = useRef(null)
  const fileRef = useRef(null)

  // Document — lives in a ref (imperative engine); React re-renders via tick.
  const docRef = useRef(null)
  const vpRef = useRef({ x: 0, y: 0, z: 1 })
  const [, setTick] = useState(0)
  const bump = () => setTick(t => t + 1)

  // One analysis per project (Round 27) — no list, no switching, never deleted. `analysisIdRef`
  // mirrors the state for `doSave`/the autosave interval: the interval effect below has an
  // empty dependency array, so anything it calls must read CURRENT values via a ref, never a
  // closed-over state variable — that gap is exactly what caused ~80 duplicate rows to get
  // created (one every 30s) before this was a ref: the interval's `doSave` closure captured
  // `analysisId` as it was at mount (null) and never saw it become non-null.
  const [analysisId, setAnalysisId] = useState(null)
  const analysisIdRef = useRef(null)
  useEffect(() => { analysisIdRef.current = analysisId }, [analysisId])
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState('')
  const [selBgId, setSelBgId] = useState(null)
  const [calibrating, setCalibrating] = useState(false)
  const dirtyRef = useRef(false)
  const savingRef = useRef(false)
  const calPtsRef = useRef([])
  const dragRef = useRef(null)
  // Transient, per-item — repopulated every render by labelAnchor() below, never persisted (kept
  // out of the item object entirely so it can't leak into save/undo snapshots). Maps item id ->
  // { baseX, baseY, x, y } so the selected item's label can be hit-tested and dragged
  // independently of the item's own geometry (labelOffset is the persisted {dx,dy}).
  const labelAnchorsRef = useRef(new Map())
  const toastTimer = useRef(0)
  const [toast, setToast] = useState('')
  const [panelW, setPanelW] = useState(() => {
    const saved = parseInt(localStorage.getItem('aj-sa-panelw'), 10)
    return saved >= PANEL_MIN && saved <= PANEL_MAX ? saved : PANEL_W
  })
  // Collapse state for the top-level right-panel sections (Site / Backgrounds / Objects) —
  // persisted like panel width so a collapsed layout sticks across sessions.
  const [collapsedPanels, setCollapsedPanels] = useState(() => {
    try { return JSON.parse(localStorage.getItem('aj-sa-collapsed') || '{}') } catch { return {} }
  })
  function togglePanel(key) {
    setCollapsedPanels(prev => {
      const next = { ...prev, [key]: !prev[key] }
      try { localStorage.setItem('aj-sa-collapsed', JSON.stringify(next)) } catch { /* private mode */ }
      return next
    })
  }
  /* Clickable section header — chevron rotates to show collapsed/expanded state. Replaces a bare
     `<div style={plbl}>Label</div>` wherever a top-level panel section should be collapsible. */
  function SectionHeader({ panelKey, children }) {
    const collapsed = !!collapsedPanels[panelKey]
    return (
      <div onClick={() => togglePanel(panelKey)}
        style={{ ...plbl, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ display: 'inline-block', fontSize: 8, transition: 'transform 0.15s ease', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
        {children}
      </div>
    )
  }

  function showToast(msg) {
    setToast(msg)
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(''), 2400)
  }
  function markDirty() { dirtyRef.current = true }
  // Scroll-to-nudge for every number input in the properties panels (setbacks, rotation, lat,
  // etc). A plain stopPropagation() was never enough — browsers only auto-step a number input on
  // wheel when it's focused (not just hovered), and even then the page can still scroll first.
  // Steps the value by the input's own `step` (falls back to 1) and fires it through the SAME
  // native value setter + 'input' event React's controlled-input tracking expects, so the
  // input's existing onChange (with all its own clamping/unit conversion) runs unchanged — this
  // only changes how the new value gets in, not what happens to it once it arrives.
  const nativeInputSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set
  function numberWheel(e) {
    e.preventDefault()
    e.stopPropagation()
    const el = e.currentTarget
    const step = parseFloat(el.step) || 1
    const cur = parseFloat(el.value) || 0
    nativeInputSetter.call(el, String(cur + (e.deltaY < 0 ? step : -step)))
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }

  // Kit tool + item selection + drafting + undo. Refs mirror the state for the
  // imperative pointer handlers (which never see fresh closures).
  const [tool, setTool] = useState('select')
  const toolRef = useRef('select')
  useEffect(() => { toolRef.current = tool }, [tool])
  const [selItemId, setSelItemId] = useState(null)
  const selItemIdRef = useRef(null)
  useEffect(() => { selItemIdRef.current = selItemId; scheduleRender() }, [selItemId]) // eslint-disable-line react-hooks/exhaustive-deps
  const draftRef = useRef(null)
  /* Undo/redo — COMMIT-based so the stacks only ever hold real changes. Selection clicks and
     drags that end where they started must not pollute the history (that was the "undo is
     confused" bug: every click pushed a snapshot, so Ctrl+Z appeared to undo the wrong thing).
     - Instant mutations (place, delete, reorder, panel edits) call saveUndo() just before
       mutating — they always change something.
     - Drag gestures call gestureStart() on pointer-down and gestureEnd() on pointer-up; the
       before-state commits ONLY if the gesture actually changed the items. */
  const undoRef = useRef([])
  const redoRef = useRef([])
  const gestureRef = useRef(null)
  /* The undo snapshot is the full editable document state MINUS the heavy background assets:
     kit items, background transforms (position/size/rotation/opacity/order/lock — matched back
     by id), stamp positions, wash, north, latitude, scale. Adding/removing a background stays
     outside undo (the pixels can't come back from a JSON string; remove is confirm-gated). */
  const snapState = () => {
    const doc = docRef.current
    return JSON.stringify({
      items: doc.items,
      bgs: doc.backgrounds.map(b => ({ id: b.id, x: b.x, y: b.y, w: b.w, h: b.h, rotation: b.rotation || 0, opacity: b.opacity != null ? b.opacity : 1, locked: !!b.locked, visible: b.visible !== false })),
      legend: doc.legend, northArrow: doc.northArrow, keyNotes: doc.keyNotes,
      wash: doc.wash, north: doc.north, latitude: doc.latitude, longitude: doc.longitude, scalePixPerFoot: doc.scalePixPerFoot,
      geoAnchor: doc.geoAnchor, satCorrection: doc.satCorrection,
    })
  }
  function applyState(s) {
    const doc = docRef.current
    const st = JSON.parse(s)
    doc.items = st.items
    const byId = new Map(doc.backgrounds.map(b => [b.id, b]))
    const ordered = []
    for (const meta of st.bgs) {
      const b = byId.get(meta.id)
      if (!b) continue   // background was removed since this snapshot — nothing to restore onto
      if (b.w !== meta.w || b.h !== meta.h) b._pdfCache = null
      Object.assign(b, { x: meta.x, y: meta.y, w: meta.w, h: meta.h, rotation: meta.rotation, opacity: meta.opacity, locked: meta.locked, visible: meta.visible })
      byId.delete(meta.id)
      ordered.push(b)
    }
    for (const b of doc.backgrounds) if (byId.has(b.id)) ordered.push(b)   // added since snapshot: keep, on top
    doc.backgrounds = ordered
    doc.legend = st.legend
    doc.northArrow = st.northArrow
    doc.keyNotes = st.keyNotes
    doc.wash = st.wash
    doc.north = st.north
    doc.latitude = st.latitude
    doc.longitude = st.longitude
    doc.scalePixPerFoot = st.scalePixPerFoot
    doc.geoAnchor = st.geoAnchor
    doc.satCorrection = st.satCorrection
  }
  function commitUndo(before) {
    undoRef.current.push(before)
    if (undoRef.current.length > 50) undoRef.current.shift()
    redoRef.current = []
    markDirty()
  }
  function saveUndo() { commitUndo(snapState()) }
  function gestureStart() { gestureRef.current = snapState() }
  function gestureEnd() {
    if (gestureRef.current != null && gestureRef.current !== snapState()) commitUndo(gestureRef.current)
    gestureRef.current = null
  }
  function undo() {
    const s = undoRef.current.pop()
    if (s == null) return
    redoRef.current.push(snapState())
    applyState(s)
    setSelItemId(null); setSelBgId(null); markDirty(); scheduleRender(); bump()
  }
  function redo() {
    const s = redoRef.current.pop()
    if (s == null) return
    undoRef.current.push(snapState())
    applyState(s)
    setSelItemId(null); setSelBgId(null); markDirty(); scheduleRender(); bump()
  }
  const [showKeys, setShowKeys] = useState(false)
  const [cropPreview, setCropPreview] = useState(false)   // hide anything off the 11×17 sheet while editing
  const [satBusy, setSatBusy] = useState(false)
  // The project's primary Parcel record (Airtable `{ id, fields }`), fetched best-effort at
  // boot — ties corner-elevation markers to Parcel Research without duplicating the values.
  // null = no parcel yet (corner markers still work, just store their value locally only).
  const parcelRef = useRef(null)
  // Shared thumbnail page picker (QC / sheet revisions / takeoffs) — multi-page PDFs resolve
  // to a client-side-extracted single-page File; unpicked pages never leave the browser.
  const { resolveUpload, picker } = usePdfPagePicker()

  // Lock BOTH html + body scroll while the tool is open — otherwise the outline behind it can
  // still scroll and its scrollbar stays visible alongside the tool's own panels (QcMarkupModal
  // uses the identical pattern).
  useEffect(() => {
    const b = document.body.style.overflow, h = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'; document.documentElement.style.overflow = 'hidden'
    return () => { document.body.style.overflow = b; document.documentElement.style.overflow = h }
  }, [])

  function freshDoc() {
    return {
      version: 1, north: 0, latitude: 33.54, longitude: null, scalePixPerFoot: null, wash: 0.35,
      backgrounds: [], items: [],
      legend: { visible: true, x: null, y: null },
      northArrow: { x: null, y: null },              // independent placeable stamp (null = default corner)
      keyNotes: { visible: true, x: null, y: null }, // "1  description" list for numbered key tags
      canvas: { x: 0, y: 0, w: 1700, h: 1100 },      // the sheet — 11×17 landscape; export crops to it
      // Ties ONE world-space point to a real lat/lng — set once, the first time a satellite
      // view is fetched (at whatever the viewport happened to be centered on). Everything the
      // satellite layer does afterward (refresh at a new pan/zoom) projects through this same
      // anchor + doc.scalePixPerFoot + doc.north, so repeated refreshes — even across sessions,
      // since this persists — stay geographically consistent with each other and with any view
      // vectors already drawn toward a distant landmark. See worldToLatLng/latLngToWorld.
      geoAnchor: null,   // { lat, lng, worldX, worldY } | null
      // Empirical correction multiplier for the satellite ground-resolution formula's
      // feetPerNativePx guess, established by manually resizing the image to match the drawing
      // (see onPointerUp's 'resize' handling) — persists so the correction survives reloads and
      // improves every future fetch's starting size instead of needing to be redone each time.
      satCorrection: null,
    }
  }
  /* Meters per 1 world-unit — world units are canvas pixels calibrated to real feet via
     doc.scalePixPerFoot (world-units per foot). */
  function metersPerWorldUnit(doc) { return doc.scalePixPerFoot ? 0.3048 / doc.scalePixPerFoot : null }
  /* World point → { lat, lng } via a local flat-earth tangent-plane projection through
     doc.geoAnchor — accurate at any scale this tool is used at (site to multi-mile regional
     context), and correctly accounts for doc.north (world-up may not be true-north-up). */
  function worldToLatLng(wx, wy, doc) {
    const a = doc.geoAnchor, mpwu = metersPerWorldUnit(doc)
    if (!a || !mpwu) return null
    const dx = wx - a.worldX, dy = wy - a.worldY
    const north = doc.north || 0
    const cosN = Math.cos(-north), sinN = Math.sin(-north)
    const rx = dx * cosN - dy * sinN, ry = dx * sinN + dy * cosN
    const metersEast = rx * mpwu, metersNorth = -ry * mpwu
    const dLat = metersNorth / 111320
    const dLng = metersEast / (111320 * Math.cos(a.lat * Math.PI / 180))
    return { lat: a.lat + dLat, lng: a.lng + dLng }
  }
  /* Inverse of worldToLatLng — where does a given lat/lng land in world space. */
  function latLngToWorld(lat, lng, doc) {
    const a = doc.geoAnchor, mpwu = metersPerWorldUnit(doc)
    if (!a || !mpwu) return null
    const metersNorth = (lat - a.lat) * 111320
    const metersEast = (lng - a.lng) * 111320 * Math.cos(a.lat * Math.PI / 180)
    const rx = metersEast / mpwu, ry = -metersNorth / mpwu
    const north = doc.north || 0
    const cosN = Math.cos(north), sinN = Math.sin(north)
    return { x: a.worldX + (rx * cosN - ry * sinN), y: a.worldY + (rx * sinN + ry * cosN) }
  }

  /* ── Rendering ─────────────────────────────────────────────────────────── */
  function sw() { return compRef.current ? compRef.current.width : 0 }
  function sh() { return compRef.current ? compRef.current.height : 0 }
  function worldToScreen(wx, wy) { const v = vpRef.current; return { x: wx * v.z + v.x, y: wy * v.z + v.y } }
  function screenToWorld(sx, sy) { const v = vpRef.current; return { x: (sx - v.x) / v.z, y: (sy - v.y) / v.z } }

  const renderRaf = useRef(0)
  function scheduleRender() {
    if (renderRaf.current) return
    renderRaf.current = requestAnimationFrame(() => { renderRaf.current = 0; renderAll() })
  }
  function drawBackgrounds(g, doc, toScreen) {
    for (const bg of doc.backgrounds) {
      if (bg.visible === false) continue
      const src = (bg._pdfCache && bg._pdfCache.canvas) || bg.canvas
      if (!src) continue
      g.save()
      g.globalAlpha = bg.opacity != null ? bg.opacity : 1
      const p = toScreen(bg.x, bg.y)
      const z = vpRef.current.z
      g.translate(p.x, p.y)
      if (bg.rotation) { const cx = bg.w * z / 2, cy = bg.h * z / 2; g.translate(cx, cy); g.rotate(bg.rotation); g.translate(-cx, -cy) }
      g.drawImage(src, 0, 0, bg.w * z, bg.h * z)
      g.restore()
    }
  }
  /* Whitewash — the standard light-field treatment: one slider, identical on every map.
     Drawn over the SHEET only, so cropping reads on screen exactly as it exports. */
  function drawWash(g, doc, x, y, w, h) {
    if (!(doc.wash > 0)) return
    g.save()
    g.globalAlpha = doc.wash
    g.fillStyle = '#ffffff'
    g.fillRect(x, y, w, h)
    g.restore()
  }
  function polyPath(g, pts, close) {
    g.beginPath()
    g.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y)
    if (close) g.closePath()
  }
  /* Resolves a label's draw position: the item's own computed default anchor (baseX, baseY) plus
     whatever manual offset the user has dragged it to (it.labelOffset — persisted, {dx,dy} in
     world units). Also stashes the result in labelAnchorsRef so the selection overlay can draw a
     grip there and onPointerDown can hit-test it — kept OUT of the item object itself so it never
     leaks into save/undo snapshots (those serialize doc.items directly, unfiltered). Callers must
     pass baseX/baseY already in the same (world-absolute) coordinate space they draw in. */
  function labelAnchor(it, baseX, baseY) {
    const o = it.labelOffset
    const x = baseX + (o ? o.dx : 0), y = baseY + (o ? o.dy : 0)
    labelAnchorsRef.current.set(it.id, { baseX, baseY, x, y })
    return { x, y }
  }
  /* Rotates the canvas around a label's own anchor (x,y) before running drawFn, so every call
     inside drawFn keeps using its normal world coordinates — only the label ends up spun in
     place. it.labelRot is degrees, 0 = unrotated, set exclusively via the drag handle in
     drawOverlay/onPointerDown/onPointerMove (never a text input, per the ask). */
  function drawRotatedLabel(g, it, x, y, drawFn) {
    const rot = it.labelRot || 0
    if (!rot) { drawFn(); return }
    g.save()
    g.translate(x, y)
    g.rotate(rot * Math.PI / 180)
    g.translate(-x, -y)
    drawFn()
    g.restore()
  }
  const LABEL_ROT_HANDLE_PX = 22
  /* World-space position of the label rotate handle — a fixed SCREEN distance from the label's
     anchor (so it's equally easy to grab at any zoom), in the direction of the label's current
     rotation. Shared by drawOverlay (draws it) and onPointerDown (hit-tests it). */
  function labelRotateHandleWorld(it, la) {
    const rot = (it.labelRot || 0) * Math.PI / 180
    const R = LABEL_ROT_HANDLE_PX / vpRef.current.z
    return { x: la.x + Math.cos(rot) * R, y: la.y + Math.sin(rot) * R }
  }
  function drawInkLabel(g, text, x, y, size) {
    g.save()
    g.font = '600 ' + (size || SITE_STYLES.label.size) + 'px "DM Sans",sans-serif'
    try { g.letterSpacing = SITE_STYLES.label.ls + 'px' } catch { /* older engines */ }
    g.fillStyle = SITE_STYLES.label.color
    g.fillText(String(text || '').toUpperCase(), x, y)
    g.restore()
  }
  /* Greedy word-wrap using the context's CURRENT font — caller sets g.font before calling. */
  function wrapLines(g, text, maxWidth) {
    const words = String(text || '').split(/\s+/).filter(Boolean)
    const lines = []
    let cur = ''
    for (const w of words) {
      const test = cur ? cur + ' ' + w : w
      if (cur && g.measureText(test).width > maxWidth) { lines.push(cur); cur = w }
      else cur = test
    }
    if (cur) lines.push(cur)
    return lines.length ? lines : ['']
  }
  function arrowHead(g, from, to, size, filled, color) {
    const a = Math.atan2(to.y - from.y, to.x - from.x)
    g.save()
    g.translate(to.x, to.y); g.rotate(a)
    g.beginPath(); g.moveTo(0, 0); g.lineTo(-size, -size * 0.45); g.lineTo(-size, size * 0.45); g.closePath()
    if (filled) { g.fillStyle = color; g.fill() } else { g.strokeStyle = color; g.lineWidth = 1; g.stroke() }
    g.restore()
  }
  /* Direction sunlight TRAVELS across the plan for a compass azimuth (radians east of true
     north), converted to world/canvas coords (y-down) and rotated by document north. Light from
     a sun at azimuth A moves toward compass A+180: unit vector (sin, −cos) of that bearing, then
     rotated with the site. Verified in Node: sun due south → light travels up-sheet at north=0;
     rotating north 90° swings it to the right. */
  function sunLightDir(azimuth, doc) {
    const A = azimuth + Math.PI
    const x = Math.sin(A), y = -Math.cos(A)
    const n = doc.north || 0
    const c = Math.cos(n), s = Math.sin(n)
    return { x: x * c - y * s, y: x * s + y * c }
  }
  /* The active solar vectors for a sun item — season/time matrix filtered by settings, positions
     computed from TRUE solar azimuth (never from the circle). Below-horizon times are dropped. */
  function sunActiveVectors(it, doc) {
    const cfg = sunSettings(it)
    const lat = doc.latitude != null ? doc.latitude : 33.54
    const out = []
    for (const { season, time } of sunVectorTimes(cfg)) {
      const solarHour = solarHourFor(time.h, doc.longitude, cfg.utcOffset)
      const pos = solarPosition(lat, season.dec, solarHour)
      if (pos.altitude <= 0) continue   // sun below horizon at this time/season — nothing to draw
      out.push({ season, time, ...pos, dir: sunLightDir(pos.azimuth, doc) })
    }
    return out
  }
  /* Sun diagram — two complementary layers with deliberately different graphic weight:
     LAYER 1 (solar REFERENCE, symbolic): the circle. Circumference = horizon, seasonal arcs =
       the sun's apparent path, thin subordinate azimuth BEARINGS with a position marker on the
       arc = "where in the sky is the sun at this time." Radius is compositional; nothing
       directional derives from it.
     LAYER 2 (solar INFLUENCE, analytical): families of PERFECTLY PARALLEL arrows crossing the
       site footprint — the sun is ~93M miles away, so at lot scale every point on the property
       receives light along the same vector at the same moment. Never rays converging on the
       building; the building is not the center of the solar system. */
  function drawSun(g, it, doc) {
    const st = SITE_STYLES.sun, cfg = sunSettings(it)
    const r = sunAutoRadius(it, doc)
    const lat = doc.latitude != null ? doc.latitude : 33.54
    const vectors = cfg.mode !== 'paths' ? sunActiveVectors(it, doc) : []
    g.save()
    g.translate(it.x, it.y)
    g.rotate(doc.north || 0)
    g.strokeStyle = st.ring; g.lineWidth = 0.6; g.globalAlpha = 0.5
    g.beginPath(); g.arc(0, 0, r + 12, 0, Math.PI * 2); g.stroke()
    for (let a = 0; a < 360; a += 15) {
      const t = (a - 90) * Math.PI / 180, major = a % 90 === 0
      g.beginPath()
      g.moveTo(Math.cos(t) * (r + 12), Math.sin(t) * (r + 12))
      g.lineTo(Math.cos(t) * (r + (major ? 4 : 8)), Math.sin(t) * (r + (major ? 4 : 8)))
      g.stroke()
    }
    g.globalAlpha = 1
    g.lineWidth = 0.8
    g.beginPath(); g.moveTo(-6, 0); g.lineTo(6, 0); g.moveTo(0, -6); g.lineTo(0, 6); g.stroke()
    // Seasonal arcs — deliberately LIGHTER than the property line (2.4) and envelope (1.3):
    // contextual range, not analytical geometry. Labels sit at the sunrise end, outside the arc.
    const seasons = [
      { dec: 23.44, rr: r, color: st.june, lbl: 'JUNE 21' },
      { dec: 0, rr: r * 0.78, color: st.equinox, lbl: 'MAR / SEP 21' },
      { dec: -23.44, rr: r * 0.56, color: st.dec, lbl: 'DEC 21' },
    ]
    for (const s of seasons) {
      const az = sunriseAzimuth(lat, s.dec)
      const t0 = az - Math.PI / 2, t1 = (Math.PI * 2 - az) - Math.PI / 2
      g.strokeStyle = s.color; g.lineWidth = 1.1; g.setLineDash([7, 4])
      g.beginPath(); g.arc(0, 0, s.rr, t0, t1); g.stroke(); g.setLineDash([])
      g.fillStyle = s.color
      for (const tt of [t0, t1]) { g.beginPath(); g.arc(Math.cos(tt) * s.rr, Math.sin(tt) * s.rr, 2.2, 0, Math.PI * 2); g.fill() }
      g.font = '600 7.5px "DM Mono",monospace'; g.textAlign = 'left'
      g.fillText(s.lbl, Math.cos(t0) * s.rr + 5, Math.sin(t0) * s.rr - 3)
    }
    // Sun-direction arrows — ONE clean arrow per date/time (the readability fix: the earlier
    // "every time × 3 parallel rays" families buried the drawing in a lattice). Each arrow tails
    // at the sun-position marker on its own SEASONAL arc and runs along the true-azimuth line
    // through the diagram center, ending just past it. Geometrically honest: the line through
    // center at bearing θ is invariant under the graphic radius (the anchor slides along the
    // same line), and its direction IS the light-travel vector for that moment — the classic
    // architectural sun-diagram convention, kept because it's also correct. Compact labels sit
    // OUTSIDE the rim, off the drawing.
    if (vectors.length) {
      const seasonR = { summerSolstice: r, equinox: r * 0.78, winterSolstice: r * 0.56 }
      g.font = '600 7px "DM Mono",monospace'; g.textAlign = 'center'
      for (const v of vectors) {
        const color = v.season.dec > 0 ? st.june : v.season.dec < 0 ? st.dec : st.equinox
        const rr = seasonR[v.season.key] || r
        const t = v.azimuth - Math.PI / 2
        const ux = Math.cos(t), uy = Math.sin(t)
        const tail = { x: ux * rr, y: uy * rr }
        const head = { x: -ux * rr * 0.18, y: -uy * rr * 0.18 }
        g.strokeStyle = color; g.lineWidth = 1; g.globalAlpha = 0.85
        g.beginPath(); g.moveTo(tail.x, tail.y); g.lineTo(head.x, head.y); g.stroke()
        arrowHead(g, tail, head, 6.5, true, color)
        g.globalAlpha = 1
        // Sun-position marker ON the arc — "where in the sky is the sun at this moment"
        g.beginPath(); g.arc(tail.x, tail.y, 2.8, 0, Math.PI * 2)
        g.fillStyle = '#fff'; g.fill(); g.stroke()
        // Label past the rim, clear of the drawing: "JUN 9A" / "EQX 12P" / "DEC 3P"
        const short = (v.season.lbl.startsWith('MAR') ? 'EQX' : v.season.lbl.split(' ')[0]) + ' ' + v.time.lbl.replace(' AM', 'A').replace(' PM', 'P')
        g.fillStyle = color
        g.fillText(short, ux * (rr + 20), uy * (rr + 20) + 2.5)
      }
    }
    g.fillStyle = st.ring; g.textAlign = 'center'
    g.font = '600 9px "DM Mono",monospace'
    g.fillText('N', 0, -(r + 19)); g.fillText('S', 0, r + 26); g.fillText('E', r + 23, 3.5); g.fillText('W', -(r + 23), 3.5)
    g.font = '500 7px "DM Mono",monospace'; g.globalAlpha = 0.65
    g.fillText('AM', r * 0.52, -r * 0.5); g.fillText('PM', -r * 0.52, -r * 0.5)
    g.globalAlpha = 1
    g.restore()

    // ── STUDY rays — the parallel-ray layer, for ONE selected moment at a time. Real sunlight
    // arrives as parallel rays (the sun is effectively at infinite distance), so a family of
    // perfectly parallel arrows crossing the site footprint shows exactly which façades/spaces
    // that moment's light reaches. Drawing this for EVERY time simultaneously was the noise —
    // the designer picks the moment under study in the panel; the arrows above stay the always-on
    // summary. Direction is pure solar azimuth; the footprint only sets placement and length. ──
    const studyV = cfg.focus ? vectors.find(v => v.season.key + '@' + v.time.h === cfg.focus) : null
    if (studyV) {
      const fp = siteFootprint(doc) || { cx: it.x, cy: it.y, w: r, h: r, maxDim: r }
      const color = studyV.season.dec > 0 ? st.june : studyV.season.dec < 0 ? st.dec : st.equinox
      const px = -studyV.dir.y, py = studyV.dir.x   // unit perpendicular — spacing axis
      // Sized to the site's extent ALONG the ray's own direction (bbox projected half-extent) —
      // a noon ray on a wide shallow lot spans the shallow depth, not the full width.
      const halfAlong = 0.5 * (Math.abs(studyV.dir.x) * fp.w + Math.abs(studyV.dir.y) * fp.h) + fp.maxDim * 0.09
      const halfPerp = 0.5 * (Math.abs(px) * fp.w + Math.abs(py) * fp.h)
      g.save()
      g.font = '600 7px "DM Mono",monospace'
      g.strokeStyle = color; g.lineWidth = 1
      for (const o of [-0.55, 0, 0.55]) {
        const ax = fp.cx + px * o * halfPerp, ay = fp.cy + py * o * halfPerp
        const tail = { x: ax - studyV.dir.x * halfAlong, y: ay - studyV.dir.y * halfAlong }
        const head = { x: ax + studyV.dir.x * halfAlong, y: ay + studyV.dir.y * halfAlong }
        g.globalAlpha = 0.7
        g.beginPath(); g.moveTo(tail.x, tail.y); g.lineTo(head.x, head.y); g.stroke()
        arrowHead(g, tail, head, 6, true, color)
        g.globalAlpha = 1
        if (o === 0) {
          g.fillStyle = color; g.textAlign = 'center'
          g.fillText(studyV.season.lbl.replace('MAR/SEP', 'EQX') + ' / ' + studyV.time.lbl, tail.x + px * 9 - studyV.dir.x * 16, tail.y + py * 9 - studyV.dir.y * 16 + 2.5)
        }
      }
      g.restore()
    }
    if (vectors.length) {
      // Key-note clarification under the diagram — the circle and the arrows mean different things
      g.save()
      g.font = '500 6.5px "DM Mono",monospace'; g.fillStyle = st.ring; g.textAlign = 'center'; g.globalAlpha = 0.8
      g.fillText('SUN PATHS INDICATE SEASONAL SOLAR RANGE.', it.x, it.y + r + 48)
      g.fillText('SOLAR VECTORS INDICATE TRUE SUN DIRECTION.', it.x, it.y + r + 57)
      g.globalAlpha = 1
      g.restore()
    }

    g.save()
    g.font = '500 7.5px "DM Mono",monospace'; g.fillStyle = st.ring; g.textAlign = 'center'
    g.fillText('LAT ' + lat.toFixed(2) + '°' + (doc.longitude != null ? '  LNG ' + doc.longitude.toFixed(2) + '°' : ''), it.x, it.y + r + 38)
    g.restore()

    // ── Technical/debug block — every number behind the drawing, for checking the work ──
    if (cfg.mode === 'debug') {
      const rows = [
        'LAT ' + lat.toFixed(4) + '°  LNG ' + (doc.longitude != null ? doc.longitude.toFixed(4) + '°' : '— (times read as solar time)'),
        'RADIUS ' + Math.round(r) + ' (graphic only' + (cfg.radiusMode === 'auto' ? ', auto ×' + cfg.radiusMultiplier : ', manual') + ')',
      ]
      for (const v of sunActiveVectors(it, doc)) {
        rows.push(v.season.lbl + ' ' + v.time.lbl + '  AZ ' + (v.azimuth * 180 / Math.PI).toFixed(1) + '°  ALT ' + (v.altitude * 180 / Math.PI).toFixed(1) + '°')
      }
      for (const s of SUN_SEASONS) {
        const rise = sunriseAzimuth(lat, s.dec) * 180 / Math.PI
        rows.push(s.lbl + '  SUNRISE AZ ' + rise.toFixed(1) + '°  SUNSET AZ ' + (360 - rise).toFixed(1) + '°')
      }
      rows.push('VECTORS USE TRUE SOLAR AZIMUTH; CIRCLE RADIUS IS GRAPHIC ONLY.')
      g.save()
      g.font = '500 7px "DM Mono",monospace'; g.textAlign = 'left'
      const bw = 250, bx = it.x + r + 24, by = it.y - (rows.length * 10) / 2
      g.fillStyle = 'rgba(255,255,255,0.88)'
      g.fillRect(bx - 6, by - 12, bw, rows.length * 10 + 18)
      g.strokeStyle = st.ring; g.lineWidth = 0.5; g.strokeRect(bx - 6, by - 12, bw, rows.length * 10 + 18)
      g.fillStyle = INK
      rows.forEach((t, i) => g.fillText(t, bx, by + i * 10))
      g.restore()
    }
  }
  /* Wind rose — 8 compass petals, strength 0–3 set by the user (sites vary too much for
     station data to be the default; the nearest instrument often misleads). */
  function drawWindrose(g, it, doc) {
    const st = SITE_STYLES.windrose, r = it.r || 55
    const petals = it.petals || [0, 0, 0, 0, 0, 0, 0, 0]
    g.save()
    g.translate(it.x, it.y)
    g.rotate(doc.north || 0)
    g.strokeStyle = st.stroke; g.lineWidth = 0.6; g.globalAlpha = 0.45
    g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.stroke()
    g.globalAlpha = 1
    petals.forEach((s, i) => {
      if (!(s > 0)) return
      const t = i * Math.PI / 4 - Math.PI / 2
      const len = r * (0.3 + 0.7 * Math.min(3, s) / 3)
      const half = Math.PI / 14
      g.beginPath()
      g.moveTo(0, 0)
      g.lineTo(Math.cos(t - half) * len, Math.sin(t - half) * len)
      g.lineTo(Math.cos(t) * len * 1.08, Math.sin(t) * len * 1.08)
      g.lineTo(Math.cos(t + half) * len, Math.sin(t + half) * len)
      g.closePath()
      g.fillStyle = st.fill; g.fill()
      g.strokeStyle = st.stroke; g.lineWidth = st.width; g.stroke()
    })
    g.beginPath(); g.arc(0, 0, 2.4, 0, Math.PI * 2); g.fillStyle = st.stroke; g.fill()
    g.font = '600 8px "DM Mono",monospace'; g.fillStyle = st.stroke; g.textAlign = 'center'
    g.fillText('N', 0, -(r + 6))
    g.restore()
    if (it.text) { const lp = labelAnchor(it, it.x - 24, it.y + r + 18); drawRotatedLabel(g, it, lp.x, lp.y, () => drawInkLabel(g, it.text, lp.x, lp.y, 9)) }
  }
  function drawTree(g, it, doc) {
    const st = SITE_STYLES.tree
    const rr = doc.scalePixPerFoot && it.diaFt ? Math.max(4, (it.diaFt * doc.scalePixPerFoot) / 2) : 18
    g.save()
    g.fillStyle = st.fill
    g.beginPath(); g.arc(it.x, it.y, rr, 0, Math.PI * 2); g.fill()
    g.strokeStyle = st.stroke; g.lineWidth = st.width
    g.stroke()
    g.beginPath()
    g.moveTo(it.x - 5, it.y); g.lineTo(it.x + 5, it.y)
    g.moveTo(it.x, it.y - 5); g.lineTo(it.x, it.y + 5)
    g.stroke()
    if (it.diaFt) { const lp = labelAnchor(it, it.x, it.y + rr + 10); g.font = '500 7.5px "DM Mono",monospace'; g.fillStyle = st.stroke; g.textAlign = 'center'; drawRotatedLabel(g, it, lp.x, lp.y, () => g.fillText(it.diaFt + '′ Ø', lp.x, lp.y)) }
    g.restore()
  }
  /* Small stylized fire hydrant glyph — body + cap + two hose nozzles, side-view silhouette. */
  function drawHydrantGlyph(g, cx, cy, color) {
    g.save()
    g.translate(cx, cy)
    g.fillStyle = color
    g.beginPath(); g.arc(0, 0.5, 4.5, 0, Math.PI * 2); g.fill()
    g.strokeStyle = '#fff'; g.lineWidth = 1; g.stroke()
    g.fillStyle = color
    g.fillRect(-2.2, -8, 4.4, 3.4)
    g.fillRect(-8, -1.5, 3.4, 3)
    g.fillRect(4.6, -1.5, 3.4, 3)
    g.restore()
  }
  function drawUtilpoint(g, it) {
    const st = SITE_STYLES.utilpoint
    const def = utilTypeDef(it.ptype)
    const color = def.color, r = st.r
    g.save()
    if (def.shape === 'hydrant') {
      drawHydrantGlyph(g, it.x, it.y, color)
    } else if (def.shape === 'square') {
      const s = r * 1.8
      g.fillStyle = color; g.fillRect(it.x - s / 2, it.y - s / 2, s, s)
      g.strokeStyle = '#fff'; g.lineWidth = 1.2; g.strokeRect(it.x - s / 2, it.y - s / 2, s, s)
    } else if (def.shape === 'diamond') {
      const dr = r * 1.3
      g.beginPath()
      g.moveTo(it.x, it.y - dr); g.lineTo(it.x + dr, it.y); g.lineTo(it.x, it.y + dr); g.lineTo(it.x - dr, it.y)
      g.closePath()
      g.fillStyle = color; g.fill()
      g.strokeStyle = '#fff'; g.lineWidth = 1.2; g.stroke()
    } else {
      g.beginPath(); g.arc(it.x, it.y, r, 0, Math.PI * 2); g.fillStyle = color; g.fill()
      g.strokeStyle = '#fff'; g.lineWidth = 1.2; g.stroke()
      g.strokeStyle = color; g.lineWidth = 0.7
      g.beginPath(); g.arc(it.x, it.y, r + 3, 0, Math.PI * 2); g.stroke()
    }
    const lbl = def.label + (def.numbered && it.n ? ' #' + it.n : '')
    const lp = labelAnchor(it, it.x, it.y + r + 13)
    g.font = '600 7.5px "DM Mono",monospace'; g.fillStyle = color; g.textAlign = 'center'
    drawRotatedLabel(g, it, lp.x, lp.y, () => g.fillText(lbl.toUpperCase(), lp.x, lp.y))
    g.restore()
  }
  function drawKeytag(g, it) {
    const st = SITE_STYLES.keytag, r = st.r
    g.save()
    g.translate(it.x, it.y)
    g.beginPath(); g.moveTo(0, -r); g.lineTo(r, 0); g.lineTo(0, r); g.lineTo(-r, 0); g.closePath()
    g.fillStyle = '#fff'; g.fill()
    g.strokeStyle = st.stroke; g.lineWidth = 1; g.stroke()
    g.font = '600 9px "DM Mono",monospace'; g.fillStyle = st.stroke; g.textAlign = 'center'
    g.fillText(String(it.n || 1), 0, 3.2)
    g.restore()
  }
  /* Corner elevation marker — a small survey-pin glyph (triangle + tick) labeled with its role
     and elevation (or a "NO ELEV" placeholder so it's obvious the value hasn't been set yet). */
  function drawCorner(g, it) {
    const st = SITE_STYLES.corner
    g.save()
    g.translate(it.x, it.y)
    g.rotate((it.rot || 0) * Math.PI / 180)
    g.beginPath(); g.moveTo(0, -7); g.lineTo(6, 5); g.lineTo(-6, 5); g.closePath()
    g.fillStyle = st.fill; g.fill()
    g.strokeStyle = '#fff'; g.lineWidth = 1; g.stroke()
    g.strokeStyle = st.stroke; g.lineWidth = 0.8
    g.beginPath(); g.moveTo(0, 5); g.lineTo(0, 10); g.stroke()
    g.restore()
    const lbl = (CORNER_LABEL[it.role] || 'Corner') + (it.elevation != null ? ' · EL ' + it.elevation + "′" : ' · no elev')
    const lp = labelAnchor(it, it.x, it.y + 22)
    g.save()
    g.font = '600 7.5px "DM Mono",monospace'; g.fillStyle = st.stroke; g.textAlign = 'center'
    drawRotatedLabel(g, it, lp.x, lp.y, () => g.fillText(lbl.toUpperCase(), lp.x, lp.y))
    g.restore()
  }
  /* Nuisance — jagged red arc facing the source (noise, privacy exposure, headlights). */
  function drawNuisance(g, it) {
    const st = SITE_STYLES.nuisance, r = 26, span = Math.PI * 0.78, teeth = 10
    g.save()
    g.translate(it.x, it.y)
    g.rotate((it.rot || 0) * Math.PI / 180)
    g.strokeStyle = st.stroke; g.lineWidth = st.width
    g.beginPath()
    for (let i = 0; i <= teeth; i++) {
      const t = -Math.PI / 2 - span / 2 + span * (i / teeth)
      const rr = r + (i % 2 ? 6 : -2)
      const x = Math.cos(t) * rr, y = Math.sin(t) * rr
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y)
    }
    g.stroke()
    g.beginPath(); g.arc(0, 0, 2, 0, Math.PI * 2); g.fillStyle = st.stroke; g.fill()
    g.restore()
    if (it.text) { const lp = labelAnchor(it, it.x - 24, it.y + 46); drawRotatedLabel(g, it, lp.x, lp.y, () => drawInkLabel(g, it.text, lp.x, lp.y, 9)) }
  }
  /* View cone — vertex + two dotted bearing rays + arc; heading/spread/reach in the panel. */
  function drawViewcone(g, it) {
    const st = SITE_STYLES.viewcone
    const r = it.r || 120, spread = (it.spread || 60) * Math.PI / 180
    const h = (it.heading || 0) * Math.PI / 180 - Math.PI / 2
    g.save()
    // Very subtle wedge fill so the cone reads at a glance without competing with the rest of
    // the diagram — same pie-slice shape as the two bearing rays + arc, filled underneath them.
    g.beginPath()
    g.moveTo(it.x, it.y)
    g.arc(it.x, it.y, r, h - spread / 2, h + spread / 2)
    g.closePath()
    g.fillStyle = st.fill
    g.fill()
    g.strokeStyle = st.stroke; g.lineWidth = st.width; g.setLineDash(st.dash)
    for (const t of [h - spread / 2, h + spread / 2]) {
      g.beginPath(); g.moveTo(it.x, it.y); g.lineTo(it.x + Math.cos(t) * r, it.y + Math.sin(t) * r); g.stroke()
    }
    g.beginPath(); g.arc(it.x, it.y, r, h - spread / 2, h + spread / 2); g.stroke()
    g.setLineDash([])
    g.beginPath(); g.arc(it.x, it.y, 2.4, 0, Math.PI * 2); g.fillStyle = st.stroke; g.fill()
    g.restore()
    // Label follows the arc's sweep (tangent at the bisector angle) instead of floating near it.
    if (it.text) drawLabelAlongArc(g, it.text, it.x, it.y, r, h)
  }
  /* Back-and-forth wavy line (movement — wind, not a single static bow). Alternates a
     perpendicular offset every half-wave via chained quadratic segments. Returns the last
     segment's control point so the caller can orient an arrowhead along the true end tangent. */
  function drawWavyLine(g, a, b) {
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1
    const ux = -dy / len, uy = dx / len   // unit perpendicular
    const waves = Math.max(2, Math.min(6, Math.round(len / 34)))
    const amp = Math.min(11, len * 0.09)
    const segs = waves * 2
    g.beginPath()
    g.moveTo(a.x, a.y)
    let lastCtrl = { x: a.x, y: a.y }
    for (let i = 1; i <= segs; i++) {
      const midT = (i - 0.5) / segs, sign = i % 2 === 1 ? 1 : -1
      const midX = a.x + dx * midT + ux * amp * sign
      const midY = a.y + dy * midT + uy * amp * sign
      const endX = a.x + dx * (i / segs), endY = a.y + dy * (i / segs)
      g.quadraticCurveTo(midX, midY, endX, endY)
      lastCtrl = { x: midX, y: midY }
    }
    g.stroke()
    return lastCtrl
  }
  /* Text rotated to run along a line (like a road/contour label), always kept upright by
     flipping 180° when the line points leftward. Used for view lines — the description should
     read along the sightline, not float unrelated to it. */
  function drawLabelAlongLine(g, text, a, b, offset = 9) {
    let ang = Math.atan2(b.y - a.y, b.x - a.x)
    if (ang > Math.PI / 2 || ang < -Math.PI / 2) ang += Math.PI
    g.save()
    g.translate((a.x + b.x) / 2, (a.y + b.y) / 2)
    g.rotate(ang)
    g.font = '600 9px "DM Sans",sans-serif'
    try { g.letterSpacing = '1px' } catch { /* older engines */ }
    g.fillStyle = INK
    g.textAlign = 'center'
    g.fillText(String(text || '').toUpperCase(), 0, -offset)
    g.restore()
  }
  /* Text rotated tangent to a circular arc at a given angle — same upright-flip idea as
     drawLabelAlongLine, but for a point ON A CURVE (the view cone's arc) instead of a line
     midpoint, so the label reads as if inscribed along the sweep rather than floating near it. */
  function drawLabelAlongArc(g, text, cx, cy, r, angle, offset = 9) {
    let tangent = angle + Math.PI / 2
    if (tangent > Math.PI / 2 || tangent < -Math.PI / 2) tangent += Math.PI
    g.save()
    g.translate(cx + Math.cos(angle) * (r + offset), cy + Math.sin(angle) * (r + offset))
    g.rotate(tangent)
    g.font = '600 9px "DM Sans",sans-serif'
    try { g.letterSpacing = '1px' } catch { /* older engines */ }
    g.fillStyle = INK
    g.textAlign = 'center'
    g.fillText(String(text || '').toUpperCase(), 0, 0)
    g.restore()
  }
  function drawArrowItem(g, it, doc) {
    const st = SITE_STYLES[it.kind]
    const [a, b] = it.pts
    const len = Math.hypot(b.x - a.x, b.y - a.y)
    if (len < 2) return
    g.save()
    if (it.kind === 'windarrow' && it.variant === 'storm') {
      // Broad translucent slab arrow — prevailing storm direction
      const ang = Math.atan2(b.y - a.y, b.x - a.x), hw = 8, hl = Math.min(24, len * 0.4), aw = 17
      g.translate(a.x, a.y); g.rotate(ang)
      g.beginPath()
      g.moveTo(0, -hw); g.lineTo(len - hl, -hw); g.lineTo(len - hl, -aw)
      g.lineTo(len, 0)
      g.lineTo(len - hl, aw); g.lineTo(len - hl, hw); g.lineTo(0, hw)
      g.closePath()
      g.fillStyle = st.storm.fill; g.fill()
      g.strokeStyle = st.storm.stroke; g.lineWidth = 1; g.stroke()
      g.restore()
    } else if (it.kind === 'windarrow') {
      // Breeze — a back-and-forth wavy line (air movement), not one static bow
      g.strokeStyle = st.stroke; g.lineWidth = st.width; g.setLineDash(st.breeze.dash)
      const lastCtrl = drawWavyLine(g, a, b)
      g.setLineDash([])
      arrowHead(g, lastCtrl, b, 9, true, st.stroke)
      g.restore()
    } else if (it.kind === 'viewarrow' && it.variant === 'ray') {
      g.strokeStyle = st.stroke; g.lineWidth = st.ray.width; g.setLineDash(st.ray.dash)
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke()
      g.setLineDash([])
      arrowHead(g, a, b, 7, false, st.stroke)
      g.restore()
    } else if (it.kind === 'viewarrow') {
      g.strokeStyle = st.stroke; g.lineWidth = st.width
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke()
      arrowHead(g, a, b, 12, true, st.stroke)
      g.restore()
    } else if (it.kind === 'slope') {
      // Slope — thin dashed arrow pointing downhill, fall tick at the high end
      g.strokeStyle = st.stroke; g.lineWidth = st.width; g.setLineDash(st.dash)
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke()
      g.setLineDash([])
      arrowHead(g, a, b, 8, true, st.stroke)
      const px = -(b.y - a.y) / len, py = (b.x - a.x) / len
      g.beginPath(); g.moveTo(a.x + px * 5, a.y + py * 5); g.lineTo(a.x - px * 5, a.y - py * 5); g.stroke()
      g.restore()
    } else if (it.kind === 'dimension') {
      // Architectural dimension line — straight line + perpendicular ticks at both ends, no
      // arrowhead (visually distinct from the directional kits above — this is a measurement,
      // not a vector). No freehand tool exists in this app for arbitrary measuring, so this is
      // the "measure at will" utility: drag anywhere, the length is read live off doc.scalePixPerFoot
      // and re-labeled automatically if the drawing's scale is later recalibrated.
      const px = -(b.y - a.y) / len, py = (b.x - a.x) / len, tick = st.tick
      g.strokeStyle = st.stroke; g.lineWidth = st.width
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke()
      for (const p of [a, b]) { g.beginPath(); g.moveTo(p.x + px * tick, p.y + py * tick); g.lineTo(p.x - px * tick, p.y - py * tick); g.stroke() }
      g.restore()
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
      const lp = labelAnchor(it, mx + px * 10, my + py * 10)
      const txt = doc && doc.scalePixPerFoot ? (Math.round((len / doc.scalePixPerFoot) * 10) / 10) + '′' : 'SET SCALE FIRST'
      drawRotatedLabel(g, it, lp.x, lp.y, () => drawInkLabel(g, txt, lp.x, lp.y, 9.5))
      return
    } else {
      g.restore()
    }
    if (it.text) {
      if (it.kind === 'viewarrow') {
        // View labels run ALONG the sightline rather than floating near the head
        drawLabelAlongLine(g, it.text, a, b)
      } else {
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
        const px2 = -(b.y - a.y) / len, py2 = (b.x - a.x) / len
        const lp = labelAnchor(it, mx + px2 * 12 - 16, my + py2 * 12)
        drawRotatedLabel(g, it, lp.x, lp.y, () => drawInkLabel(g, it.text, lp.x, lp.y, 9.5))
      }
    }
  }
  /* Context arc — teal dashed arc through 3 clicked points (ridge lines, panorama extents). */
  function drawContextArc(g, it) {
    const st = SITE_STYLES.contextarc
    const [A, B, Cp] = it.pts
    const O = circumcenter(A, B, Cp)
    g.save()
    g.strokeStyle = st.stroke; g.lineWidth = st.width; g.setLineDash(st.dash)
    if (!O) {
      g.beginPath(); g.moveTo(A.x, A.y); g.lineTo(Cp.x, Cp.y); g.stroke()
    } else {
      const R = Math.hypot(A.x - O.x, A.y - O.y)
      const norm = t => ((t % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
      const a0 = norm(Math.atan2(A.y - O.y, A.x - O.x))
      const a1 = norm(Math.atan2(Cp.y - O.y, Cp.x - O.x))
      const am = norm(Math.atan2(B.y - O.y, B.x - O.x))
      const cwSpan = norm(a1 - a0)
      const anticlockwise = !(norm(am - a0) <= cwSpan)
      g.beginPath(); g.arc(O.x, O.y, R, a0, a1, anticlockwise); g.stroke()
    }
    g.setLineDash([])
    g.fillStyle = st.stroke
    for (const p of [A, Cp]) { g.beginPath(); g.arc(p.x, p.y, 2.2, 0, Math.PI * 2); g.fill() }
    g.restore()
    if (it.text) { const lp = labelAnchor(it, B.x + 8, B.y - 6); drawRotatedLabel(g, it, lp.x, lp.y, () => drawInkLabel(g, it.text, lp.x, lp.y, 9.5)) }
  }
  function drawSiteItem(g, it, doc) {
    if (it.visible === false) return
    const st = SITE_STYLES[it.kind]
    if (!st) return
    if (it.kind === 'label') {
      if (it.leader) {
        g.save(); g.strokeStyle = INK; g.lineWidth = 0.7
        g.beginPath(); g.moveTo(it.x, it.y - 4); g.lineTo(it.leader.x, it.leader.y); g.stroke()
        g.beginPath(); g.arc(it.leader.x, it.leader.y, 1.6, 0, Math.PI * 2); g.fillStyle = INK; g.fill()
        g.restore()
      }
      // The leader line (if any) stays fixed in place — only the text itself rotates, in place,
      // around its own anchor point.
      if (it.rot) {
        g.save()
        g.translate(it.x, it.y)
        g.rotate(it.rot * Math.PI / 180)
        drawInkLabel(g, it.text, 0, 0)
        g.restore()
      } else {
        drawInkLabel(g, it.text, it.x, it.y)
      }
      return
    }
    if (it.kind === 'sun') { drawSun(g, it, doc); return }
    if (it.kind === 'windrose') { drawWindrose(g, it, doc); return }
    if (it.kind === 'tree') { drawTree(g, it, doc); return }
    if (it.kind === 'utilpoint') { drawUtilpoint(g, it); return }
    if (it.kind === 'keytag') { drawKeytag(g, it); return }
    if (it.kind === 'corner') { drawCorner(g, it); return }
    if (it.kind === 'nuisance') { drawNuisance(g, it); return }
    if (it.kind === 'viewcone') { drawViewcone(g, it); return }
    const pts = it.pts || []
    if (pts.length < 2) return
    if (ARROW_KINDS.includes(it.kind)) { drawArrowItem(g, it, doc); return }
    if (it.kind === 'contextarc') { if (pts.length === 3) drawContextArc(g, it); return }
    const closed = !OPEN_LINE_KINDS.includes(it.kind)
    g.save()
    const pending = it.kind === 'pl' && it.variant === 'pending'
    g.strokeStyle = pending ? st.pending.stroke : st.stroke
    g.lineWidth = st.width
    if (pending) g.setLineDash(st.pending.dash)
    else if (st.dash) g.setLineDash(st.dash)
    if (st.fill && closed && pts.length > 2) { itemPath(g, it, true); g.fillStyle = st.fill; g.fill() }
    if (it.kind === 'importantzone' && pts.length > 2) {
      g.save(); itemPath(g, it, true); g.clip()
      const bpts = hasCurves(it) ? flattenItemPts(it, true) : pts
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
      for (const p of bpts) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y) }
      g.fillStyle = st.dot
      for (let y = minY; y <= maxY; y += 7) for (let x = minX + (Math.round(y / 7) % 2) * 3.5; x <= maxX; x += 7) { g.beginPath(); g.arc(x, y, 0.9, 0, Math.PI * 2); g.fill() }
      g.restore()
    }
    itemPath(g, it, closed && pts.length > 2)
    g.stroke()
    g.setLineDash([])
    if (it.kind === 'hazardedge') {
      // Perpendicular hatch ticks on one side — bluff / exposure edge (follows curves)
      const hpts = hasCurves(it) ? flattenItemPts(it, false) : pts
      g.lineWidth = 1
      for (let ei = 0; ei < hpts.length - 1; ei++) {
        const a = hpts[ei], b = hpts[ei + 1]
        const segLen = Math.hypot(b.x - a.x, b.y - a.y)
        if (segLen < 4) continue
        const ux = (b.x - a.x) / segLen, uy = (b.y - a.y) / segLen
        for (let d = 6; d < segLen; d += 9) {
          const x = a.x + ux * d, y = a.y + uy * d
          g.beginPath(); g.moveTo(x, y); g.lineTo(x - uy * 6, y + ux * 6); g.stroke()
        }
      }
    }
    if (it.kind === 'approach') {
      const p = pts[pts.length - 1]
      g.beginPath(); g.arc(p.x, p.y, 3.4, 0, Math.PI * 2); g.fillStyle = st.stroke; g.fill()
    }
    if (it.kind === 'utilline') {
      g.fillStyle = st.stroke
      for (const p of [pts[0], pts[pts.length - 1]]) { g.beginPath(); g.arc(p.x, p.y, 2.6, 0, Math.PI * 2); g.fill() }
    }
    if (it.kind === 'pl') {
      g.fillStyle = '#fff'
      for (const p of pts) { g.beginPath(); g.arc(p.x, p.y, st.vertexR, 0, Math.PI * 2); g.fill(); g.stroke() }
      // Auto setbacks + derived buildable envelope (needs scale + per-edge distances in ft).
      // Runs on the FLATTENED outline: each sample carries its original edge index, so a
      // curved edge offsets by its own setback and the envelope follows the curve. The envelope
      // itself is a half-plane clip (clipEnvelope), not an offset-outline — see the comment on
      // clipEnvelope for why (it's what fixed the self-intersecting "bow-tie" artifacts).
      if (doc.scalePixPerFoot && hasAnySetback(it) && pts.length > 2) {
        const flat = flattenItemPts(it, true)
        const dists = flat.map(f => edgeSetbackFt(it, f.edge) * doc.scalePixPerFoot)
        const env = computeEnvelope(flat, dists)
        if (env) {
          const est = SITE_STYLES.envelope, sst = SITE_STYLES.setback
          g.save()
          polyPath(g, env, true)
          g.fillStyle = est.fill; g.fill()
          // The envelope boundary IS the setback line — style it with the red dashed setback
          // convention directly, rather than drawing a second, separately-computed reference
          // line per edge (that old approach never joined cleanly at corners where adjacent
          // edges had different setback distances — a straight per-edge offset segment simply
          // doesn't connect to its neighbor's, so it read as broken/crossed clutter).
          g.strokeStyle = sst.stroke; g.lineWidth = sst.width; g.setLineDash(sst.dash); g.stroke(); g.setLineDash([])
          g.restore()
          // Optional numeric setback distances — off by default (a lot filed with several
          // same-role edges would repeat the same number down the whole run); toggled per-PL
          // in the properties panel. Biased toward the polygon's centroid (not the precise inward
          // normal clipEnvelope/miterOffset compute) so a label always lands inside the parcel
          // regardless of winding — good enough for a caption, no need for exact placement.
          if (it.showSetbackLabels && it.edgeRoles) {
            let ccx = 0, ccy = 0
            for (const p of pts) { ccx += p.x; ccy += p.y }
            ccx /= pts.length; ccy /= pts.length
            g.save()
            g.font = '600 8px "DM Mono",monospace'; g.fillStyle = sst.stroke; g.textAlign = 'center'
            for (let i = 0; i < pts.length; i++) {
              if (!it.edgeRoles[i]) continue
              const ft = edgeSetbackFt(it, i)
              if (!(ft > 0)) continue
              const a = pts[i], b = pts[(i + 1) % pts.length]
              const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
              const dx = ccx - mx, dy = ccy - my, dl = Math.hypot(dx, dy) || 1
              g.fillText((Math.round(ft * 10) / 10) + '′', mx + (dx / dl) * 14, my + (dy / dl) * 14)
            }
            g.restore()
          }
        } else {
          drawInkLabel(g, 'Setbacks exceed the parcel — no buildable area', pts[0].x, pts[0].y - 10, 8.5)
        }
      }
    }
    if (it.text && pts.length >= 2) {
      if (closed && pts.length > 2) {
        let cx = 0, cy = 0
        for (const p of pts) { cx += p.x; cy += p.y }
        const lp = labelAnchor(it, cx / pts.length, cy / pts.length)
        drawRotatedLabel(g, it, lp.x, lp.y, () => drawInkLabel(g, it.text, lp.x, lp.y))
      } else {
        const mi = Math.floor((pts.length - 1) / 2)
        const a = pts[mi], b = pts[Math.min(mi + 1, pts.length - 1)]
        const lp = labelAnchor(it, (a.x + b.x) / 2 + 6, (a.y + b.y) / 2 - 6)
        drawRotatedLabel(g, it, lp.x, lp.y, () => drawInkLabel(g, it.text, lp.x, lp.y, 9.5))
      }
    }
    g.restore()
  }
  function itemsBBox(doc) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, any = false
    for (const bg of doc.backgrounds) { minX = Math.min(minX, bg.x); minY = Math.min(minY, bg.y); maxX = Math.max(maxX, bg.x + bg.w); maxY = Math.max(maxY, bg.y + bg.h); any = true }
    for (const it of doc.items) {
      if (it.pts) {
        const bp = hasCurves(it) ? flattenItemPts(it, !isOpenKind(it.kind)) : it.pts
        for (const p of bp) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); any = true }
      } else if (it.x != null) {
        const R = it.kind === 'viewcone' ? (it.r || 120) : (POINT_KINDS.includes(it.kind) ? pointExtent(it, doc) + 26 : 8)
        minX = Math.min(minX, it.x - R); minY = Math.min(minY, it.y - R)
        maxX = Math.max(maxX, it.x + R); maxY = Math.max(maxY, it.y + R); any = true
      }
    }
    return any ? { minX, minY, maxX, maxY } : null
  }
  /* Legend + scale bar + north arrow — the standardization stamps (world space). Legend and
     north arrow are independently placeable objects; defaults anchor to the canvas sheet. */
  function drawStamps(g, doc) {
    const c = doc.canvas
    if (!c) return
    const kinds = KIT_ORDER.filter(k => doc.items.some(it => it.kind === k) || (k === 'envelope' && doc.items.some(it => it.kind === 'pl' && hasAnySetback(it))))
    if (doc.legend.visible && kinds.length) {
      const lx = doc.legend.x != null ? doc.legend.x : c.x + 14
      const ly = doc.legend.y != null ? doc.legend.y : c.y + 14
      const rowH = 17, w = 158, h = kinds.length * rowH + 30
      g.save()
      g.fillStyle = 'rgba(255,255,255,0.92)'; g.strokeStyle = INK; g.lineWidth = 0.8
      g.fillRect(lx, ly, w, h); g.strokeRect(lx, ly, w, h)
      drawInkLabel(g, 'Site Analysis Key', lx + 10, ly + 17, 10)
      kinds.forEach((k, i) => {
        const st = SITE_STYLES[k], y = ly + 30 + i * rowH
        g.save()
        g.strokeStyle = st.stroke || st.ring || (k === 'utilpoint' && utilTypeDef('other').color) || INK
        g.lineWidth = Math.min(st.width || 1.2, 2)
        if (st.dash) g.setLineDash(st.dash.map(d => d * 0.7))
        if (st.fill) { g.fillStyle = st.fill; g.fillRect(lx + 10, y - 5, 22, 9) }
        g.beginPath(); g.moveTo(lx + 10, y); g.lineTo(lx + 32, y); g.stroke()
        g.restore()
        drawInkLabel(g, st.label, lx + 40, y + 3.5, 8.5)
      })
      g.restore()
      doc._legendBox = { x: lx, y: ly, w, h }
    } else { doc._legendBox = null }
    if (doc.scalePixPerFoot) {
      const ftOptions = [10, 20, 40, 50, 100, 200]
      const target = c.w / 5
      const ft = ftOptions.reduce((a, b) => Math.abs(b * doc.scalePixPerFoot - target) < Math.abs(a * doc.scalePixPerFoot - target) ? b : a)
      const px = ft * doc.scalePixPerFoot
      const sx0 = c.x + 14, sy0 = c.y + c.h - 16
      g.save()
      g.strokeStyle = INK; g.lineWidth = 1.4
      g.beginPath(); g.moveTo(sx0, sy0); g.lineTo(sx0 + px, sy0); g.stroke()
      g.beginPath(); g.moveTo(sx0, sy0 - 4); g.lineTo(sx0, sy0 + 4); g.stroke()
      g.beginPath(); g.moveTo(sx0 + px, sy0 - 4); g.lineTo(sx0 + px, sy0 + 4); g.stroke()
      g.restore()
      drawInkLabel(g, ft + "\u2032", sx0 + px + 7, sy0 + 4, 10)
      // Printed scale \u2014 the sheet is 11\u00d717, so canvas width maps to 17 inches
      const ftPerInch = c.w / 17 / doc.scalePixPerFoot
      drawInkLabel(g, '1\u2033 = ' + (Math.round(ftPerInch * 10) / 10) + '\u2032 at 11\u00d717', sx0, sy0 - 10, 7.5)
    }
    // North arrow \u2014 its own draggable object (default: sheet top-right)
    const na = doc.northArrow || {}
    const nx = na.x != null ? na.x : c.x + c.w - 30
    const ny = na.y != null ? na.y : c.y + 34
    const r = 15
    g.save()
    g.translate(nx, ny); g.rotate(doc.north || 0)
    g.strokeStyle = INK; g.lineWidth = 1.2
    g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.stroke()
    g.beginPath(); g.moveTo(0, r - 4); g.lineTo(0, -r + 3); g.stroke()
    g.beginPath(); g.moveTo(0, -r + 3); g.lineTo(-3.4, -r + 9); g.lineTo(3.4, -r + 9); g.closePath(); g.fillStyle = INK; g.fill()
    g.restore()
    drawInkLabel(g, 'N', nx - 3.5, ny - r - 5, 10)
    doc._northBox = { x: nx - r - 5, y: ny - r - 12, w: (r + 5) * 2, h: (r + 5) * 2 + 7, cx: nx, cy: ny }

    // Key Notes — numbered descriptions for placed key tags (independent draggable stamp). A
    // bare number on the map means nothing without this — the tag IS the pointer, the note is here.
    const tags = doc.items.filter(it => it.kind === 'keytag' && (it.text || '').trim()).sort((a, b) => (a.n || 0) - (b.n || 0))
    if (doc.keyNotes && doc.keyNotes.visible && tags.length) {
      const kw = 190, padL = 24, lineH = 10.5, rowGap = 5
      g.save()
      g.font = '500 8.5px "DM Sans",sans-serif'
      const wrapped = tags.map(t => wrapLines(g, (t.text || '').toUpperCase(), kw - padL - 12))
      const rowHeights = wrapped.map(lines => lines.length * lineH + rowGap)
      const bodyH = rowHeights.reduce((a, b) => a + b, 0)
      const kh = bodyH + 30
      const kx = doc.keyNotes.x != null ? doc.keyNotes.x : c.x + c.w - kw - 14
      const ky = doc.keyNotes.y != null ? doc.keyNotes.y : c.y + c.h - kh - 34
      g.fillStyle = 'rgba(255,255,255,0.92)'; g.strokeStyle = INK; g.lineWidth = 0.8
      g.fillRect(kx, ky, kw, kh); g.strokeRect(kx, ky, kw, kh)
      drawInkLabel(g, 'Key Notes', kx + 10, ky + 17, 10)
      let y = ky + 30
      tags.forEach((t, i) => {
        g.font = '700 8.5px "DM Mono",monospace'; g.fillStyle = INK; g.textAlign = 'left'
        g.fillText(String(t.n || i + 1), kx + 10, y)
        g.font = '500 8.5px "DM Sans",sans-serif'
        wrapped[i].forEach((ln, li) => g.fillText(ln, kx + padL, y + li * lineH))
        y += rowHeights[i]
      })
      g.restore()
      doc._keyNotesBox = { x: kx, y: ky, w: kw, h: kh }
    } else { doc._keyNotesBox = null }
  }
  function drawItems(g, doc) {
    // Reset first — labelAnchor() only re-populates entries for items that actually draw a label
    // THIS frame, so a cleared/never-set text (or a deleted item) can't leave a stale grip behind.
    labelAnchorsRef.current.clear()
    for (const it of doc.items) drawSiteItem(g, it, doc)
    drawStamps(g, doc)
  }
  function renderAll() {
    const comp = compRef.current, doc = docRef.current
    if (!comp || !doc) return
    const g = comp.getContext('2d')
    const z = vpRef.current.z
    g.setTransform(1, 0, 0, 1, 0, 0)
    // Pasteboard ground + the 11×17 sheet (Illustrator artboard model — export crops to it)
    g.fillStyle = '#dde3e6'
    g.fillRect(0, 0, comp.width, comp.height)
    const c = doc.canvas
    let cp = null
    if (c) {
      cp = worldToScreen(c.x, c.y)
      g.save()
      g.shadowColor = 'rgba(7,39,45,0.20)'; g.shadowBlur = 14
      g.fillStyle = '#ffffff'
      g.fillRect(cp.x, cp.y, c.w * z, c.h * z)
      g.restore()
    }
    // Crop preview — clip everything (backgrounds + wash + items) to the sheet, matching what
    // will actually export. Off by default while composing (it's useful to see a background
    // extend past the sheet edge while you're still positioning it).
    const clipped = cropPreviewRef.current && cp
    if (clipped) { g.save(); g.beginPath(); g.rect(cp.x, cp.y, c.w * z, c.h * z); g.clip() }
    drawBackgrounds(g, doc, worldToScreen)
    if (cp) drawWash(g, doc, cp.x, cp.y, c.w * z, c.h * z)
    ensurePdfCaches()
    g.save()
    g.translate(vpRef.current.x, vpRef.current.y)
    g.scale(z, z)
    drawItems(g, doc)
    g.restore()
    if (clipped) g.restore()
    if (cp) {
      g.strokeStyle = 'rgba(7,39,45,0.4)'; g.lineWidth = 1
      g.strokeRect(cp.x + 0.5, cp.y + 0.5, c.w * z, c.h * z)
    }
    drawOverlay()
  }
  function drawOverlay() {
    const ui = uiRef.current, doc = docRef.current
    if (!ui || !doc) return
    const g = ui.getContext('2d')
    g.setTransform(1, 0, 0, 1, 0, 0)
    g.clearRect(0, 0, ui.width, ui.height)
    // Selected background — dashed bbox + corner handles
    const bg = doc.backgrounds.find(b => b.id === selBgIdRef.current)
    if (bg) {
      const p = worldToScreen(bg.x, bg.y), z = vpRef.current.z
      g.save()
      g.strokeStyle = (bg.locked || bg.sat) ? 'rgba(7,39,45,0.35)' : INK; g.lineWidth = 1; g.setLineDash([5, 4])
      g.strokeRect(p.x, p.y, bg.w * z, bg.h * z)
      g.setLineDash([])
      if (!bg.locked) {
        for (const [hx, hy] of [[0, 0], [1, 0], [1, 1], [0, 1]]) {
          g.fillStyle = '#fff'
          g.fillRect(p.x + hx * bg.w * z - 4, p.y + hy * bg.h * z - 4, 8, 8)
          g.strokeRect(p.x + hx * bg.w * z - 4, p.y + hy * bg.h * z - 4, 8, 8)
        }
      }
      g.restore()
    }
    // Draft polygon in progress
    const draft = draftRef.current
    if (draft && draft.pts.length) {
      g.save()
      g.strokeStyle = '#ec4899'; g.lineWidth = 1.5; g.setLineDash([5, 4])
      g.beginPath()
      const p0 = worldToScreen(draft.pts[0].x, draft.pts[0].y)
      g.moveTo(p0.x, p0.y)
      for (let i = 1; i < draft.pts.length; i++) { const pp = worldToScreen(draft.pts[i].x, draft.pts[i].y); g.lineTo(pp.x, pp.y) }
      g.stroke(); g.setLineDash([])
      for (const dp of draft.pts) { const pp = worldToScreen(dp.x, dp.y); g.beginPath(); g.arc(pp.x, pp.y, 4, 0, Math.PI * 2); g.fillStyle = '#fff'; g.fill(); g.strokeStyle = '#ec4899'; g.stroke() }
      if (POLY_KINDS.includes(draft.kind) && draft.pts.length > 2) { g.beginPath(); g.arc(p0.x, p0.y, 8, 0, Math.PI * 2); g.strokeStyle = '#ec4899'; g.stroke() }
      if (OPEN_LINE_KINDS.includes(draft.kind) && draft.pts.length >= 2) {
        const pl = worldToScreen(draft.pts[draft.pts.length - 1].x, draft.pts[draft.pts.length - 1].y)
        g.beginPath(); g.arc(pl.x, pl.y, 8, 0, Math.PI * 2); g.strokeStyle = '#ec4899'; g.stroke()
      }
      g.restore()
    }
    // Selected item — halo + vertex handles. Locked items get a muted gray outline with no
    // handles at all (nothing to grab), so "selected but locked" reads differently at a glance
    // from "selected and editable."
    const selIt = docRef.current && docRef.current.items.find(i2 => i2.id === selItemIdRef.current)
    const selColor = selIt && selIt.locked ? '#8a949a' : '#ec4899'
    if (selIt && selIt.pts) {
      const v = vpRef.current
      g.save()
      g.strokeStyle = selColor; g.setLineDash([4, 3])
      // Follow the real geometry (lines + beziers) via the world transform
      g.save()
      g.translate(v.x, v.y); g.scale(v.z, v.z)
      g.lineWidth = 1 / v.z
      g.setLineDash([4 / v.z, 3 / v.z])
      itemPath(g, selIt, !isOpenKind(selIt.kind) && selIt.pts.length > 2)
      g.stroke()
      g.restore()
      g.setLineDash([])
      if (!selIt.locked) {
        // Control handles (curved anchors): thin line + round knob per handle
        if (isCurvableKind(selIt.kind)) {
          for (const sp of selIt.pts) {
            for (const hk of ['hi', 'ho']) {
              if (!sp[hk]) continue
              const aq = worldToScreen(sp.x, sp.y)
              const hq = worldToScreen(sp.x + sp[hk].x, sp.y + sp[hk].y)
              g.strokeStyle = selColor; g.lineWidth = 0.8
              g.beginPath(); g.moveTo(aq.x, aq.y); g.lineTo(hq.x, hq.y); g.stroke()
              g.beginPath(); g.arc(hq.x, hq.y, 3.5, 0, Math.PI * 2)
              g.fillStyle = '#fff'; g.fill(); g.stroke()
            }
          }
        }
        g.strokeStyle = selColor; g.lineWidth = 1
        for (const sp of selIt.pts) { const qq = worldToScreen(sp.x, sp.y); g.fillStyle = '#fff'; g.fillRect(qq.x - 4, qq.y - 4, 8, 8); g.strokeRect(qq.x - 4, qq.y - 4, 8, 8) }
      }
      // Role tags at edge midpoints (F / S1 / S2 / R) — so which edge is which is verifiable
      // at a glance instead of hunting through the properties panel's dropdown list.
      if (selIt.kind === 'pl' && selIt.edgeRoles) {
        const ROLE_TAG = { front: 'F', side1: 'S1', side2: 'S2', rear: 'R' }
        g.font = '700 9px "DM Mono",monospace'; g.textAlign = 'center'
        for (let i = 0; i < selIt.pts.length; i++) {
          const role = selIt.edgeRoles[i]
          if (!role) continue
          const a = selIt.pts[i], b = selIt.pts[(i + 1) % selIt.pts.length]
          const mq = worldToScreen((a.x + b.x) / 2, (a.y + b.y) / 2)
          g.fillStyle = '#fff'
          g.fillRect(mq.x - 9, mq.y - 8, 18, 12)
          g.fillStyle = selColor
          g.fillText(ROLE_TAG[role] || '', mq.x, mq.y + 1.5)
        }
      }
      g.restore()
    } else if (selIt && POINT_KINDS.includes(selIt.kind)) {
      const c = worldToScreen(selIt.x, selIt.y)
      const R = pointExtent(selIt, docRef.current) * vpRef.current.z + 6
      g.save(); g.strokeStyle = selColor; g.lineWidth = 1; g.setLineDash([4, 3])
      g.beginPath(); g.arc(c.x, c.y, R, 0, Math.PI * 2); g.stroke(); g.setLineDash([]); g.restore()
    } else if (selIt && selIt.kind === 'label') {
      const qq = worldToScreen(selIt.x, selIt.y)
      g.save(); g.strokeStyle = selColor; g.setLineDash([4, 3]); g.strokeRect(qq.x - 6, qq.y - 16, 130, 22); g.setLineDash([]); g.restore()
    }
    // Label grip — a small diamond handle at the item's CURRENT label position, independent of
    // the item's own vertex/point handles above. 'label' items skip this (the whole item IS the
    // label; dragging it already moves it). Only drawn if the item actually rendered a label this
    // frame (labelAnchorsRef is repopulated every render by labelAnchor(), so an item with no
    // text this frame — the label was cleared, or the item type never shows one — has no entry).
    if (selIt && !selIt.locked && selIt.kind !== 'label') {
      const la = labelAnchorsRef.current.get(selIt.id)
      if (la) {
        const p = worldToScreen(la.x, la.y)
        g.save()
        if (la.x !== la.baseX || la.y !== la.baseY) {
          const base = worldToScreen(la.baseX, la.baseY)
          g.strokeStyle = selColor; g.lineWidth = 0.8; g.setLineDash([3, 2])
          g.beginPath(); g.moveTo(base.x, base.y); g.lineTo(p.x, p.y); g.stroke()
          g.setLineDash([])
        }
        g.beginPath()
        g.moveTo(p.x, p.y - 6); g.lineTo(p.x + 6, p.y); g.lineTo(p.x, p.y + 6); g.lineTo(p.x - 6, p.y)
        g.closePath()
        g.fillStyle = '#fff'; g.fill(); g.strokeStyle = selColor; g.lineWidth = 1; g.stroke()
        // Rotate handle — a short arm + circle at the label's current rotation (it.labelRot).
        // Dragging it spins the label in place around this same anchor; the diamond above only
        // ever moves the anchor, so the two gestures never fight each other.
        const rw = labelRotateHandleWorld(selIt, la)
        const rp = worldToScreen(rw.x, rw.y)
        g.strokeStyle = selColor; g.lineWidth = 1
        g.beginPath(); g.moveTo(p.x, p.y); g.lineTo(rp.x, rp.y); g.stroke()
        g.beginPath(); g.arc(rp.x, rp.y, 4.5, 0, Math.PI * 2)
        g.fillStyle = '#fff'; g.fill(); g.strokeStyle = selColor; g.lineWidth = 1; g.stroke()
        g.restore()
      }
    }
    // Calibration points
    for (const pt of calPtsRef.current) {
      const p = worldToScreen(pt.x, pt.y)
      g.save()
      g.beginPath(); g.arc(p.x, p.y, 5, 0, Math.PI * 2)
      g.fillStyle = '#ec4899'; g.fill(); g.strokeStyle = '#fff'; g.lineWidth = 1.5; g.stroke()
      g.restore()
    }
    if (calPtsRef.current.length === 2) {
      const a = worldToScreen(calPtsRef.current[0].x, calPtsRef.current[0].y)
      const b = worldToScreen(calPtsRef.current[1].x, calPtsRef.current[1].y)
      g.save()
      g.strokeStyle = '#ec4899'; g.setLineDash([4, 3]); g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke()
      g.restore()
    }
  }
  const selBgIdRef = useRef(null)
  useEffect(() => { selBgIdRef.current = selBgId; scheduleRender() }, [selBgId]) // eslint-disable-line react-hooks/exhaustive-deps
  const cropPreviewRef = useRef(false)
  useEffect(() => { cropPreviewRef.current = cropPreview; scheduleRender() }, [cropPreview]) // eslint-disable-line react-hooks/exhaustive-deps

  /* PDF backgrounds re-render at the current zoom (crisp), sketch-tool pattern simplified. */
  function ensurePdfCaches() {
    const doc = docRef.current
    if (!doc) return
    const z = Math.min(Math.max(vpRef.current.z, 0.5), Math.sqrt((3072 * 3072) / Math.max(1, 1)))
    for (const bg of doc.backgrounds) {
      if (!bg.pdfPage || bg._pdfRendering) continue
      const cap = Math.sqrt((3072 * 3072) / Math.max(1, bg.w * bg.h))
      const s = Math.min(Math.max(vpRef.current.z, 0.5), cap)
      if (bg._pdfCache && Math.abs(bg._pdfCache.s - s) < 0.001) continue
      bg._pdfRendering = true
      try {
        const vp1 = bg.pdfPage.getViewport({ scale: 1 })
        const scale = s * (bg.w / vp1.width)
        const vp = bg.pdfPage.getViewport({ scale })
        const canvas = document.createElement('canvas')
        canvas.width = Math.max(1, Math.round(vp.width)); canvas.height = Math.max(1, Math.round(vp.height))
        bg.pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise.then(() => {
          bg._pdfCache = { canvas, s }
          bg._pdfRendering = false
          scheduleRender()
        }).catch(() => { bg._pdfRendering = false })
      } catch { bg._pdfRendering = false }
    }
    void z
  }

  /* ── Backgrounds ───────────────────────────────────────────────────────── */
  function addBackgroundFromImage(img, name, extras = {}) {
    const doc = docRef.current
    const vw = sw() / vpRef.current.z, vh = sh() / vpRef.current.z
    const ar = img.naturalWidth / img.naturalHeight
    let w = vw * 0.82, h = w / ar
    if (h > vh * 0.82) { h = vh * 0.82; w = h * ar }
    const c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight
    c.getContext('2d').drawImage(img, 0, 0)
    const world0 = screenToWorld(sw() / 2 - (w * vpRef.current.z) / 2, sh() / 2 - (h * vpRef.current.z) / 2)
    doc.backgrounds.push({
      id: newId(), name: name || 'Background', x: world0.x, y: world0.y, w, h, rotation: 0, opacity: 1,
      canvas: c, ...extras,
    })
    markDirty(); setSelBgId(doc.backgrounds[doc.backgrounds.length - 1].id); bump(); scheduleRender()
  }
  /* Live satellite underlay — fetched from Mapbox on demand (api/vicinity-map.js `style:
     'satellite'`), not uploaded. Excluded from serialization (see serializeDoc) so toggling it
     on/off never touches the saved file; removing it from the Backgrounds panel IS "off," this
     is "on."
     SIZED TO THE CURRENT VIEW, not a fixed parcel-level crop — zoom the canvas OUT first
     (revealing blank pasteboard around the site) and refresh to pull in a wide contextual image
     for drawing view vectors toward distant landmarks (ocean, hills, canyons, built structures
     miles away); zoom back in and refresh again for tight site-level detail. This is what makes
     it behave like an unbounded map rather than a single dropped-in image.
     The very first fetch has no scale or geo-anchor yet, so it falls back to a fixed
     parcel-level zoom and BOOTSTRAPS both `doc.scalePixPerFoot` and `doc.geoAnchor` from the
     result (anchored at wherever the viewport happened to be centered). Every fetch after that
     projects through that same anchor (worldToLatLng/latLngToWorld), so repeated refreshes stay
     geographically consistent with each other and with anything already drawn. */
  async function refreshSatellite() {
    if (!projectId) { showToast('No project on this milestone'); return }
    setSatBusy(true)
    const doc = docRef.current
    try {
      const token = await window.__clerkGetToken()
      let lat, lng, zoom
      const bootstrapping = !doc.geoAnchor || !doc.scalePixPerFoot
      if (bootstrapping) {
        const geoRes = await fetch('/api/vicinity-map?projectId=' + encodeURIComponent(projectId) + '&geocode=1', { headers: { Authorization: 'Bearer ' + token } })
        const geo = await geoRes.json()
        if (!geo.lat || !geo.lng) { showToast(geo.address ? 'Could not geocode the project address' : 'No address on the project'); return }
        lat = geo.lat; lng = geo.lng; zoom = 20
      } else {
        const centerWorld = screenToWorld(sw() / 2, sh() / 2)
        const centerGeo = worldToLatLng(centerWorld.x, centerWorld.y, doc)
        lat = centerGeo.lat; lng = centerGeo.lng
        // Zoom chosen so the fetched image covers the current viewport's longer visible
        // dimension, in feet — the actual "infinite canvas" behavior. SAT_PX is the literal,
        // non-retina pixel width/height requested below (no @2x — see api/vicinity-map.js for
        // why: that "double the actual pixels, same geographic extent" assumption was the exact
        // source of a confirmed, precise 2x oversizing bug, independent of doc.scalePixPerFoot).
        const viewWft = (sw() / vpRef.current.z) / doc.scalePixPerFoot
        const viewHft = (sh() / vpRef.current.z) / doc.scalePixPerFoot
        const feet = Math.max(viewWft, viewHft) * 1.15
        const metersPerNativePx = (feet * 0.3048) / SAT_PX
        zoom = Math.max(3, Math.min(20, Math.log2(156543.03392 * Math.cos(lat * Math.PI / 180) / metersPerNativePx)))
      }
      const res = await fetch('/api/vicinity-map', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ lat, lng, zoom, style: 'satellite', width: SAT_PX, height: SAT_PX }),
      })
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Satellite fetch failed') }
      const blob = await res.blob()
      const img = await decodeImg(URL.createObjectURL(blob))
      // Plain Web Mercator ground resolution at this zoom/latitude — no retina halving, since the
      // fetch above is a plain (non-@2x) image: img.naturalWidth/Height should equal SAT_PX
      // exactly, one native pixel = one requested pixel, nothing to reason about beyond that.
      const metersPerNativePx = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom)
      // doc.satCorrection is an empirical multiplier established by manually resizing the
      // satellite to match the already-verified-correct drawing (see onPointerUp's 'resize'
      // handling) — the formula above is a best-effort guess that has proven unreliable, so any
      // correction the user has dialed in is applied to every future fetch's own guess as a
      // starting point, rather than needing to be redone from scratch each time.
      const feetPerNativePx = metersPerNativePx * 3.28084 * (doc.satCorrection || 1)
      const c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight
      c.getContext('2d').drawImage(img, 0, 0)
      saveUndo()
      let centerWorld
      if (bootstrapping) {
        centerWorld = screenToWorld(sw() / 2, sh() / 2)
        if (!doc.geoAnchor) doc.geoAnchor = { lat, lng, worldX: centerWorld.x, worldY: centerWorld.y }
        if (!doc.scalePixPerFoot) doc.scalePixPerFoot = 1 / feetPerNativePx
      } else {
        centerWorld = latLngToWorld(lat, lng, doc)
      }
      const worldPerNativePx = feetPerNativePx * doc.scalePixPerFoot
      const w = img.naturalWidth * worldPerNativePx, h = img.naturalHeight * worldPerNativePx
      let bg = doc.backgrounds.find(b => b.sat)
      // feetPerNativePx (the image's OWN known real-world resolution, from Mapbox) is kept on the
      // background so its size can be re-derived any time doc.scalePixPerFoot changes later (e.g.
      // the user calibrates the drawing's actual scale AFTER satellite was already fetched at the
      // bootstrap's own guessed scale) — see resyncSatelliteScale, called from finishCalibration.
      // satZoom/satLat/satNativeW kept purely for the diagnostic readout in the Backgrounds panel
      // (Round 33/34 scale-mismatch investigation) — not used by any placement math.
      if (bg) Object.assign(bg, { x: centerWorld.x - w / 2, y: centerWorld.y - h / 2, w, h, rotation: doc.north || 0, canvas: c, feetPerNativePx, satZoom: zoom, satLat: lat, satNativeW: img.naturalWidth })
      else {
        bg = { id: newId(), name: 'Satellite View', x: centerWorld.x - w / 2, y: centerWorld.y - h / 2, w, h, rotation: doc.north || 0, opacity: 1, canvas: c, sat: true, feetPerNativePx, satZoom: zoom, satLat: lat, satNativeW: img.naturalWidth }
        doc.backgrounds.push(bg)
      }
      bump(); scheduleRender()
      showToast(bootstrapping ? 'Satellite view added — remove it from Backgrounds to toggle off' : 'Satellite view refreshed for the current view')
    } catch (err) {
      showToast('Satellite fetch failed — ' + (err.message || 'unknown'))
    } finally { setSatBusy(false) }
  }
  async function handleFile(file) {
    if (!file) return
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
      // Multi-page PDFs go through the shared thumbnail page picker (same one as QC, sheet
      // revisions, and takeoffs) — the picked page is extracted client-side by pdf-lib, so
      // `resolved` is always a single-page PDF whose bytes embed straight into the save
      // (vector-crisp forever). Unpicked pages never leave the browser.
      const resolved = await resolveUpload(file)
      if (!resolved) return   // user cancelled the picker
      const pdfjs = await loadPdfjs()
      if (!pdfjs) { showToast('PDF engine failed to load'); return }
      const buf = await resolved.arrayBuffer()
      const pdfBytes = new Uint8Array(buf.slice(0))
      const pdf = await pdfjs.getDocument({ data: new Uint8Array(buf) }).promise
      const page = await pdf.getPage(1)
      const vp = page.getViewport({ scale: 200 / 72 })
      const pc = document.createElement('canvas'); pc.width = vp.width; pc.height = vp.height
      await page.render({ canvasContext: pc.getContext('2d'), viewport: vp }).promise
      const blob = await new Promise(r => pc.toBlob(r, 'image/png'))
      const img = await decodeImg(URL.createObjectURL(blob))
      addBackgroundFromImage(img, resolved.name.replace(/\.pdf$/i, ''), { pdfPage: page, pdfBytes })
    } else if (file.type.startsWith('image/')) {
      const img = await decodeImg(URL.createObjectURL(file))
      addBackgroundFromImage(img, file.name.replace(/\.[^.]+$/, ''))
    } else {
      showToast('Drop an image or a PDF')
    }
  }
  function moveBg(id, dir) {
    const doc = docRef.current
    const i = doc.backgrounds.findIndex(b => b.id === id)
    const j = i + dir
    if (i === -1 || j < 0 || j >= doc.backgrounds.length) return
    saveUndo()
    const [b] = doc.backgrounds.splice(i, 1)
    doc.backgrounds.splice(j, 0, b)
    markDirty(); bump(); scheduleRender()
  }
  /* Objects panel drag-to-reorder — a drag handle (⠿), not the old ▲▼ buttons (too slow to move
     an object far up/down a long list). Two things make the gesture read clearly:
     1. A floating "ghost" copy of the row follows the pointer 1:1 (position: fixed, no jump —
        its position is `dragObjRow.rect.top + rawPointerDelta`, never recomputed from layout).
     2. The OTHER rows FLIP-animate (First-Last-Invert-Play) into their new slots whenever the
        live splice changes their position, so it's visually obvious where the drop will land.
     Target slot is computed arithmetically from `round(pointerDelta / rowHeight)` off the start
     index — not by re-measuring row rects on every move, which would fight the FLIP animation
     those very rows are mid-transition on. */
  const objRowRefs = useRef({})
  const objFlipRef = useRef(null)
  const [dragObj, setDragObj] = useState(null)   // { id, rect: DOMRect, dy: number } | null
  useLayoutEffect(() => {
    const before = objFlipRef.current
    if (!before) return
    objFlipRef.current = null
    for (const id of Object.keys(before)) {
      const el = objRowRefs.current[id]
      if (!el) continue
      const delta = before[id] - el.getBoundingClientRect().top
      if (!delta) continue
      el.style.transition = 'none'
      el.style.transform = 'translateY(' + delta + 'px)'
      requestAnimationFrame(() => { el.style.transition = 'transform 180ms ease'; el.style.transform = '' })
    }
  })
  function objDragStart(e, it) {
    e.preventDefault(); e.stopPropagation()
    const rowEl = objRowRefs.current[it.id]
    if (!rowEl) return
    const rect = rowEl.getBoundingClientRect()
    const rowH = rect.height + 2
    const startY = e.clientY
    let order = [...docRef.current.items].reverse().map(x => x.id)
    const startIdx = order.indexOf(it.id)
    saveUndo()
    setDragObj({ id: it.id, rect, dy: 0 })
    function move(ev) {
      const dy = ev.clientY - startY
      setDragObj(d => (d && d.id === it.id ? { ...d, dy } : d))
      const newIdx = Math.max(0, Math.min(order.length - 1, startIdx + Math.round(dy / rowH)))
      const idxNow = order.indexOf(it.id)
      if (newIdx !== idxNow) {
        const before = {}
        for (const id2 of order) { if (id2 === it.id) continue; const el = objRowRefs.current[id2]; if (el) before[id2] = el.getBoundingClientRect().top }
        objFlipRef.current = before
        const next = [...order]
        next.splice(idxNow, 1); next.splice(newIdx, 0, it.id)
        order = next
        docRef.current.items = [...next].reverse().map(id2 => docRef.current.items.find(x => x.id === id2))
        bump()
      }
    }
    function up() {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      setDragObj(null)
      markDirty(); scheduleRender()
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
  }
  /* Objects-panel row label: locked kind label + whatever distinguishes this instance. */
  function itemListLabel(it) {
    const base = (SITE_STYLES[it.kind] && SITE_STYLES[it.kind].label) || it.kind
    if (it.kind === 'label') return 'Note · ' + (it.text || '')
    if (it.kind === 'keytag') return base + ' #' + (it.n || 1) + (it.text ? ' · ' + it.text : '')
    if (it.kind === 'utilpoint') return utilTypeDef(it.ptype).label
    if (it.kind === 'corner') return (CORNER_LABEL[it.role] || base) + (it.elevation != null ? ' · ' + it.elevation + "′" : '')
    if (it.kind === 'pl' && it.variant === 'pending') return base + ' (pending)'
    if (it.text) return base + ' · ' + it.text
    return base
  }
  async function removeBg(id) {
    const doc = docRef.current
    const bg = doc.backgrounds.find(b => b.id === id)
    if (!bg) return
    if (!(await confirmDialog({ title: 'Remove background?', message: 'Remove "' + bg.name + '" from the composite?', confirmLabel: 'Remove', danger: true }))) return
    doc.backgrounds = doc.backgrounds.filter(b => b.id !== id)
    if (selBgId === id) setSelBgId(null)
    markDirty(); bump(); scheduleRender()
  }

  /* ── Pointer: pan/zoom, background move/resize, calibration ─────────────── */
  function itemAt(sx, sy) {
    const w = screenToWorld(sx, sy)
    const doc = docRef.current
    const tol = 6 / vpRef.current.z
    for (let i = doc.items.length - 1; i >= 0; i--) {
      const it = doc.items[i]
      if (it.locked) continue   // locked elements ignore canvas clicks — select/unlock via the Objects panel
      if (it.visible === false) continue   // hidden elements aren't clickable either
      if (it.kind === 'label') {
        if (w.x >= it.x - 8 && w.x <= it.x + 120 && Math.abs(w.y - it.y + 5) < 12) return it
        continue
      }
      if (POINT_KINDS.includes(it.kind)) {
        if (Math.hypot(w.x - it.x, w.y - it.y) < Math.max(pointExtent(it, doc), tol + 6)) return it
        continue
      }
      const pts = it.pts || []
      const closed = !isOpenKind(it.kind)
      for (let vi = 0; vi < pts.length; vi++) if (Math.hypot(w.x - pts[vi].x, w.y - pts[vi].y) < tol + 3) return it
      const hp = hasCurves(it) ? flattenItemPts(it, closed) : pts
      for (let ei = 0; ei < (closed ? hp.length : hp.length - 1); ei++) {
        const a = hp[ei], b = hp[(ei + 1) % hp.length]
        if (distToSeg(w.x, w.y, a.x, a.y, b.x, b.y) < tol) return it
      }
      if (closed && pts.length > 2 && SITE_STYLES[it.kind] && SITE_STYLES[it.kind].fill && pointInPoly(w.x, w.y, hp)) return it
    }
    return null
  }
  function vertexIndexAt(it, w) {
    const tol = (6 / vpRef.current.z) + 3
    const pts = it.pts || []
    for (let i = 0; i < pts.length; i++) if (Math.hypot(w.x - pts[i].x, w.y - pts[i].y) < tol) return i
    return -1
  }
  function commitDraft() {
    const d = draftRef.current
    draftRef.current = null
    const minPts = d && (d.kind === 'contextarc' ? 3 : OPEN_LINE_KINDS.includes(d.kind) ? 2 : 3)
    if (!d || d.pts.length < minPts) { drawOverlay(); return }
    saveUndo()
    const it = { id: newId(), kind: d.kind, pts: d.pts }
    if (d.kind === 'pl') { it.variant = 'confirmed'; it.edgeRoles = d.pts.map(() => null); it.roleSetbacks = { front: 0, side1: 0, side2: 0, rear: 0 } }
    docRef.current.items.push(it)
    setTool('select'); setSelItemId(it.id)
    scheduleRender(); bump()
  }
  async function placeLabel(w) {
    const txt = await promptDialog({ title: 'Label', placeholder: 'e.g. OCEAN VIEWS', required: true })
    if (txt === null || !txt.trim()) return
    saveUndo()
    docRef.current.items.push({ id: newId(), kind: 'label', x: w.x, y: w.y, text: txt.trim() })
    setTool('select'); scheduleRender(); bump()
  }
  function bgAtPoint(sx, sy) {
    const doc = docRef.current
    const w = screenToWorld(sx, sy)
    for (let i = doc.backgrounds.length - 1; i >= 0; i--) {
      const bg = doc.backgrounds[i]
      if (bg.locked) continue   // locked backgrounds ignore canvas clicks (select via the panel)
      if (bg.visible === false) continue
      // bg.sat CAN be dragged (calibration — see onPointerUp) even though its size is computed;
      // only resizing/rotating it by hand is disallowed (handleAt/resize is separately gated).
      if (w.x >= bg.x && w.x <= bg.x + bg.w && w.y >= bg.y && w.y <= bg.y + bg.h) return bg
    }
    return null
  }
  function handleAt(bg, sx, sy) {
    if (!bg) return null
    const z = vpRef.current.z, p = worldToScreen(bg.x, bg.y)
    const corners = [[0, 0, 'tl'], [1, 0, 'tr'], [1, 1, 'br'], [0, 1, 'bl']]
    for (const [hx, hy, id] of corners) {
      if (Math.abs(sx - (p.x + hx * bg.w * z)) < 8 && Math.abs(sy - (p.y + hy * bg.h * z)) < 8) return id
    }
    return null
  }
  function onPointerDown(e) {
    const ev = evRef.current
    try { ev.setPointerCapture(e.pointerId) } catch { /* synthetic */ }
    const rect = ev.getBoundingClientRect()
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top
    if (calibrating) {
      const w = screenToWorld(sx, sy)
      calPtsRef.current = [...calPtsRef.current, { x: w.x, y: w.y }].slice(-2)
      drawOverlay()
      if (calPtsRef.current.length === 2) finishCalibration()
      return
    }
    if (e.button === 1 || spaceRef.current) {
      dragRef.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, vx: vpRef.current.x, vy: vpRef.current.y }
      return
    }
    const t = toolRef.current
    const wpt = screenToWorld(sx, sy)
    if (DRAFT_KINDS.includes(t)) {
      if (!draftRef.current) draftRef.current = { kind: t, pts: [] }
      const d = draftRef.current
      if (POLY_KINDS.includes(t) && d.pts.length > 2) {
        // Closed polygons finish by clicking back on the first point
        const f = worldToScreen(d.pts[0].x, d.pts[0].y)
        if (Math.hypot(sx - f.x, sy - f.y) < 12) { commitDraft(); return }
      }
      if (OPEN_LINE_KINDS.includes(t) && d.pts.length >= 2) {
        // Open lines finish by clicking the last point again (or Enter)
        const l = worldToScreen(d.pts[d.pts.length - 1].x, d.pts[d.pts.length - 1].y)
        if (Math.hypot(sx - l.x, sy - l.y) < 12) { commitDraft(); return }
      }
      d.pts.push({ x: wpt.x, y: wpt.y })
      if (t === 'contextarc' && d.pts.length === 3) { commitDraft(); return }
      drawOverlay()
      return
    }
    if (POINT_KINDS.includes(t)) {
      saveUndo()
      const doc = docRef.current
      const it = { id: newId(), kind: t, x: wpt.x, y: wpt.y }
      if (t === 'sun') { it.r = 90; it.solar = { mode: 'vectors', radiusMode: 'auto' } }
      if (t === 'windrose') { it.r = 55; it.petals = [2, 1, 1, 0, 1, 2, 3, 2] }
      if (t === 'tree') it.diaFt = 15
      if (t === 'utilpoint') { it.ptype = 'other'; it.n = doc.items.filter(i2 => i2.kind === 'utilpoint' && i2.ptype === 'other').length + 1 }
      if (t === 'keytag') { it.n = doc.items.reduce((m, i2) => i2.kind === 'keytag' ? Math.max(m, i2.n || 0) : m, 0) + 1; it.text = '' }
      if (t === 'nuisance') it.rot = 0
      if (t === 'viewcone') { it.heading = 0; it.spread = 60; it.r = 120 }
      if (t === 'corner') {
        // Auto-assign the next unused corner; prefill its elevation from Parcel Research if
        // that field already has a value there (read-sync — never overwrite a placed marker).
        const used = new Set(doc.items.filter(i2 => i2.kind === 'corner').map(i2 => i2.role))
        it.role = CORNER_ROLES.find(r => !used.has(r)) || CORNER_ROLES[0]
        const pf = parcelRef.current && parcelRef.current.fields
        it.elevation = pf && pf[CORNER_FIELD[it.role]] != null ? pf[CORNER_FIELD[it.role]] : null
        it.rot = 0
      }
      doc.items.push(it)
      setTool('select'); setSelItemId(it.id)
      scheduleRender(); bump()
      return
    }
    if (ARROW_KINDS.includes(t)) {
      gestureStart()
      const it = { id: newId(), kind: t, pts: [{ x: wpt.x, y: wpt.y }, { x: wpt.x, y: wpt.y }] }
      if (t === 'windarrow') it.variant = 'breeze'
      if (t === 'viewarrow') it.variant = 'major'
      docRef.current.items.push(it)
      dragRef.current = { kind: 'arrow', it }
      return
    }
    if (t === 'label') { placeLabel(wpt); return }
    {
      const doc = docRef.current
      if (doc._northBox && wpt.x >= doc._northBox.x && wpt.x <= doc._northBox.x + doc._northBox.w && wpt.y >= doc._northBox.y && wpt.y <= doc._northBox.y + doc._northBox.h) {
        gestureStart()
        dragRef.current = { kind: 'north', sx, sy, ox: doc._northBox.cx, oy: doc._northBox.cy }
        return
      }
      if (doc._legendBox && wpt.x >= doc._legendBox.x && wpt.x <= doc._legendBox.x + doc._legendBox.w && wpt.y >= doc._legendBox.y && wpt.y <= doc._legendBox.y + doc._legendBox.h) {
        gestureStart()
        dragRef.current = { kind: 'legend', sx, sy, ox: doc._legendBox.x, oy: doc._legendBox.y }
        return
      }
      if (doc._keyNotesBox && wpt.x >= doc._keyNotesBox.x && wpt.x <= doc._keyNotesBox.x + doc._keyNotesBox.w && wpt.y >= doc._keyNotesBox.y && wpt.y <= doc._keyNotesBox.y + doc._keyNotesBox.h) {
        gestureStart()
        dragRef.current = { kind: 'keynotes', sx, sy, ox: doc._keyNotesBox.x, oy: doc._keyNotesBox.y }
        return
      }
      // Control-handle knobs of the selected item take priority (they sit off the path).
      // Plain drag keeps the pair mirrored (smooth anchor); Alt-drag breaks the symmetry.
      const selIt0 = doc.items.find(i2 => i2.id === selItemIdRef.current)
      if (selIt0 && !selIt0.locked && isCurvableKind(selIt0.kind) && selIt0.pts) {
        for (let vi = 0; vi < selIt0.pts.length; vi++) {
          const sp = selIt0.pts[vi]
          for (const hk of ['ho', 'hi']) {
            if (!sp[hk]) continue
            const hq = worldToScreen(sp.x + sp[hk].x, sp.y + sp[hk].y)
            if (Math.hypot(sx - hq.x, sy - hq.y) < 8) {
              gestureStart()
              dragRef.current = { kind: 'handle', it: selIt0, vi, which: hk, mirror: !e.altKey }
              return
            }
          }
        }
      }
      // Label grip of the selected item — also takes priority over the general item hit-test
      // (same reasoning as the control handles above: it sits off the item's own path/point).
      // The rotate handle is checked FIRST since it sits farther from the anchor than the move
      // diamond — testing it first means a drag that lands closer to the rotate circle than to
      // the diamond correctly grabs rotation, not the move grip.
      if (selIt0 && !selIt0.locked && selIt0.kind !== 'label') {
        const la = labelAnchorsRef.current.get(selIt0.id)
        if (la) {
          const rw = labelRotateHandleWorld(selIt0, la)
          const rq = worldToScreen(rw.x, rw.y)
          if (Math.hypot(sx - rq.x, sy - rq.y) < 8) {
            gestureStart()
            dragRef.current = { kind: 'labelRot', it: selIt0, cx: la.x, cy: la.y }
            return
          }
          const lq = worldToScreen(la.x, la.y)
          if (Math.hypot(sx - lq.x, sy - lq.y) < 8) {
            gestureStart()
            const o = selIt0.labelOffset
            dragRef.current = { kind: 'labelOffset', it: selIt0, sx, sy, ox: o ? o.dx : 0, oy: o ? o.dy : 0 }
            return
          }
        }
      }
      const hit = itemAt(sx, sy)
      if (hit) {
        const vi = hit.pts ? vertexIndexAt(hit, wpt) : -1
        // Alt-drag a whole element to leave the original in place and drag off a copy
        // (Illustrator convention). Doesn't apply to vertex drags — Alt already means
        // "break the handle mirror" there.
        if (e.altKey && vi < 0) {
          saveUndo()
          const dup = JSON.parse(JSON.stringify(hit))
          dup.id = newId()
          if (dup.kind === 'keytag') dup.n = doc.items.reduce((m, i2) => i2.kind === 'keytag' ? Math.max(m, i2.n || 0) : m, 0) + 1
          if (dup.kind === 'utilpoint' && dup.ptype === 'other') dup.n = doc.items.filter(i2 => i2.kind === 'utilpoint' && i2.ptype === 'other').length + 1
          doc.items.push(dup)
          setSelItemId(dup.id); setSelBgId(null)
          dragRef.current = { kind: 'item', it: dup, sx, sy, opts: (dup.pts || []).map(pp => ({ ...pp })), ox: dup.x, oy: dup.y, lox: dup.leader && dup.leader.x, loy: dup.leader && dup.leader.y }
          scheduleRender(); bump()
          return
        }
        setSelItemId(hit.id)
        setSelBgId(null)
        gestureStart()
        dragRef.current = vi >= 0
          ? { kind: 'vertex', it: hit, vi }
          : { kind: 'item', it: hit, sx, sy, opts: (hit.pts || []).map(pp => ({ ...pp })), ox: hit.x, oy: hit.y, lox: hit.leader && hit.leader.x, loy: hit.leader && hit.leader.y }
        return
      }
      if (selItemIdRef.current) setSelItemId(null)
    }
    const sel = docRef.current.backgrounds.find(b => b.id === selBgIdRef.current)
    const h = !sel?.locked && handleAt(sel, sx, sy)
    if (sel && h) {
      gestureStart()
      dragRef.current = { kind: 'resize', h, sx, sy, bg: sel, ox: sel.x, oy: sel.y, ow: sel.w, oh: sel.h }
      return
    }
    const hit = bgAtPoint(sx, sy)
    if (hit) {
      setSelBgId(hit.id)
      gestureStart()
      dragRef.current = { kind: 'move', sx, sy, bg: hit, ox: hit.x, oy: hit.y }
      return
    }
    setSelBgId(null)
    dragRef.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, vx: vpRef.current.x, vy: vpRef.current.y }
  }
  function onPointerMove(e) {
    const d = dragRef.current
    if (!d) return
    const ev = evRef.current, rect = ev.getBoundingClientRect()
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top
    if (d.kind === 'pan') {
      vpRef.current.x = d.vx + (e.clientX - d.sx)
      vpRef.current.y = d.vy + (e.clientY - d.sy)
      scheduleRender()
      return
    }
    if (d.kind === 'arrow') {
      const w = screenToWorld(sx, sy)
      d.it.pts[1] = { x: w.x, y: w.y }
      markDirty(); scheduleRender()
      return
    }
    const z = vpRef.current.z
    if (d.kind === 'legend') {
      const doc = docRef.current
      doc.legend.x = d.ox + (sx - d.sx) / z
      doc.legend.y = d.oy + (sy - d.sy) / z
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'north') {
      const doc = docRef.current
      doc.northArrow = { x: d.ox + (sx - d.sx) / z, y: d.oy + (sy - d.sy) / z }
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'keynotes') {
      const doc = docRef.current
      doc.keyNotes.x = d.ox + (sx - d.sx) / z
      doc.keyNotes.y = d.oy + (sy - d.sy) / z
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'labelOffset') {
      d.it.labelOffset = { dx: d.ox + (sx - d.sx) / z, dy: d.oy + (sy - d.sy) / z }
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'labelRot') {
      // Absolute bearing from the anchor to the pointer — the handle is a clock hand, not a
      // relative delta, so there's no drift to accumulate. Snaps to the nearest 45° when close.
      const w = screenToWorld(sx, sy)
      let deg = Math.atan2(w.y - d.cy, w.x - d.cx) * 180 / Math.PI
      const snaps = [0, 45, 90, 135, 180, -45, -90, -135]
      for (const s of snaps) {
        let diff = deg - s
        while (diff > 180) diff -= 360
        while (diff < -180) diff += 360
        if (Math.abs(diff) < 5) { deg = s; break }
      }
      d.it.labelRot = deg
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'handle') {
      const w = screenToWorld(sx, sy)
      const sp = d.it.pts[d.vi]
      const rel = { x: w.x - sp.x, y: w.y - sp.y }
      sp[d.which] = rel
      const other = d.which === 'ho' ? 'hi' : 'ho'
      if (d.mirror && sp[other]) sp[other] = { x: -rel.x, y: -rel.y }
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'vertex') {
      const w = screenToWorld(sx, sy)
      // Spread keeps the anchor's control handles (relative offsets ride along)
      d.it.pts[d.vi] = { ...d.it.pts[d.vi], x: w.x, y: w.y }
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'item') {
      const dx = (sx - d.sx) / z, dy = (sy - d.sy) / z
      if (d.it.pts) d.it.pts = d.opts.map(pp => ({ ...pp, x: pp.x + dx, y: pp.y + dy }))
      else { d.it.x = d.ox + dx; d.it.y = d.oy + dy; if (d.it.leader) d.it.leader = { x: d.lox + dx, y: d.loy + dy } }
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'move') {
      d.bg.x = d.ox + (sx - d.sx) / z
      d.bg.y = d.oy + (sy - d.sy) / z
      markDirty(); scheduleRender()
      return
    }
    if (d.kind === 'resize') {
      const dw = (sx - d.sx) / z
      const ar = d.ow / d.oh
      let nw = d.ow, nx = d.ox, ny = d.oy
      if (d.h === 'br' || d.h === 'tr') nw = Math.max(40, d.ow + dw)
      else nw = Math.max(40, d.ow - dw)
      const nh = nw / ar
      if (d.h === 'bl' || d.h === 'tl') nx = d.ox + d.ow - nw
      if (d.h === 'tl' || d.h === 'tr') ny = d.oy + d.oh - nh
      d.bg.x = nx; d.bg.y = ny; d.bg.w = nw; d.bg.h = nh
      d.bg._pdfCache = null   // re-render the PDF at the new size
      markDirty(); scheduleRender()
    }
  }
  function onPointerUp() {
    const d = dragRef.current
    dragRef.current = null
    // Dragging the satellite layer is how the geocoded address (approximate — rooftop/street
    // level, often off by tens of feet) gets calibrated onto the actual drawing: shift the
    // persistent geo-anchor by the same delta the image moved, so every FUTURE refresh (which
    // recomputes the image's position fresh from the anchor) reproduces this corrected position
    // instead of snapping back to the raw geocode.
    if (d && d.kind === 'move' && d.bg.sat && docRef.current.geoAnchor) {
      const dx = d.bg.x - d.ox, dy = d.bg.y - d.oy
      if (dx || dy) {
        const a = docRef.current.geoAnchor
        docRef.current.geoAnchor = { ...a, worldX: a.worldX + dx, worldY: a.worldY + dy }
      }
    }
    // Resizing the satellite is a manual, empirical scale correction — the ground-resolution
    // formula's zoom/lat math is a best-effort GUESS at how many real feet Mapbox's imagery
    // actually covers (repeatedly wrong so far, by a consistent factor, in ways that couldn't be
    // pinned down analytically without live access to test against). Dragging a corner until the
    // image visually lines up with the already-verified-correct drawing is the one way to
    // guarantee correctness regardless of what Mapbox is actually doing. The resulting ratio
    // (true feet-per-native-pixel implied by the new size, vs. what the formula guessed) is saved
    // as doc.satCorrection and multiplied into every future fetch's own guess — so the correction
    // improves the STARTING size of the next Refresh instead of needing to be redone from scratch
    // every time. aspect ratio is preserved by the existing generic resize handler (d.ow/d.oh),
    // so this never distorts the image.
    if (d && d.kind === 'resize' && d.bg.sat && d.bg.feetPerNativePx && docRef.current.scalePixPerFoot) {
      const formulaFeetPerNativePx = d.bg.feetPerNativePx
      const trueFeetWidth = d.bg.w / docRef.current.scalePixPerFoot
      const trueFeetPerNativePx = trueFeetWidth / d.bg.canvas.width
      const ratio = trueFeetPerNativePx / formulaFeetPerNativePx
      if (isFinite(ratio) && ratio > 0) {
        docRef.current.satCorrection = (docRef.current.satCorrection || 1) * ratio
        d.bg.feetPerNativePx = trueFeetPerNativePx
        showToast('Satellite scale corrected — future refreshes start from this calibration')
      }
    }
    if (d && d.kind === 'arrow') {
      const [a, b] = d.it.pts
      if (Math.hypot(b.x - a.x, b.y - a.y) * vpRef.current.z < 8) {
        // Too short to read as an arrow — treat as a mis-click and discard
        docRef.current.items = docRef.current.items.filter(i2 => i2.id !== d.it.id)
        scheduleRender()
      } else {
        setTool('select'); setSelItemId(d.it.id)
        scheduleRender(); bump()
      }
    }
    // Commits the drag's before-state only if the gesture actually changed something —
    // discarded arrows and click-without-move end up as no history entry at all.
    gestureEnd()
  }
  /* Double-click (select tool): on a vertex — toggle smooth ⇄ corner (smooth pulls out
     mirrored handles at ⅓ of the neighbor distances, the Illustrator convention); on an
     edge — insert a node there (de Casteljau split, so a curved edge keeps its exact shape). */
  function onDblClick(e) {
    if (toolRef.current !== 'select') return
    const ev = evRef.current, rect = ev.getBoundingClientRect()
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top
    const w = screenToWorld(sx, sy)
    const doc = docRef.current
    const tol = 6 / vpRef.current.z
    const cand = []
    const sel = doc.items.find(i2 => i2.id === selItemIdRef.current)
    if (sel && !sel.locked) cand.push(sel)
    const hit = itemAt(sx, sy)   // itemAt already skips locked items
    if (hit && hit !== sel) cand.push(hit)
    for (const it of cand) {
      if (!isCurvableKind(it.kind) || !it.pts) continue
      const closed = !isOpenKind(it.kind)
      // 1) vertex → toggle smooth ⇄ corner
      for (let vi = 0; vi < it.pts.length; vi++) {
        const p = it.pts[vi]
        if (Math.hypot(w.x - p.x, w.y - p.y) < tol + 3) {
          saveUndo()
          if (p.hi || p.ho) { delete p.hi; delete p.ho }
          else {
            const n = it.pts.length
            const prev = it.pts[(vi - 1 + n) % n], next = it.pts[(vi + 1) % n]
            const hasPrev = closed || vi > 0
            const hasNext = closed || vi < n - 1
            const dx = (hasNext ? next.x : p.x) - (hasPrev ? prev.x : p.x)
            const dy = (hasNext ? next.y : p.y) - (hasPrev ? prev.y : p.y)
            const len = Math.hypot(dx, dy) || 1
            const ux = dx / len, uy = dy / len
            if (hasPrev) { const lo = Math.hypot(p.x - prev.x, p.y - prev.y) / 3; p.hi = { x: -ux * lo, y: -uy * lo } }
            if (hasNext) { const ln = Math.hypot(next.x - p.x, next.y - p.y) / 3; p.ho = { x: ux * ln, y: uy * ln } }
          }
          setSelItemId(it.id)
          scheduleRender(); bump()
          return
        }
      }
      // 2) edge → insert a node at the click point
      const flat = flattenItemPts(it, closed)
      const segCount = closed ? flat.length : flat.length - 1
      let best = null
      for (let k = 0; k < segCount; k++) {
        const a = flat[k], b = flat[(k + 1) % flat.length]
        const dd = distToSeg(w.x, w.y, a.x, a.y, b.x, b.y)
        if (dd < tol && (!best || dd < best.d)) {
          const ddx = b.x - a.x, ddy = b.y - a.y, l2 = ddx * ddx + ddy * ddy
          const tt = l2 ? Math.max(0, Math.min(1, ((w.x - a.x) * ddx + (w.y - a.y) * ddy) / l2)) : 0
          const bt = b.edge === a.edge ? b.t : 1
          best = { d: dd, edge: a.edge, t: a.t + (bt - a.t) * tt }
        }
      }
      if (best) {
        saveUndo()
        splitEdge(it, best.edge, Math.min(0.95, Math.max(0.05, best.t || 0.5)), closed)
        setSelItemId(it.id)
        scheduleRender(); bump()
        return
      }
    }
  }
  function onWheel(e) {
    e.preventDefault()
    const rect = evRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top
    const f = e.deltaY < 0 ? 1.1 : 1 / 1.1
    const v = vpRef.current
    // Floor is deliberately tiny (not 0.1) — the satellite underlay needs to zoom out to regional
    // scale (miles, to draw view vectors toward the ocean/mountains/distant landmarks), and the
    // visible extent in feet is screen_px / (z * scalePixPerFoot). A 0.1 floor capped every site
    // at well under a mile of visible extent regardless of scale — the interactive zoom itself,
    // not the satellite fetch math (already verified to regional scale), was the real ceiling.
    const nz = Math.min(8, Math.max(0.0005, v.z * f))
    const zr = nz / v.z
    v.x = cx - zr * (cx - v.x); v.y = cy - zr * (cy - v.y); v.z = nz
    scheduleRender(); bump()
  }
  const spaceRef = useRef(false)
  useEffect(() => {
    const kd = e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      if (e.key === ' ') { e.preventDefault(); spaceRef.current = true }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); if (e.shiftKey) redo(); else undo() }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || e.key === 'Y')) { e.preventDefault(); redo() }
      if (e.key === 'Enter' && draftRef.current) { e.preventDefault(); commitDraft() }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selItemIdRef.current) {
        const selIt = docRef.current.items.find(i2 => i2.id === selItemIdRef.current)
        if (selIt && !selIt.locked) {
          e.preventDefault()
          saveUndo()
          docRef.current.items = docRef.current.items.filter(i2 => i2.id !== selItemIdRef.current)
          setSelItemId(null); scheduleRender(); bump()
        }
      }
      if (e.key === 'Escape') {
        if (draftRef.current) { draftRef.current = null; drawOverlay() }
        else if (calibrating) { setCalibrating(false); calPtsRef.current = []; drawOverlay() }
        else { setSelBgId(null); setSelItemId(null) }
      }
      // Arrow-key nudge for the selected background — finer than a mouse drag (1 screen px per
      // press, 10 with Shift, independent of zoom) for calibrating the satellite layer precisely
      // onto the drawing once you're zoomed in close.
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selBgIdRef.current) {
        const bg = docRef.current.backgrounds.find(b => b.id === selBgIdRef.current)
        if (bg && !bg.locked) {
          e.preventDefault()
          const step = (e.shiftKey ? 10 : 1) / vpRef.current.z
          const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0
          const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
          saveUndo()
          bg.x += dx; bg.y += dy
          if (bg.sat && docRef.current.geoAnchor) {
            const a = docRef.current.geoAnchor
            docRef.current.geoAnchor = { ...a, worldX: a.worldX + dx, worldY: a.worldY + dy }
          }
          scheduleRender(); bump()
        }
      }
      // Same fine-control nudge for the selected item's LABEL grip (independent of the item's own
      // position) — only fires when the item actually has a label showing and isn't the plain
      // 'label' item (whole item IS the label there, already covered by background-style nudge
      // logic on regular items via mouse drag / the item drag path, not arrow keys — kept simple).
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selItemIdRef.current) {
        const it = docRef.current.items.find(i2 => i2.id === selItemIdRef.current)
        const la = it && labelAnchorsRef.current.get(it.id)
        if (it && !it.locked && it.kind !== 'label' && la) {
          e.preventDefault()
          const step = (e.shiftKey ? 10 : 1) / vpRef.current.z
          const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0
          const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
          const o = it.labelOffset
          saveUndo()
          it.labelOffset = { dx: (o ? o.dx : 0) + dx, dy: (o ? o.dy : 0) + dy }
          scheduleRender(); bump()
        }
      }
    }
    const ku = e => { if (e.key === ' ') spaceRef.current = false }
    document.addEventListener('keydown', kd); document.addEventListener('keyup', ku)
    return () => { document.removeEventListener('keydown', kd); document.removeEventListener('keyup', ku) }
  }, [calibrating]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Scale calibration ─────────────────────────────────────────────────── */
  /* The satellite image's size in world units is baked in at fetch time from WHATEVER
     scalePixPerFoot existed then (see refreshSatellite) — it never automatically re-derives
     itself. If satellite was added before the drawing's real scale was calibrated (the common
     order: drop in satellite first, import/resize the site plan by eye, THEN run Set Scale
     against a known dimension on it), the satellite keeps the bootstrap's own guessed scale and
     ends up visibly the wrong size relative to the now-correctly-scaled drawing. Re-derive it
     from its known real-world resolution (feetPerNativePx, stored per-fetch) any time the scale
     changes, resizing around its current center so it doesn't drift position. */
  function resyncSatelliteScale() {
    const doc = docRef.current
    const bg = doc.backgrounds.find(b => b.sat)
    if (!bg || !bg.feetPerNativePx || !doc.scalePixPerFoot || !bg.canvas) return
    const cx = bg.x + bg.w / 2, cy = bg.y + bg.h / 2
    const worldPerNativePx = bg.feetPerNativePx * doc.scalePixPerFoot
    bg.w = bg.canvas.width * worldPerNativePx
    bg.h = bg.canvas.height * worldPerNativePx
    bg.x = cx - bg.w / 2
    bg.y = cy - bg.h / 2
  }
  async function finishCalibration() {
    const [a, b] = calPtsRef.current
    const px = Math.hypot(b.x - a.x, b.y - a.y)
    const ans = await promptDialog({ title: 'Set scale', message: 'Real-world distance between the two points (feet):', placeholder: 'e.g. 50', required: true })
    setCalibrating(false)
    calPtsRef.current = []
    drawOverlay()
    if (ans === null) return
    const ft = parseFloat(ans)
    if (!(ft > 0) || !(px > 0)) { showToast('Scale not set'); return }
    saveUndo()
    docRef.current.scalePixPerFoot = px / ft
    resyncSatelliteScale()
    bump(); scheduleRender()
    showToast('Scale set — 1 px = ' + (Math.round(ft * 12 / px * 100) / 100) + '"')
  }

  /* ── Serialization + persistence ───────────────────────────────────────── */
  function serializeDoc() {
    const doc = docRef.current
    return {
      version: 1,
      north: doc.north, latitude: doc.latitude, longitude: doc.longitude, scalePixPerFoot: doc.scalePixPerFoot, wash: doc.wash,
      vp: { ...vpRef.current },
      // The live satellite underlay (bg.sat) is deliberately excluded — it's fetched fresh each
      // time it's toggled on rather than embedded, so saves stay lean and the imagery is never
      // "an uploaded image" the team has to manage or re-upload.
      backgrounds: doc.backgrounds.filter(bg => !bg.sat).map(bg => ({
        name: bg.name, x: bg.x, y: bg.y, w: bg.w, h: bg.h, rotation: bg.rotation || 0, opacity: bg.opacity, locked: !!bg.locked, visible: bg.visible !== false,
        ...(bg.pdfBytes
          ? { pdfData: u8ToDataUrl(bg.pdfBytes), png: placeholderPng(bg) }
          : { png: bg.canvas.toDataURL('image/png') }),
      })),
      items: doc.items,
      legend: doc.legend,
      northArrow: doc.northArrow,
      keyNotes: doc.keyNotes,
      canvas: doc.canvas,
      geoAnchor: doc.geoAnchor,
      satCorrection: doc.satCorrection,
    }
  }
  function placeholderPng(bg) {
    const maxW = 1200
    if (bg.canvas.width <= maxW) return bg.canvas.toDataURL('image/png')
    const s = maxW / bg.canvas.width
    const c = document.createElement('canvas'); c.width = maxW; c.height = Math.max(1, Math.round(bg.canvas.height * s))
    c.getContext('2d').drawImage(bg.canvas, 0, 0, c.width, c.height)
    return c.toDataURL('image/png')
  }
  async function hydrateDoc(raw) {
    const doc = freshDoc()
    doc.north = raw.north || 0
    doc.latitude = raw.latitude != null ? raw.latitude : 33.54
    doc.longitude = raw.longitude != null ? raw.longitude : null
    doc.scalePixPerFoot = raw.scalePixPerFoot || null
    doc.wash = raw.wash != null ? raw.wash : 0.35
    doc.items = raw.items || []
    doc.legend = raw.legend || doc.legend
    doc.keyNotes = raw.keyNotes || doc.keyNotes
    doc.geoAnchor = raw.geoAnchor || null
    doc.satCorrection = raw.satCorrection || null
    if (raw.vp) vpRef.current = { x: raw.vp.x || 0, y: raw.vp.y || 0, z: raw.vp.z || 1 }
    for (const b of (raw.backgrounds || [])) {
      const img = await decodeImg(b.png)
      const c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight
      c.getContext('2d').drawImage(img, 0, 0)
      const bg = { id: newId(), name: b.name || 'Background', x: b.x, y: b.y, w: b.w, h: b.h, rotation: b.rotation || 0, opacity: b.opacity != null ? b.opacity : 1, locked: !!b.locked, visible: b.visible !== false, canvas: c }
      if (b.pdfData && window.pdfjsLib) {
        try {
          const bytes = dataUrlToU8(b.pdfData)
          const pdoc = await window.pdfjsLib.getDocument({ data: bytes.slice(0) }).promise
          bg.pdfPage = await pdoc.getPage(1)
          bg.pdfBytes = bytes
        } catch { /* raster fallback */ }
      }
      doc.backgrounds.push(bg)
    }
    doc.northArrow = raw.northArrow || { x: null, y: null }
    if (raw.canvas) doc.canvas = raw.canvas
    else {
      // Pre-canvas saves: wrap an 11×17 sheet around the existing content so nothing crops away
      const bb = itemsBBox(doc)
      if (bb) {
        let w = (bb.maxX - bb.minX) + 140, h = (bb.maxY - bb.minY) + 140
        if (w / h > 17 / 11) h = w * 11 / 17; else w = h * 17 / 11
        doc.canvas = { x: (bb.minX + bb.maxX) / 2 - w / 2, y: (bb.minY + bb.maxY) / 2 - h / 2, w, h }
      }
    }
    return doc
  }
  /* Center the sheet in the viewport at a comfortable zoom. */
  function fitViewToCanvas() {
    const c = docRef.current && docRef.current.canvas
    if (!c || !compRef.current || !sw() || !sh()) return
    const z = Math.min(sw() / (c.w + 120), sh() / (c.h + 120)) || 1
    vpRef.current = { z, x: (sw() - c.w * z) / 2 - c.x * z, y: (sh() - c.h * z) / 2 - c.y * z }
    scheduleRender(); bump()
  }
  function buildExportCanvas(scale = 2) {
    const doc = docRef.current
    const c = doc.canvas
    if (!c) return null
    // The export IS the sheet — anything off the canvas crops away, like an artboard.
    const minX = c.x, minY = c.y
    const cnv = document.createElement('canvas')
    cnv.width = Math.max(1, Math.round(c.w * scale)); cnv.height = Math.max(1, Math.round(c.h * scale))
    const g = cnv.getContext('2d')
    g.fillStyle = '#fff'; g.fillRect(0, 0, cnv.width, cnv.height)
    const savedVp = vpRef.current
    vpRef.current = { x: -minX * scale, y: -minY * scale, z: scale }
    drawBackgrounds(g, doc, (wx, wy) => ({ x: wx * scale - minX * scale, y: wy * scale - minY * scale }))
    drawWash(g, doc, 0, 0, cnv.width, cnv.height)
    g.save()
    g.translate(-minX * scale, -minY * scale)
    g.scale(scale, scale)
    drawItems(g, doc)
    g.restore()
    vpRef.current = savedVp
    return cnv
  }
  async function thumbBlob() {
    const exp = buildExportCanvas(0.4)
    if (!exp) return null
    const tw = 360, th = Math.max(1, Math.round(exp.height * (tw / exp.width)))
    const c = document.createElement('canvas'); c.width = tw; c.height = th
    c.getContext('2d').drawImage(exp, 0, 0, tw, th)
    return new Promise(r => c.toBlob(r, 'image/png'))
  }
  async function doSave(silent = false) {
    if (savingRef.current) return
    savingRef.current = true
    if (!silent) setSaveStatus('Saving…')
    try {
      const jsonBlob = new Blob([JSON.stringify(serializeDoc())], { type: 'application/json' })
      const tb = await thumbBlob()
      // Best-effort — a save must never fail over PDF rendering. This is the "final state PDF"
      // PostMilestoneModal freezes into Documents at post time (api/site-analyses.js#freeze).
      const pdfBlob = await buildPdfBlob().catch(() => null)
      const id = await saveSiteAnalysis({ analysisId: analysisIdRef.current, msId, projectId, jsonBlob, thumbBlob: tb, pdfBlob })
      analysisIdRef.current = id
      setAnalysisId(id)
      dirtyRef.current = false
      setSaveStatus('Saved ' + new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }))
    } catch (err) {
      console.error('[SiteAnalysis] save error', err)
      setSaveStatus('Save failed')
      if (!silent) showToast('Save failed — ' + (err.message || 'unknown'))
    } finally { savingRef.current = false }
  }
  /* Load the project's one analysis, creating it (empty) the first time this project is opened.
     There is no "open a different one" or "new analysis" — this always resolves to the same
     row for this project, every time, for every team member. */
  async function loadProjectAnalysis() {
    try {
      setLoading(true)
      const a = await getOrCreateSiteAnalysis(projectId, msId)
      docRef.current = await hydrateDoc(a.doc || {})
      analysisIdRef.current = a.id
      setAnalysisId(a.id)
      dirtyRef.current = false
      setLoading(false); bump(); scheduleRender()
      if (!a.doc || !a.doc.vp) fitViewToCanvas()
    } catch (err) {
      console.error('[SiteAnalysis] load error', err)
      setLoading(false)
      showToast('Could not open — ' + (err.message || 'unknown'))
    }
  }
  /* Seed latitude from the project address — one Mapbox geocode via the vicinity-map endpoint. */
  async function latFromAddress() {
    if (!projectId) { showToast('No project on this milestone'); return }
    try {
      const token = await window.__clerkGetToken()
      const res = await fetch('/api/vicinity-map?projectId=' + encodeURIComponent(projectId) + '&geocode=1', { headers: { Authorization: 'Bearer ' + token } })
      const d = await res.json()
      if (d.lat) {
        saveUndo()
        docRef.current.latitude = Math.round(d.lat * 10000) / 10000
        // Longitude rides along — it feeds the clock-time → solar-time correction for the sun
        // diagram's true solar vectors (see solarHourFor). Falls back gracefully when absent.
        if (d.lng != null) docRef.current.longitude = Math.round(d.lng * 10000) / 10000
        bump(); scheduleRender()
        showToast('Lat ' + docRef.current.latitude + '°' + (d.lng != null ? ' / lng ' + docRef.current.longitude + '°' : '') + ' from the project address')
      } else showToast(d.address ? 'Could not geocode the address' : 'No address on the project')
    } catch { showToast('Address lookup failed') }
  }
  /* The one PDF renderer — used by the save path (so a "final state" PDF always exists for
     PostMilestoneModal to freeze into Documents) AND by the on-demand Preview PDF button.
     Client-side only: embeds the export PNG on a page proportioned to the 11×17 sheet. */
  async function buildPdfBlob() {
    const exp = buildExportCanvas(2)
    if (!exp) return null
    const pngBlob = await new Promise(r => exp.toBlob(r, 'image/png'))
    const pngBytes = new Uint8Array(await pngBlob.arrayBuffer())
    const { PDFDocument } = await import('pdf-lib')
    const pdfDoc = await PDFDocument.create()
    const png = await pdfDoc.embedPng(pngBytes)
    const c = docRef.current.canvas
    const pageW = 17 * 72   // points — the sheet's long edge is always 17"
    const pageH = pageW * (c.h / c.w)
    const page = pdfDoc.addPage([pageW, pageH])
    page.drawImage(png, { x: 0, y: 0, width: pageW, height: pageH })
    const bytes = await pdfDoc.save()
    return new Blob([bytes], { type: 'application/pdf' })
  }
  async function doPreviewPdf() {
    // Open the tab synchronously so the browser doesn't block it as a popup, then point it at
    // the rendered PDF once ready — same pattern as MilestoneSections.jsx's rendering-package PDF.
    const tab = window.open('', '_blank')
    if (tab) {
      tab.document.write('<body style="margin:0;font-family:\'DM Sans\',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#f4f7f8;color:#07272D;"><p style="font-size:13px;color:#9ab0ba;letter-spacing:0.01em">Generating PDF…</p></body>')
      tab.document.close()
    }
    try {
      const blob = await buildPdfBlob()
      if (!blob) { if (tab) tab.close(); showToast('Add a background first'); return }
      const url = URL.createObjectURL(blob)
      if (tab) tab.location = url
      else window.open(url, '_blank')
    } catch (err) {
      if (tab) tab.close()
      showToast('PDF preview failed — ' + (err.message || 'unknown'))
    }
  }
  /* Autosave on close — the tool has no unsaved-changes prompt, so the ✕ button (and Escape,
     wherever it's wired) must flush a pending edit before the doc/canvas refs go away. */
  async function handleClose() {
    if (dirtyRef.current && !savingRef.current) { try { await doSave(true) } catch { /* doSave already reports its own failure */ } }
    onClose()
  }

  /* ── Boot: size canvases, load newest analysis (or fresh), autosave ─────── */
  useEffect(() => {
    let cancelled = false
    docRef.current = freshDoc()
    function size() {
      const wrap = wrapRef.current
      if (!wrap) return
      for (const ref of [compRef, uiRef, evRef]) {
        if (ref.current) { ref.current.width = wrap.offsetWidth; ref.current.height = wrap.offsetHeight }
      }
      scheduleRender()
    }
    size()
    const ro = new ResizeObserver(size)
    if (wrapRef.current) ro.observe(wrapRef.current)
    loadPdfjs()
    // Best-effort — corner markers work standalone even with no Parcel Research yet.
    if (projectId) {
      getParcels(projectId).then(list => { if (!cancelled) parcelRef.current = (list && list[0]) || null })
        .catch(() => { parcelRef.current = null })
    }
    if (!cancelled) loadProjectAnalysis()
    const auto = setInterval(() => { if (dirtyRef.current && !savingRef.current) doSave(true) }, 30000)
    return () => {
      cancelled = true
      ro.disconnect()
      clearInterval(auto)
      if (renderRaf.current) cancelAnimationFrame(renderRaf.current)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── UI ─────────────────────────────────────────────────────────────────── */
  const doc = docRef.current
  const chip = { fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '4px 10px', borderRadius: 4, border: '0.5px solid rgba(255,255,255,0.28)', background: 'transparent', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', whiteSpace: 'nowrap' }
  const plbl = { fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', borderBottom: '0.5px solid var(--aj-border)', paddingBottom: 2, marginBottom: 6 }
  // The object-properties block reads as tool CONFIGURATION (Backgrounds/Objects/Site) unless it's
  // visually boxed off — a plain `plbl` caption blends right in. This card + accent-colored
  // "PROPERTIES" chip is the one place in the panel with a hard edge, so it's obvious where the
  // kit-tool settings end and "you are now editing a specific object" begins.
  const propsCard = { border: '1px solid var(--aj-accent)', borderRadius: 8, background: 'var(--aj-bg-elevated)', padding: '10px 10px 12px', boxShadow: '0 1px 4px rgba(7,39,45,0.10)' }
  const propsChip = { display: 'inline-block', fontFamily: '"DM Mono",monospace', fontSize: 8.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fff', background: 'var(--aj-accent)', padding: '2px 7px', borderRadius: 3, marginRight: 7 }

  return createPortal(
    <div data-modal-id="SiteAnalysisModal" style={{ position: 'fixed', inset: 0, zIndex: 760, background: 'var(--aj-bg-subtle)', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar — QC-family dark brand chrome */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: INK, color: '#fff', flexShrink: 0 }}>
        <span style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 600, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          Site Analysis Map{projectName ? ' · ' + projectName : ''}
        </span>
        <button style={chip} onClick={() => doSave(false)}>Save</button>
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>{saveStatus}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{Math.round(vpRef.current.z * 100)}%</span>
        <button style={chip} title="Shortcuts & how to place" onClick={() => setShowKeys(true)}>?</button>
        <button style={{ ...chip, ...(cropPreview ? { background: 'rgba(255,255,255,0.18)', color: '#fff' } : {}) }}
          title="Preview with anything off the sheet hidden — matches what actually exports"
          onClick={() => setCropPreview(v => !v)}>{cropPreview ? 'Crop: On' : 'Crop: Off'}</button>
        <button style={{ ...chip, background: '#fff', color: INK, fontWeight: 600, border: 'none' }} onClick={doPreviewPdf}>Preview PDF</button>
        <button style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 15, cursor: 'pointer', padding: '2px 6px' }} title="Close" onClick={handleClose}>✕</button>
      </div>

      {picker}

      {showKeys && (
        <div onClick={() => setShowKeys(false)} style={{ position: 'fixed', inset: 0, zIndex: 780, background: 'rgba(7,39,45,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--aj-bg-surface)', borderRadius: 10, border: '0.5px solid var(--aj-border)', padding: '18px 22px', width: 420, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 600, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 12 }}>How to place & edit</div>
            {[
              ['Polygons (PL, areas)', 'Click each corner · click the first point (or Enter) to close · Esc cancels'],
              ['Lines (SB, APRCH, ULINE, HZRD)', 'Click each point · click the last point again (or Enter) to finish'],
              ['Context arc', 'Click 3 points — start, through, end'],
              ['Arrows (WIND, VIEW, SLOPE)', 'Press and drag from tail to head'],
              ['Symbols (SUN, ROSE, TREE, …)', 'Click to drop, then tune in the right panel'],
              ['Select / move', 'Click an element · drag body or corner handles'],
              ['Add a node', 'Double-click on an edge (works on curves too)'],
              ['Curve a corner', 'Double-click a vertex — control handles appear; drag them (Alt breaks the mirror); double-click again to flatten'],
              ['Delete', 'Select, then Delete or Backspace'],
              ['Undo / redo', 'Ctrl+Z / Ctrl+Shift+Z (or Ctrl+Y)'],
              ['Pan / zoom', 'Space + drag (or drag empty ground) · mouse wheel'],
              ['Legend & north arrow', 'Drag them anywhere on the sheet · legend toggle in the right panel'],
              ['Draw order', 'Objects panel — click to select, drag the ⠿ handle to reorder'],
              ['The sheet', 'Export crops to the 11×17 canvas · rotate a background (Backgrounds panel) to square north'],
              ['Show / hide', '👁 in the Objects or Backgrounds panel — hidden items are inert, not just invisible'],
              ['Lock a background or element', 'Padlock in its panel row — locked items ignore canvas clicks (no accidental drags)'],
              ['Property line setbacks', 'Assign each edge Front / Side 1 / Side 2 / Rear, then one setback per side — the buildable envelope derives automatically'],
              ['Corner elevations', 'Left rail → ELEV — ties to Parcel Research automatically, no duplicate entry'],
              ['Duplicate', 'Alt + drag an element to leave the original and drag off a copy'],
              ['Resize the panel', 'Drag the divider between the canvas and the right panel'],
              ['Key tags', 'Give one a note in its properties card — it appears in the Key Notes list (toggle below)'],
              ['Crop preview', 'Toggle in the top bar — hides anything off the sheet, matching what actually exports'],
              ['Satellite underlay', 'Backgrounds panel → + Add satellite view (fetched live, not saved with the file). Zoom the canvas OUT first, then hit its 🔄 Refresh button to pull in a wider image for view vectors toward distant landmarks — it always sizes to whatever you’re currently looking at'],
              ['Preview PDF', 'Top bar — opens a new tab and renders the current state as a PDF'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 10, marginBottom: 7 }}>
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-accent)', width: 150, flexShrink: 0 }}>{k}</span>
                <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', lineHeight: 1.4 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Kit rail — every element is typed; styling is locked in SITE_STYLES */}
        <div style={{ width: 58, flexShrink: 0, borderRight: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-surface)', overflowY: 'auto', padding: '8px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {[
            [null, [['select', 'SEL', 'Select / edit']]],
            ['BOUND', [['pl', 'PL', 'Property line'], ['setback', 'SB', 'Setback line (manual)'], ['easement', 'ESMT', 'Easement']]],
            ['AREAS', [['structure', 'BLDG', 'Existing structure'], ['water', 'H2O', 'Water body'], ['vegmass', 'VEG', 'Vegetation mass'], ['boulders', 'ROCK', 'Rock / boulders'], ['importantzone', 'ZONE', 'Important zone']]],
            ['CLIMATE', [['sun', 'SUN', 'Sun path (from latitude + north)'], ['windrose', 'ROSE', 'Wind rose'], ['windarrow', 'WIND', 'Wind arrow (drag)']]],
            ['INFLNC', [['viewarrow', 'VIEW', 'View arrow (drag)'], ['viewcone', 'CONE', 'View cone'], ['contextarc', 'ARC', 'Context arc (3 clicks)'], ['approach', 'APRCH', 'Approach path'], ['nuisance', 'NUIS', 'Nuisance source'], ['slope', 'SLOPE', 'Slope arrow (drag, points downhill)']]],
            ['SITE', [['tree', 'TREE', 'Significant tree'], ['utilpoint', 'UTIL', 'Utility point'], ['utilline', 'ULINE', 'Utility line'], ['hazardedge', 'HZRD', 'Hazard edge'], ['corner', 'ELEV', 'Corner elevation (tied to Parcel Research)'], ['keytag', 'TAG', 'Numbered key tag']]],
            ['MEASURE', [['dimension', 'DIM', 'Dimension line (drag) — live distance off the current scale']]],
            [null, [['label', 'TEXT', 'Label']]],
          ].map(([grp, tools], gi) => (
            <div key={gi} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
              {grp && <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 6.5, letterSpacing: '0.12em', color: 'var(--aj-text-muted)', marginTop: 6, marginBottom: 1 }}>{grp}</div>}
              {tools.map(([id, lbl, tip]) => (
                <button key={id} title={tip} onClick={() => setTool(id)}
                  style={{ width: 44, height: 28, borderRadius: 5, border: '0.5px solid ' + (tool === id ? 'var(--aj-accent)' : 'transparent'), background: tool === id ? 'var(--aj-bg-subtle)' : 'transparent', color: tool === id ? 'var(--aj-accent)' : 'var(--aj-text-mid)', fontFamily: '"DM Mono",monospace', fontSize: 8.5, letterSpacing: '0.04em', cursor: 'pointer', padding: 0 }}>
                  {lbl}
                </button>
              ))}
            </div>
          ))}
        </div>
        {/* Canvas */}
        <div ref={wrapRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#fff' }}>
          <canvas ref={compRef} style={{ position: 'absolute', inset: 0 }} />
          <canvas ref={uiRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
          <canvas ref={evRef} style={{ position: 'absolute', inset: 0, touchAction: 'none', cursor: calibrating ? 'crosshair' : 'default' }}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onWheel={onWheel} onDoubleClick={onDblClick} />
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' }}>Loading…</div>
          )}
          {!loading && doc && doc.backgrounds.length === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'none' }}>
              <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 14, fontWeight: 300, letterSpacing: '0.04em', color: 'var(--aj-text-mid)' }}>Start with a base</div>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)', maxWidth: 340, textAlign: 'center' }}>Add a site plan PDF or aerial image from the Backgrounds panel — then build the analysis from the kit.</div>
            </div>
          )}
          {toast && (
            <div style={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', background: INK, color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 14px', borderRadius: 5, boxShadow: '0 2px 12px rgba(7,39,45,0.3)' }}>{toast}</div>
          )}
        </div>

        {/* Drag to resize the right panel — persists across sessions (aj-sa-panelw) */}
        <PanelResizeHandle
          getStart={() => panelW}
          onChange={w => { setPanelW(w); scheduleRender() }}
          onCommit={w => { try { localStorage.setItem('aj-sa-panelw', String(w)) } catch { /* private mode */ } }}
        />
        {/* Right panel — Properties is pinned to the bottom (own scroll if a complex item makes
            it tall); Site/Backgrounds/Objects scroll independently in the space above it. */}
        <div style={{ width: panelW, flexShrink: 0, background: 'var(--aj-bg-surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 10px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <SectionHeader panelKey="site">Site</SectionHeader>
            {!collapsedPanels.site && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', width: 56 }}>LATITUDE</span>
                  <input type="number" step="0.01" value={doc ? doc.latitude : 33.54}
                    onWheel={numberWheel}
                    onFocus={gestureStart} onBlur={gestureEnd}
                    onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { doc.latitude = v; markDirty(); bump(); scheduleRender() } }}
                    style={{ flex: 1, fontFamily: '"DM Mono",monospace', fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none' }} />
                  <button onClick={latFromAddress} title="Look up latitude + longitude from the project address"
                    style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, padding: '3px 6px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-accent)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    From address
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', width: 56 }} title="Feeds the clock-time → solar-time correction for the sun diagram's solar vectors. Blank = times read as solar time (fine for a diagram).">LONGITUDE</span>
                  <input type="number" step="0.01" value={doc && doc.longitude != null ? doc.longitude : ''}
                    placeholder="—"
                    onWheel={numberWheel}
                    onFocus={gestureStart} onBlur={gestureEnd}
                    onChange={e => { const v = parseFloat(e.target.value); doc.longitude = isNaN(v) ? null : v; markDirty(); bump(); scheduleRender() }}
                    style={{ flex: 1, fontFamily: '"DM Mono",monospace', fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', width: 56 }} title="Rotates the north arrow, sun path, and wind rose. 0° = plan-up is true north.">NORTH</span>
                  <input type="number" step="1" value={doc ? Math.round(((doc.north || 0) * 180 / Math.PI) * 10) / 10 : 0}
                    onWheel={numberWheel}
                    onFocus={gestureStart} onBlur={gestureEnd}
                    onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { doc.north = v * Math.PI / 180; markDirty(); bump(); scheduleRender() } }}
                    style={{ flex: 1, fontFamily: '"DM Mono",monospace', fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none' }} />
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>°</span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => { setCalibrating(true); calPtsRef.current = []; showToast('Click two points a known distance apart') }}
                    style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '5px 4px', border: '0.5px solid ' + (calibrating ? 'var(--aj-accent)' : 'var(--aj-border-mid)'), borderRadius: 4, background: calibrating ? 'var(--aj-bg-subtle)' : 'transparent', color: calibrating ? 'var(--aj-accent)' : 'var(--aj-text-mid)', cursor: 'pointer' }}>
                    {calibrating ? 'Click 2 points…' : 'Set Scale'}
                  </button>
                </div>
                <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-muted)', marginTop: 5 }}>
                  {doc && doc.scalePixPerFoot ? ('Scale: ' + (Math.round(doc.scalePixPerFoot * 100) / 100) + ' px/ft') : 'Scale not set'}
                </div>
              </>
            )}
          </div>

          <div>
            <SectionHeader panelKey="backgrounds">Backgrounds</SectionHeader>
            {!collapsedPanels.backgrounds && (
              <>
                {doc && doc.backgrounds.length === 0 && (
                  <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', marginBottom: 6 }}>No backgrounds yet.</div>
                )}
                {doc && [...doc.backgrounds].reverse().map(bg => (
                  <div key={bg.id} onClick={() => setSelBgId(bg.id)}
                    style={{ border: '0.5px solid ' + (selBgId === bg.id ? 'var(--aj-accent)' : 'var(--aj-border)'), borderRadius: 4, padding: '6px 7px', marginBottom: 5, cursor: 'pointer', background: selBgId === bg.id ? 'var(--aj-bg-subtle)' : 'transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: bg.visible === false ? 'var(--aj-text-muted)' : 'var(--aj-text)', fontStyle: bg.visible === false ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bg.name}{bg.pdfPage ? ' · PDF' : ''}{bg.sat ? ' · live, not saved' : ''}</span>
                      <button title={bg.visible === false ? 'Show' : 'Hide'}
                        onClick={e => { e.stopPropagation(); saveUndo(); bg.visible = bg.visible === false ? true : false; bump(); scheduleRender() }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: bg.visible === false ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)', fontSize: 11, padding: 0 }}>{bg.visible === false ? '🚫' : '👁'}</button>
                      <button title={bg.locked ? 'Unlock (allow move / resize / adjust)' : 'Lock in place'}
                        onClick={e => { e.stopPropagation(); saveUndo(); bg.locked = !bg.locked; bump(); scheduleRender() }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: bg.locked ? 'var(--aj-accent)' : 'var(--aj-text-muted)', fontSize: 11, padding: 0 }}>{bg.locked ? '🔒' : '🔓'}</button>
                      <button title="Up" onClick={e => { e.stopPropagation(); moveBg(bg.id, 1) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, padding: 0 }}>▲</button>
                      <button title="Down" onClick={e => { e.stopPropagation(); moveBg(bg.id, -1) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, padding: 0 }}>▼</button>
                      <button title={bg.locked ? 'Unlock to remove' : 'Remove'} onClick={e => { e.stopPropagation(); if (!bg.locked) removeBg(bg.id) }} style={{ background: 'none', border: 'none', cursor: bg.locked ? 'not-allowed' : 'pointer', color: 'var(--aj-text-muted)', opacity: bg.locked ? 0.4 : 1, fontSize: 11, padding: 0 }}>✕</button>
                    </div>
                    {bg.locked ? (
                      <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 8.5, letterSpacing: '0.08em', color: 'var(--aj-text-muted)', marginTop: 4 }}>LOCKED — clicks pass through</div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', width: 46 }}>OPACITY</span>
                          <input type="range" min="10" max="100" value={Math.round((bg.opacity != null ? bg.opacity : 1) * 100)}
                            onClick={e => e.stopPropagation()}
                            onPointerDown={gestureStart} onPointerUp={gestureEnd}
                            onChange={e => { bg.opacity = parseInt(e.target.value) / 100; markDirty(); bump(); scheduleRender() }}
                            style={{ flex: 1, accentColor: INK }} />
                          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text)', width: 30, textAlign: 'right' }}>{Math.round((bg.opacity != null ? bg.opacity : 1) * 100)}%</span>
                        </div>
                        {bg.sat ? (
                          <>
                            <button onClick={e => { e.stopPropagation(); refreshSatellite() }} disabled={satBusy}
                              title="Fetch fresh imagery sized to whatever the canvas is currently showing — zoom out first for a wider contextual view"
                              style={{ width: '100%', marginTop: 4, padding: '4px 0', fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: satBusy ? 'var(--aj-text-muted)' : 'var(--aj-accent)', cursor: satBusy ? 'default' : 'pointer' }}>
                              {satBusy ? 'Fetching…' : '🔄 Refresh for current view'}
                            </button>
                            {bg.feetPerNativePx && (
                              <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-mid)', marginTop: 5, textAlign: 'center' }}
                                title="Mapbox's own claimed ground coverage for this image, independent of your Set Scale calibration — cross-check it against a known lot dimension">
                                Image covers ≈ {Math.round(bg.canvas.width * bg.feetPerNativePx)}′ × {Math.round(bg.canvas.height * bg.feetPerNativePx)}′ of ground
                                {bg.satZoom != null && <><br />zoom {Math.round(bg.satZoom * 100) / 100} · lat {Math.round(bg.satLat * 1000) / 1000}° · {bg.satNativeW}px</>}
                              </div>
                            )}
                            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 4, lineHeight: 1.4 }}>
                              Zoom the canvas out to see more, then Refresh to pull in a wider image — same idea zoomed back in for site detail. Orientation follows NORTH automatically. The geocoded address is only approximate — drag the image (or select it and use arrow keys for fine control) to align it exactly with your drawing; the correction sticks for every future Refresh. If the image is sized wrong relative to your (already-calibrated) drawing, drag a corner handle to resize it until it matches — that correction is remembered too and improves every future Refresh, no need to redo it.
                            </div>
                            <button onClick={async e => {
                              e.stopPropagation()
                              if (!(await confirmDialog({ title: 'Reset position?', message: 'Discards any manual alignment and re-centers on the geocoded project address.' }))) return
                              saveUndo()
                              docRef.current.geoAnchor = null
                              bump()
                              refreshSatellite()
                            }}
                              style={{ width: '100%', marginTop: 4, padding: '4px 0', fontFamily: '"DM Sans",sans-serif', fontSize: 10, border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text-muted)', cursor: 'pointer' }}>
                              Reset position to address
                            </button>
                          </>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }} title="Rotate the site plan so true north matches the drawing (set NORTH in the Site panel to match)">
                            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', width: 46 }}>ROTATE</span>
                            <input type="range" min="-180" max="180" step="0.5" value={Math.round(((bg.rotation || 0) * 180 / Math.PI) * 2) / 2}
                              onClick={e => e.stopPropagation()}
                              onPointerDown={gestureStart} onPointerUp={gestureEnd}
                              onChange={e => { bg.rotation = parseFloat(e.target.value) * Math.PI / 180; markDirty(); bump(); scheduleRender() }}
                              style={{ flex: 1, accentColor: INK }} />
                            <input type="number" step="0.5" value={Math.round(((bg.rotation || 0) * 180 / Math.PI) * 10) / 10}
                              onClick={e => e.stopPropagation()} onWheel={numberWheel}
                              onFocus={gestureStart} onBlur={gestureEnd}
                              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { bg.rotation = v * Math.PI / 180; markDirty(); bump(); scheduleRender() } }}
                              style={{ width: 44, fontFamily: '"DM Mono",monospace', fontSize: 9.5, padding: '1px 3px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 3, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none' }} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
                <button onClick={() => fileRef.current?.click()}
                  style={{ width: '100%', padding: 6, background: 'transparent', border: '1px dashed var(--aj-border-mid)', borderRadius: 4, color: 'var(--aj-accent)', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, cursor: 'pointer' }}>
                  + Add image / PDF
                </button>
                {!doc?.backgrounds.some(bg => bg.sat) && (
                  <button onClick={refreshSatellite} disabled={satBusy}
                    title="Fetches live from Mapbox, sized to whatever the canvas is currently showing — not saved with the file. Remove it from this list any time to turn it off."
                    style={{ width: '100%', marginTop: 6, padding: 6, background: 'transparent', border: '1px dashed var(--aj-border-mid)', borderRadius: 4, color: satBusy ? 'var(--aj-text-muted)' : 'var(--aj-accent)', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, cursor: satBusy ? 'default' : 'pointer' }}>
                    {satBusy ? 'Fetching satellite view…' : '+ Add satellite view'}
                  </button>
                )}
                {doc && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', width: 46 }} title="White overlay above the backgrounds — the standard light-field treatment">WASH</span>
                    <input type="range" min="0" max="85" value={Math.round(doc.wash * 100)}
                      onPointerDown={gestureStart} onPointerUp={gestureEnd}
                      onChange={e => { doc.wash = parseInt(e.target.value) / 100; markDirty(); bump(); scheduleRender() }}
                      style={{ flex: 1, accentColor: INK }} />
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text)', width: 30, textAlign: 'right' }}>{Math.round(doc.wash * 100)}%</span>
                  </div>
                )}
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*,.pdf,application/pdf" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleFile(f) }} />
          </div>

          {doc && doc.items.length > 0 && (
            <div>
              <SectionHeader panelKey="objects">Objects</SectionHeader>
              {!collapsedPanels.objects && (
                <>
                  {/* Illustrator-style object list: top row = frontmost. Click selects on the map;
                      drag the ⠿ handle to reorder (see objDragStart for the ghost + FLIP animation). */}
                  {[...doc.items].reverse().map(it => {
                    const st = SITE_STYLES[it.kind] || {}
                    const sel = selItemId === it.id
                    const hidden = it.visible === false
                    const dragging = dragObj && dragObj.id === it.id
                    return (
                      <div key={it.id} data-obj-row ref={el => { objRowRefs.current[it.id] = el }}
                        onClick={() => { setSelItemId(it.id); setSelBgId(null) }}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 6px', marginBottom: 2, borderRadius: 4, cursor: 'pointer', border: '0.5px solid ' + (sel ? 'var(--aj-accent)' : 'transparent'), background: sel ? 'var(--aj-bg-subtle)' : 'transparent', opacity: dragging ? 0.35 : 1 }}>
                        <span onPointerDown={e => objDragStart(e, it)} title="Drag to reorder"
                          style={{ cursor: 'grab', color: 'var(--aj-text-muted)', fontSize: 12, padding: '0 2px', touchAction: 'none' }}>⠿</span>
                        <span style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, opacity: hidden ? 0.35 : 1, background: st.fill || 'transparent', border: '1.5px solid ' + (st.stroke || st.ring || (it.kind === 'utilpoint' && utilTypeDef(it.ptype).color) || INK) }} />
                        <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: (it.locked || hidden) ? 'var(--aj-text-muted)' : 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: (it.locked || hidden) ? 'italic' : 'normal' }}>{itemListLabel(it)}</span>
                        <button title={hidden ? 'Show' : 'Hide'}
                          onClick={e => { e.stopPropagation(); saveUndo(); it.visible = hidden ? true : false; bump(); scheduleRender() }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: hidden ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)', fontSize: 11, padding: '0 2px' }}>{hidden ? '🚫' : '👁'}</button>
                        <button title={it.locked ? 'Unlock (allow select / move / edit)' : 'Lock in place'}
                          onClick={e => { e.stopPropagation(); saveUndo(); it.locked = !it.locked; if (it.locked && selItemId === it.id) setSelItemId(it.id); bump(); scheduleRender() }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: it.locked ? 'var(--aj-accent)' : 'var(--aj-text-muted)', fontSize: 11, padding: '0 2px' }}>{it.locked ? '🔒' : '🔓'}</button>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          )}
          {dragObj && (() => {
            const it = doc && doc.items.find(x => x.id === dragObj.id)
            if (!it) return null
            const st = SITE_STYLES[it.kind] || {}
            return (
              <div style={{
                position: 'fixed', left: dragObj.rect.left, top: dragObj.rect.top + dragObj.dy,
                width: dragObj.rect.width, height: dragObj.rect.height,
                display: 'flex', alignItems: 'center', gap: 4, padding: '3px 6px',
                background: 'var(--aj-bg-elevated)', border: '1px solid var(--aj-accent)', borderRadius: 4,
                boxShadow: '0 6px 18px rgba(7,39,45,0.28)', pointerEvents: 'none', zIndex: 800,
              }}>
                <span style={{ color: 'var(--aj-text-muted)', fontSize: 12, padding: '0 2px' }}>⠿</span>
                <span style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: st.fill || 'transparent', border: '1.5px solid ' + (st.stroke || st.ring || (it.kind === 'utilpoint' && utilTypeDef(it.ptype).color) || INK) }} />
                <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itemListLabel(it)}</span>
              </div>
            )
          })()}
        </div>
        <div style={{ flexShrink: 0, borderTop: '0.5px solid var(--aj-border)', padding: 10, maxHeight: '48vh', overflowY: 'auto', background: 'var(--aj-bg-surface)' }}>
          {(() => {
            const selIt = doc && doc.items.find(i2 => i2.id === selItemId)
            if (!selIt) return (
              <div style={{ ...propsCard, border: '1px solid var(--aj-border-mid)', boxShadow: 'none' }}>
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.5 }}>
                  <span style={{ ...propsChip, background: 'var(--aj-text-muted)' }}>Properties</span>
                  Pick a kit tool on the left rail and click the map to place. Select an element to edit it here.
                </div>
                {doc && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!doc.legend.visible} onChange={e => { saveUndo(); doc.legend.visible = e.target.checked; scheduleRender(); bump() }} />
                    Show legend
                  </label>
                )}
                {doc && doc.items.some(it => it.kind === 'keytag' && (it.text || '').trim()) && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!doc.keyNotes.visible} onChange={e => { saveUndo(); doc.keyNotes.visible = e.target.checked; scheduleRender(); bump() }} />
                    Show key notes
                  </label>
                )}
              </div>
            )
            const st = SITE_STYLES[selIt.kind] || {}
            const inputStyle = { width: 58, fontFamily: '"DM Mono",monospace', fontSize: 11, padding: '2px 5px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none' }
            if (selIt.locked) return (
              <div style={propsCard}>
                <div style={{ ...plbl, display: 'flex', alignItems: 'center', border: 'none', paddingBottom: 0, marginBottom: 8 }}>
                  <span style={propsChip}>Properties</span>
                  <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: 'var(--aj-text)' }}>{(st.label || selIt.kind)}</span>
                </div>
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.5, marginBottom: 8 }}>
                  🔒 Locked — not selectable or editable on the map. Unlock it to move, reshape, or delete it.
                </div>
                <button onClick={() => { saveUndo(); selIt.locked = false; scheduleRender(); bump() }}
                  style={{ width: '100%', fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '5px 0', border: '0.5px solid var(--aj-accent)', borderRadius: 4, background: 'transparent', color: 'var(--aj-accent)', cursor: 'pointer' }}>
                  Unlock
                </button>
              </div>
            )
            return (
              <div style={propsCard}>
                <div style={{ ...plbl, display: 'flex', alignItems: 'center', border: 'none', paddingBottom: 0, marginBottom: 8 }}>
                  <span style={propsChip}>Properties</span>
                  <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: 'var(--aj-text)' }}>{(st.label || selIt.kind)}</span>
                </div>
                {selIt.kind === 'pl' && (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selIt.variant === 'pending'} onChange={e => { selIt.variant = e.target.checked ? 'pending' : 'confirmed'; saveUndo(); scheduleRender(); bump() }} />
                      Boundary pending confirmation
                    </label>
                    {!doc.scalePixPerFoot && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#b3402f', marginBottom: 6 }}>Set the scale first — setback offsets need real distances.</div>}
                    {!selIt.edgeRoles ? (
                      <button onClick={() => { saveUndo(); selIt.edgeRoles = (selIt.pts || []).map(() => null); selIt.roleSetbacks = { front: 0, side1: 0, side2: 0, rear: 0 }; scheduleRender(); bump() }}
                        style={{ width: '100%', fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '6px 0', border: '0.5px solid var(--aj-accent)', borderRadius: 4, background: 'transparent', color: 'var(--aj-accent)', cursor: 'pointer' }}>
                        Switch to Front / Side / Rear setbacks
                      </button>
                    ) : (
                      <>
                        <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.1em', color: 'var(--aj-text-muted)', marginBottom: 4 }}>WHICH EDGE IS WHICH</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                          {(selIt.pts || []).map((p, ei) => {
                            const b = selIt.pts[(ei + 1) % selIt.pts.length]
                            const lenFt = doc.scalePixPerFoot ? Math.round(Math.hypot(b.x - p.x, b.y - p.y) / doc.scalePixPerFoot) : null
                            return (
                              <label key={ei} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-mid)' }}>
                                <span style={{ width: 62, flexShrink: 0 }}>E{ei + 1}{lenFt != null ? ' (' + lenFt + '′)' : ''}</span>
                                <select value={selIt.edgeRoles[ei] || ''} style={{ ...inputStyle, width: 'auto', flex: 1 }}
                                  onChange={e => { selIt.edgeRoles[ei] = e.target.value || null; markDirty(); scheduleRender(); bump() }}>
                                  <option value="">—</option>
                                  <option value="front">Front</option>
                                  <option value="side1">Side 1</option>
                                  <option value="side2">Side 2</option>
                                  <option value="rear">Rear</option>
                                </select>
                              </label>
                            )
                          })}
                        </div>
                        <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.1em', color: 'var(--aj-text-muted)', marginBottom: 4 }}>SETBACK PER SIDE (FT)</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
                          {[['front', 'Front'], ['rear', 'Rear'], ['side1', 'Side 1'], ['side2', 'Side 2']].map(([role, lbl]) => (
                            <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-mid)' }}>
                              <span style={{ width: 38, flexShrink: 0 }}>{lbl}</span>
                              <input type="number" min="0" style={inputStyle} value={selIt.roleSetbacks[role] || 0}
                                onWheel={numberWheel}
                                onChange={e => { selIt.roleSetbacks[role] = Math.max(0, parseFloat(e.target.value) || 0); markDirty(); scheduleRender(); bump() }} />
                            </label>
                          ))}
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!selIt.showSetbackLabels}
                            onChange={e => { saveUndo(); selIt.showSetbackLabels = e.target.checked; scheduleRender(); bump() }} />
                          Show setback distance labels
                        </label>
                      </>
                    )}
                    <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 6, lineHeight: 1.45 }}>
                      The buildable envelope derives automatically from these. Double-click an edge
                      to add a node (it inherits that edge's role); double-click a vertex to curve
                      it (drag the handles).
                    </div>
                  </>
                )}
                {selIt.kind === 'label' && (
                  <>
                    <input type="text" value={selIt.text || ''} style={{ ...inputStyle, width: '100%' }}
                      onChange={e => { selIt.text = e.target.value; markDirty(); scheduleRender(); bump() }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>ROTATE °</span>
                      <input type="number" step="5" style={inputStyle} value={selIt.rot || 0}
                        onWheel={numberWheel}
                        onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { selIt.rot = v; markDirty(); scheduleRender(); bump() } }} />
                    </div>
                  </>
                )}
                {selIt.kind === 'sun' && (() => {
                  const cfg = sunSettings(selIt)
                  const setSolar = patch => { saveUndo(); selIt.solar = { ...(selIt.solar || {}), ...patch }; scheduleRender(); bump() }
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>MODE</span>
                        <select value={cfg.mode} style={{ ...inputStyle, width: 'auto', flex: 1 }}
                          onChange={e => setSolar({ mode: e.target.value })}>
                          <option value="paths">Solar paths only</option>
                          <option value="vectors">Solar paths + key vectors</option>
                          <option value="debug">Technical / debug</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>SIZE</span>
                        <select value={cfg.radiusMode} style={{ ...inputStyle, width: 'auto' }}
                          onChange={e => setSolar({ radiusMode: e.target.value })}>
                          <option value="auto">Auto</option>
                          <option value="manual">Manual</option>
                        </select>
                        {cfg.radiusMode === 'manual' && (
                          <input type="number" min="40" max="900" style={inputStyle} value={selIt.r || 90}
                            onWheel={numberWheel}
                            onChange={e => { selIt.r = Math.max(40, parseFloat(e.target.value) || 90); markDirty(); scheduleRender(); bump() }} />
                        )}
                      </div>
                      {cfg.mode !== 'paths' && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>VECTORS</span>
                            <select value={cfg.vectorSet} style={{ ...inputStyle, width: 'auto', flex: 1 }}
                              onChange={e => setSolar({ vectorSet: e.target.value })}>
                              <option value="presentation">Key set (Jun + Dec, 9 AM / 3 PM)</option>
                              <option value="full">All (3 seasons × 9 / 12 / 3)</option>
                            </select>
                          </div>
                          {cfg.vectorSet === 'presentation' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
                              <input type="checkbox" checked={cfg.showNoon}
                                onChange={e => setSolar({ showNoon: e.target.checked })} />
                              Include solar noon
                            </label>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}
                            title="Draws parallel sunlight rays across the whole site for ONE moment — which façades and outdoor spaces that light actually reaches. One at a time by design.">
                            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>STUDY</span>
                            <select value={cfg.focus || ''} style={{ ...inputStyle, width: 'auto', flex: 1 }}
                              onChange={e => setSolar({ focus: e.target.value || null })}>
                              <option value="">None</option>
                              {sunVectorTimes(cfg).map(({ season, time }) => (
                                <option key={season.key + '@' + time.h} value={season.key + '@' + time.h}>
                                  {season.lbl.replace('MAR/SEP', 'EQX')} / {time.lbl} — parallel rays
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                      {cfg.mode === 'debug' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }} title="Hours from UTC (e.g. -8 for PST). Blank = inferred from longitude.">UTC OFFSET</span>
                          <input type="number" step="1" style={inputStyle} placeholder="auto"
                            value={cfg.utcOffset != null ? cfg.utcOffset : ''}
                            onWheel={numberWheel}
                            onChange={e => { const v = parseFloat(e.target.value); setSolar({ utcOffset: isNaN(v) ? null : v }) }} />
                        </div>
                      )}
                      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', lineHeight: 1.45 }}>
                        The circle shows seasonal range and is sized for composition. The arrows are TRUE
                        sun directions computed from latitude{doc.longitude != null ? ' + longitude' : ''} (Site
                        panel) — their bearing never changes with the circle's size.
                      </div>
                    </>
                  )
                })()}
                {selIt.kind === 'windrose' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>SIZE</span>
                      <input type="number" min="30" max="160" style={inputStyle} value={selIt.r || 55}
                        onWheel={numberWheel}
                        onChange={e => { selIt.r = Math.max(30, parseFloat(e.target.value) || 55); markDirty(); scheduleRender(); bump() }} />
                    </div>
                    <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.1em', color: 'var(--aj-text-muted)', marginBottom: 4 }}>PETALS (0–3 EMPHASIS)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                      {['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'].map((dir, i) => (
                        <label key={dir} style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-mid)' }}>
                          <span style={{ width: 18 }}>{dir}</span>
                          <input type="number" min="0" max="3" style={{ ...inputStyle, width: 40 }}
                            value={(selIt.petals && selIt.petals[i]) || 0}
                            onWheel={numberWheel}
                            onChange={e => {
                              selIt.petals = selIt.petals || [0, 0, 0, 0, 0, 0, 0, 0]
                              selIt.petals[i] = Math.min(3, Math.max(0, parseInt(e.target.value) || 0))
                              markDirty(); scheduleRender(); bump()
                            }} />
                        </label>
                      ))}
                    </div>
                    <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 6, lineHeight: 1.45 }}>
                      Set what THIS site actually feels — the nearest wind station often misleads.
                    </div>
                  </>
                )}
                {(selIt.kind === 'windarrow' || selIt.kind === 'viewarrow') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>TYPE</span>
                    <select value={selIt.variant || (selIt.kind === 'windarrow' ? 'breeze' : 'major')} style={{ ...inputStyle, width: 'auto', flex: 1 }}
                      onChange={e => { selIt.variant = e.target.value; markDirty(); scheduleRender(); bump() }}>
                      {selIt.kind === 'windarrow'
                        ? <><option value="breeze">Breeze (dashed curve)</option><option value="storm">Storm (broad slab)</option></>
                        : <><option value="major">Major view (bold)</option><option value="ray">View ray (fine dotted)</option></>}
                    </select>
                  </div>
                )}
                {selIt.kind === 'viewcone' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                    {[['heading', 'HDG °', 0], ['spread', 'SPREAD °', 60], ['r', 'REACH', 120]].map(([k, lbl, dflt]) => (
                      <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-mid)' }}>
                        {lbl}
                        <input type="number" style={inputStyle} value={selIt[k] != null ? selIt[k] : dflt}
                          onWheel={numberWheel}
                          onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { selIt[k] = v; markDirty(); scheduleRender(); bump() } }} />
                      </label>
                    ))}
                  </div>
                )}
                {selIt.kind === 'tree' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>CANOPY Ø FT</span>
                    <input type="number" min="1" style={inputStyle} value={selIt.diaFt || 15}
                      onWheel={numberWheel}
                      onChange={e => { selIt.diaFt = Math.max(1, parseFloat(e.target.value) || 15); markDirty(); scheduleRender(); bump() }} />
                    {!doc.scalePixPerFoot && <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: '#b3402f' }}>needs scale</span>}
                  </div>
                )}
                {selIt.kind === 'utilpoint' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>TYPE</span>
                    <select value={selIt.ptype === 'site' ? 'other' : (selIt.ptype || 'other')} style={{ ...inputStyle, width: 'auto', flex: 1 }}
                      onChange={e => {
                        selIt.ptype = e.target.value
                        if (selIt.ptype === 'other' && !selIt.n) selIt.n = doc.items.filter(i2 => i2.kind === 'utilpoint' && i2.ptype === 'other' && i2 !== selIt).length + 1
                        markDirty(); scheduleRender(); bump()
                      }}>
                      <optgroup label="Water">
                        <option value="water_meter">Water Meter</option>
                        <option value="water_poc">Water POC</option>
                      </optgroup>
                      <optgroup label="Gas">
                        <option value="gas_meter">Gas Meter</option>
                        <option value="gas_poc">Gas POC</option>
                      </optgroup>
                      <optgroup label="Electrical">
                        <option value="elec_meter">Electrical Meter</option>
                        <option value="elec_panel">Electrical Panel</option>
                      </optgroup>
                      <optgroup label="Fire / Sewer">
                        <option value="hydrant">Fire Hydrant</option>
                        <option value="sewer_cleanout">Sewer Cleanout</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="telecom">Telecom / Cable</option>
                        <option value="well">Well</option>
                        <option value="septic">Septic</option>
                        <option value="other">Other (numbered)</option>
                      </optgroup>
                    </select>
                  </div>
                )}
                {selIt.kind === 'keytag' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>NUMBER</span>
                      <input type="number" min="1" style={inputStyle} value={selIt.n || 1}
                        onWheel={numberWheel}
                        onChange={e => { selIt.n = Math.max(1, parseInt(e.target.value) || 1); markDirty(); scheduleRender(); bump() }} />
                    </div>
                    <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.1em', color: 'var(--aj-text-muted)', marginBottom: 4 }}>NOTE (KEY NOTES LIST)</div>
                    <input type="text" placeholder="What is this tag pointing at?" value={selIt.text || ''} style={{ ...inputStyle, width: '100%' }}
                      onChange={e => { selIt.text = e.target.value; markDirty(); scheduleRender(); bump() }} />
                    <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 6, lineHeight: 1.45 }}>
                      The number on the map just points here — the description appears in the Key Notes list
                      on the sheet (toggle it below when at least one tag has a note).
                    </div>
                  </>
                )}
                {selIt.kind === 'nuisance' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>FACING °</span>
                    <input type="number" step="5" style={inputStyle} value={selIt.rot || 0}
                      onWheel={numberWheel}
                      onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { selIt.rot = v; markDirty(); scheduleRender(); bump() } }} />
                  </div>
                )}
                {selIt.kind === 'corner' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>ROTATE °</span>
                      <input type="number" step="5" style={inputStyle} value={selIt.rot || 0}
                        onWheel={numberWheel}
                        onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) { selIt.rot = v; markDirty(); scheduleRender(); bump() } }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>CORNER</span>
                      <select value={selIt.role || 'high1'} style={{ ...inputStyle, width: 'auto', flex: 1 }}
                        onChange={e => {
                          selIt.role = e.target.value
                          if (selIt.elevation == null && parcelRef.current) {
                            const v = parcelRef.current.fields[CORNER_FIELD[selIt.role]]
                            if (v != null) selIt.elevation = v
                          }
                          markDirty(); scheduleRender(); bump()
                        }}>
                        {CORNER_ROLES.map(r => <option key={r} value={r}>{CORNER_LABEL[r]}</option>)}
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>ELEVATION FT</span>
                      <input type="number" step="0.1" style={inputStyle} value={selIt.elevation != null ? selIt.elevation : ''}
                        placeholder="—"
                        onWheel={numberWheel}
                        onChange={e => {
                          const v = e.target.value === '' ? null : parseFloat(e.target.value)
                          selIt.elevation = (v == null || isNaN(v)) ? null : v
                          markDirty(); scheduleRender(); bump()
                          if (parcelRef.current && selIt.elevation != null) {
                            updateParcelField(parcelRef.current.id, { [CORNER_FIELD[selIt.role]]: selIt.elevation }).catch(() => {})
                          }
                        }} />
                    </div>
                    <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', lineHeight: 1.45 }}>
                      {parcelRef.current
                        ? 'Tied to Parcel Research — editing here updates that record too, so the two never drift apart.'
                        : 'No Parcel Research on this project yet — the value is stored on this marker only.'}
                    </div>
                  </>
                )}
                {((selIt.pts && selIt.kind !== 'pl' && selIt.kind !== 'setback') || ['windrose', 'nuisance', 'viewcone'].includes(selIt.kind)) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>LABEL</span>
                    <input type="text" value={selIt.text || ''} style={{ ...inputStyle, width: '100%', flex: 1 }}
                      onChange={e => { selIt.text = e.target.value; markDirty(); scheduleRender(); bump() }} />
                  </div>
                )}
                <button onClick={() => { saveUndo(); doc.items = doc.items.filter(i2 => i2.id !== selIt.id); setSelItemId(null); scheduleRender(); bump() }}
                  style={{ marginTop: 8, width: '100%', fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '5px 0', border: '0.5px solid rgba(179,64,47,0.5)', borderRadius: 4, background: 'transparent', color: '#b3402f', cursor: 'pointer' }}>
                  Delete element
                </button>
              </div>
            )
          })()}
        </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
